import { cloneElement, isValidElement, useState, useRef, useEffect, useMemo } from "react";
import { validateBienPrep, isCycleOuvert, isElsLance, isPebLance, isPebDone } from "@/lib/bienPrepValidation";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbDetail } from "@/components/layout/BreadcrumbContext";
import { SectionCard } from "@/components/layout/SectionCard";
import { CardGrid, CardGridItem } from "@/components/layout/CardGrid";
import { FormField } from "@/components/ui/FormField";
import { FreeTextAutocompleteField } from "@/components/ui/FreeTextAutocompleteField";
import { SelectField } from "@/components/ui/SelectField";
import { DateField } from "@/components/ui/DateField";
import { TimeField } from "@/components/ui/TimeField";
import { todayIso } from "@/lib/today";
import { TextAreaField } from "@/components/ui/TextAreaField";
import { Checkbox } from "@/components/ui/Checkbox";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { MapPin, Home, DollarSign, Zap, Building2, Globe, Image, Settings, CheckCircle, AlertTriangle, Users, ClipboardCheck, ArrowLeftRight, History, Loader2, Sparkles, Trash2, FileText, Rocket, CheckCircle2, Trees, RefreshCw, Plug, Wallet, Activity, CalendarCheck } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { PhotoGallery } from "@/components/biens/PhotoGallery";
import { FicheTabBar } from "@/components/biens/FicheTabBar";
import { FICHE_TAB_IDS, FICHE_TAB_META, countMissingByTab, type FicheTabId } from "@/components/biens/ficheTabs";
import type { FicheTabAlert } from "@/components/biens/FicheTabBar";
import { FieldGroup } from "@/components/biens/FieldGroup";
import { FicheHeader } from "@/components/biens/FicheHeader";
import { SurfaceOrientationRepeater } from "@/components/biens/SurfaceOrientationRepeater";
import { buildRepeaterPatch, hydrateRepeaterRows } from "@/lib/surfaceOrientationRows";
import type { SurfaceOrientation } from "@/types/bien";
import { PublicationPublishButton, PublicationStaleBanner } from "@/components/biens/PublicationGlobalStatus";
import { ImmowebStatusLine } from "@/components/biens/PortalStatusIndicator";
import { useUnpublishedChangesGuard } from "@/hooks/useUnpublishedChangesGuard";
import type { Bien } from "@/types/bien";
import { EQUIPEMENT_TAGS } from "@/data/constants";
import { useBien, useBiens, useUpdateBien, useCreateBien, useDeleteBien, useMarkBienAsPublished } from "@/hooks/useBiens";
import { usePortalPublishMutation } from "@/hooks/usePortalPublications";
import { portalActionForStatutChange } from "@/lib/portalPublish";
import { externalEnabledPortals } from "@/lib/portals";
import { useBiensPermRole } from "@/hooks/useBiensPermRole";
import { useLocataires, useCreateLocataire, useUpdateLocataire, useTransferLocataire } from "@/hooks/useLocataires";
import { useProfiles } from "@/hooks/useProfiles";
import { useQuartiers } from "@/hooks/useQuartiers";
import { BienUpdateSchema } from "@/schemas/bien";
import { isPublishable, PUBLISH_GATE_LABEL_KEYS, type PublishGateInput } from "@/lib/publishGate";
import { IMMOWEB_TEXT_LIMITS } from "@/lib/immoweb/textLimits";
import { getTypesBien, getTypesEnvironnement, getTypesSol, getCaveOptions, getParkingOptions, getGarageOptions, getChargesInclusesOptions, getDisponibiliteOptions, getEtatsBatiment, getAnneesBati, getChauffageEnergie, CLASSES_PEB } from "@/data/options";
import { useSessionState } from "@/hooks/useSessionState";
import { useRole } from "@/contexts/RoleContext";
import { useLaunchMission } from "@/hooks/useLaunchMission";
import { checkElsStates } from "@/v2/components/CheckElsBadges";
import { useAuth } from "@/contexts/AuthContext";
import { useFieldPermissions } from "@/hooks/useFieldPermissions";
import { useFieldPermsEditor } from "@/contexts/fieldPermsEditor";
import { PermStateCell } from "@/components/biens/PermStateCell";
import { PermVisibilityToggle } from "@/components/biens/PermVisibilityToggle";
import { BienFieldPermissions } from "./BienFieldPermissions";
import { getCityFromPostalCode, getPostalCodeFromCity, getRegionFromPostalCode } from "@/lib/postalCodeLookup";
import { computeBienVersion } from "@/lib/bienVersion";
import { useListNavigation } from "@/hooks/useListNavigation";
import { generateBienDescription, translateBienDescription, type AiLang } from "@/lib/aiBienDescription";
import { resetElsCycleForLoue } from "@/lib/elsResetCycle";
import { canToggleSousOption, sousOptionEnterPatch, sousOptionExitPatch } from "@/lib/sousOption";
import { isPebExpiringSoon, isPebReportComplete, promotePebCycle, getPebStatut } from "@/lib/pebCycle";
import { formatDate } from "@/v2/lib/formatDate";
import { hasExitCycleInFlight } from "@/v2/lib/bienLifecycle";
import type { BienRow } from "@/schemas/bien";
import { cn } from "@/lib/utils";
import type { BienDetailHideableSection } from "@/lib/ficheVisibility";
import {
  visibleFicheTabs,
  fallbackActiveTab,
  isFieldVisible,
  isSectionVisible,
  sectionStructuralKey,
  tabStructuralKey,
} from "@/lib/ficheVisibility";
import type { FichePreset } from "@/contexts/fieldPermsEditor";
import { useBienFichePreset } from "@/hooks/useBienFichePreset";

/**
 * Wrapper : attend que `useBien` ait résolu avant de rendre le contenu.
 * Sans cette garde, `useSessionState` plus bas s'initialise avec l'objet
 * par défaut vide au premier render (quand `foundBien` est encore undefined),
 * et le formulaire reste figé sur ces valeurs vides même une fois la réponse
 * DB arrivée. On force un re-mount via `key` quand la ligne DB change
 * (`updated_at`), pour que `useSessionState` reparte avec les vraies valeurs.
 */
import type { AppRole } from "@/contexts/RoleContext";

interface BienDetailPageProps {
  bienIdOverride?: string;
  /**
   * Force le rôle effectif utilisé pour les permissions (canSee, canEdit, RPC
   * read/write). Sans override, on prend le rôle rendu par `useBiensPermRole`
   * (`collaborateur` → `collaborateur_afr`, sinon le rôle réel).
   *
   * Seul usage restant : `/publi-temp`, qui force `admin`. Les workspaces V2
   * n'en passent plus — c'était précisément ce qui faisait que le même bien
   * donnait deux jeux de droits selon la porte empruntée.
   */
  roleOverride?: AppRole;
  /**
   * Masque les sections fournies (locataires / ELS / finalisation SRS).
   * Utilisé par /publi-temp pour ne montrer que les champs descriptifs +
   * publication, en cachant tout ce qui touche au workflow opérationnel.
   * Les données restent chargées en arrière-plan (pour cohérence du save)
   * mais ne sont pas rendues.
   */
  hideSections?: BienDetailHideableSection[];
  /**
   * Désactive les bannières d'alerte "champs obligatoires manquants" et
   * "champs recommandés mailing manquants". Le bien reste sauvable même
   * vide. Pour /publi-temp où il n'y a pas de gate de complétude.
   */
  skipRequiredValidation?: boolean;
  /**
   * Masque le tab "Droits des champs" même si role === admin. Pour les
   * contextes (ex. /publi-temp) où l admin externe ne doit pas voir/toucher
   * la matrice de permissions.
   */
  hidePermissionsTab?: boolean;
  /**
   * Préréglage de popup actif (Apparts for Rent / Pipeline ELS). Ne
   * s'applique QUE si le rôle effectif appartient à la famille
   * `collaborateur` (cf. §9 de la spec) — sinon ignoré silencieusement, la
   * fiche complète normale s'affiche. Jamais transmis à une RPC.
   */
  preset?: FichePreset;
  /**
   * Onglet ouvert au montage, au lieu du défaut "Le bien". Utilisé par la
   * modale du workspace Publication : un utilisateur au rôle `publication`
   * qui ouvre une fiche vient quasi toujours pour la Commercialisation, pas
   * pour éditer "Le bien" (demande Thibaud 2026-08-20).
   */
  initialTab?: FicheTabId;
}

export function BienDetailPage({
  bienIdOverride,
  roleOverride,
  hideSections,
  skipRequiredValidation,
  hidePermissionsTab,
  preset,
  initialTab,
}: BienDetailPageProps = {}) {
  // bienIdOverride permet de monter BienDetailPage sans router (typique :
  // dans une modale ouverte par-dessus une autre page V2). Sans override,
  // on lit le segment :id de l'URL (comportement standard via les Routes).
  const params = useParams();
  const id = bienIdOverride ?? params.id;
  const pathRole = useBiensPermRole();
  const permRole = roleOverride ?? pathRole;
  const { isLoading } = useBien(id, permRole);

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  // key={id} : force un REMOUNT complet quand on navigue d'un bien à l'autre
  // (flèches prev/next du fil d'Ariane). Sans ça, `useSessionState` plus bas
  // ne repart PAS sur les bonnes valeurs : son initializer useState ne re-run
  // qu'au mount, et son reset interne ne se déclenche que si `version`
  // (= bien.updated_at) change. Or des biens d'un même lot d'import partagent
  // le MÊME updated_at → version identique → form figé sur le bien précédent
  // (URL + position changent mais le contenu reste celui d'avant — bug
  // 2026-06-03, visible surtout quand le bien cible est déjà en cache React
  // Query, donc sans passage par l'écran de chargement qui remonterait sinon).
  return (
    <BienDetailPageContent
      key={id}
      bienIdOverride={bienIdOverride}
      roleOverride={roleOverride}
      hideSections={hideSections}
      skipRequiredValidation={skipRequiredValidation}
      hidePermissionsTab={hidePermissionsTab}
      preset={preset}
      initialTab={initialTab}
    />
  );
}

function BienDetailPageContent({
  bienIdOverride,
  roleOverride,
  hideSections,
  skipRequiredValidation,
  hidePermissionsTab,
  preset,
  initialTab,
}: BienDetailPageProps = {}) {
  /**
   * Mode « réglage des droits » : la page /v2/admin/droits-champs monte cette
   * même fiche dans ce contexte. `null` en usage normal.
   *
   * Deux conséquences dans tout ce composant, chacune commentée sur place :
   *  - `F()` rend un sélecteur d'état au lieu du champ ;
   *  - toutes les branches conditionnelles sont FORCÉES ouvertes, sinon on ne
   *    pourrait pas régler les droits d'un champ que l'écran nous cache.
   * Et surtout : l'enregistrement est neutralisé (cf. `saveBien`).
   */
  const permsEditor = useFieldPermsEditor();
  const permsMode = !!permsEditor;

  const params = useParams();
  const id = bienIdOverride ?? params.id;
  const { t, i18n } = useTranslation();
  // roleOverride priorité absolue (modale V2 émulant une vue V1) ; sinon
  // dérivation classique via le pathname.

  // Translated option arrays
  const TYPES_BIEN = useMemo(() => getTypesBien(t), [t]);
  const TYPES_ENVIRONNEMENT = useMemo(() => getTypesEnvironnement(t), [t]);
  const TYPES_SOL = useMemo(() => getTypesSol(t), [t]);
  const CAVE_OPTIONS = useMemo(() => getCaveOptions(t), [t]);
  const PARKING_OPTIONS = useMemo(() => getParkingOptions(t), [t]);
  const GARAGE_OPTIONS = useMemo(() => getGarageOptions(t), [t]);
  const CHARGES_INCLUSES_OPTIONS = useMemo(() => getChargesInclusesOptions(t), [t]);
  const DISPONIBILITE_OPTIONS = useMemo(() => getDisponibiliteOptions(t), [t]);
  // Tri-état pour meuble + companyContract : permet de distinguer
  // "non saisi" (—) de "Non explicite". Utilisé avec setBool / boolToOption.
  const OUI_NON_OPTIONS = useMemo(() => [
    { value: "", label: "—" },
    { value: "oui", label: t("common.yes") },
    { value: "non", label: t("common.no") },
  ], [t]);
  const ETATS_BATIMENT = useMemo(() => getEtatsBatiment(t), [t]);
  const ANNEES_BATI = useMemo(() => getAnneesBati(t), [t]);
  const CHAUFFAGE_ENERGIE = useMemo(() => getChauffageEnergie(t), [t]);

  // Rôle effectif (mappé selon le path) — utilisé pour TOUS les appels
  // permission-aware (read, write, canSee/canEdit) afin d'éviter les
  // désyncs UI ↔ RPC. roleOverride a priorité (utile pour les modales V2
  // qui veulent émuler un rôle indépendamment du pathname courant).
  const pathRole = useBiensPermRole();
  const permRole = roleOverride ?? pathRole;

  // Le préréglage ne s'applique qu'à la famille collaborateur (spec §9) — un
  // c_level ou un admin ouvrant la même popup depuis Apparts for Rent garde
  // la vue complète, ce sont des écrans de supervision pour eux.
  const presetActive: FichePreset | null =
    preset && permRole.startsWith("collaborateur") ? preset : null;
  const { presetVisible } = useBienFichePreset(presetActive);

  // permRole est déclaré plus haut via useBiensPermRole() — réutilisé ici
  // pour useFieldPermissions, garantissant que canSee/canEdit, useBien,
  // useUpdateBien partagent le MÊME rôle effectif.
  // (Remonté ici depuis plus loin dans le fichier — chantier visibilité
  // contextuelle 2026-08 : le calcul de ficheTabs/visibleTabIds, lui aussi
  // plus bas, a besoin de canSee avant sa déclaration d'origine.)
  const { canSee, canEdit } = useFieldPermissions(permRole);

  // Helper court : visibilité effective d'une section, cf. isSectionVisible
  // (mode droits → toujours visible ; puis hideSections statique, historique
  // /publi-temp ; puis rôle réel + préréglage).
  const showSection = (key: BienDetailHideableSection) =>
    isSectionVisible(key, {
      permsMode,
      hideSections,
      canSeeStructural: canSee,
      presetVisible: presetActive ? presetVisible : null,
    });

  // Chargement via useBien avec le MÊME permRole que canSee/canEdit et que la
  // mutation : sinon le RPC strippe des champs visibles côté UI, et après
  // save+refetch le form repart à vide comme si le save n'avait pas pris.
  const { data: foundBien } = useBien(id, permRole);
  const bien = foundBien ?? {
    id: id || "", referenceInterne: "", projet: "", referenceExterne: "", codeImmoweb: "",
    typeBien: "", transaction: undefined, seraDispoPour: "", codePostal: "", ville: "", region: "",
    disponibilite: "", renonRecuLe: "", finDeContrat: "",
    cycleOuvertLe: null as string | null,
    entreeGestion: "rotation",
    collaborateurSrs: "",
    // companyContract + meuble omis volontairement : la form state mapping
    // utilise `bien.X ?? null` plus bas pour produire le tri-état attendu.
    propertyBox: "",
    etage: "", etagesTotal: "", surfaceHabitable: "", surfaceBrute: "", surfaceSalon: "",
    surfaceCuisine: "", nbChambres: "", nbSdb: "", nbDouches: "", nbToilettes: "",
    surfaceChambre1: "", equipements: [] as string[], surfaceTerrasse: "", surfaceJardin: "", loyerMensuel: "",
    chargesMensuelles: "", parkingLoyerMensuel: "", caveLoyerMensuel: "", garageLoyerMensuel: "",
    chargesIncluses: [] as string[], classePeb: "", certificatPeb: "", pebFinLe: "",
    consoKwh: "", emissionCo2: "", consoTheorique: "", chauffageEnergie: "", inspectionElecValide: "", expertPeb: "",
    pebDatePrevue: "", pebHeure: "", pebCommentaire: "",
    anneeConstruction: "", anneeRenovation: "", nbFacades: "", largeurFacade: "",
    etatBatiment: "", statutPublication: "Brouillon", photosOk: "", videoOk: "", textesOk: false, titreFr: "", descriptionFr: "",
    titreNl: "", descriptionNl: "",
    titreEn: "", descriptionEn: "",
    videoYoutubeUrl: "", alerteTexte: "", photos: [] as import("@/types/bien").BienPhoto[],
    prestataireEls: "", prestataireEle: "", edlDatePrevue: "", edlHeure: "",
    statutEle: "", eleNonFait: false,
    prestataireEleRefusePar: "", prestataireEleRefuseAt: null as string | null,
    // appartPretPhotos omis volontairement : tri-état null/true/false (cf. meuble pattern)
    // — bien.appartPretPhotos ?? null géré dans le mapping form state ci-dessous.
    pointsForts: "",
    // Workflow finalisation SRS — étape 1 (collab clôture) + étape 2 (CEO ferme).
    dossierCloture: false, dossierClos: false,
    // Un ELS est-il requis sur ce cycle ? (migration 106, NOT NULL DEFAULT true).
    // ⚠️ Doit figurer ici : l'état du formulaire est une LISTE BLANCHE, un champ
    // rendu mais absent de cet objet est vidé au premier enregistrement.
    elsRequis: true,
    // Jumeau PEB de elsRequis (migration 131) — même contrat, même raison.
    pebRequis: true,
  };


  // version = bien.updated_at : si la DB a été mise à jour depuis la dernière
  // session (re-import, autre admin, etc.), le sessionStorage est invalidé
  // automatiquement pour éviter d'écraser des données fraîches avec un form stale.
  const bienVersion = computeBienVersion(bien);
  const [data, setData] = useSessionState(`bien-${bien.id}`, {
    // ?? "" : imports Immoweb écrivent NULL (pas de correspondance catalogue) ;
    // jamais null ici, FormField/FreeTextAutocompleteField crashent sur null.length.
    referenceInterne: bien.referenceInterne ?? "",
    projet: bien.projet ?? "",
    referenceExterne: bien.referenceExterne,
    codeImmoweb: bien.codeImmoweb,
    typeBien: bien.typeBien,
    transaction: bien.transaction,
    disponibilite: ({ "EN PRÉPARATION": "en_preparation", "DISPONIBLE": "disponible", "LOUÉ": "loue", "VENDU": "vendu" } as Record<string,string>)[bien.disponibilite ?? ""] ?? "",
    seraDispoPour: bien.seraDispoPour,
    renonRecuLe: bien.renonRecuLe,
    // Cycle ouvert le… (migration 135) : null = aucun cycle. Jamais "" —
    // colonne timestamptz. ⚠️ L'état du formulaire est une LISTE BLANCHE :
    // absent d'ici, le champ serait vidé au premier enregistrement.
    cycleOuvertLe: (bien as { cycleOuvertLe?: string | null }).cycleOuvertLe ?? null,
    // Défaut 'rotation' (rows pré-081 = NULL) : comportement historique.
    entreeGestion: (bien as { entreeGestion?: string | null }).entreeGestion ?? "rotation",
    finDeContrat: bien.finDeContrat,
    // Sous option (overlay pause de vitrine, cf. lib/sousOption.ts). Reset en ""
    // pour la date (jamais null : DateField/read-only crashent sur null.length).
    sousOption: (bien as { sousOption?: boolean | null }).sousOption ?? false,
    sousOptionDepuis: (bien as { sousOptionDepuis?: string | null }).sousOptionDepuis ?? "",
    sousOptionStatutAvant: (bien as { sousOptionStatutAvant?: string | null }).sousOptionStatutAvant ?? "",
    collaborateurSrs: bien.collaborateurSrs,
    companyContract: bien.companyContract ?? null,
    adresse: bien.adresse ?? "",
    propertyBox: bien.propertyBox ?? "",
    codePostal: bien.codePostal,
    ville: bien.ville,
    region: (bien.region ?? "").toLowerCase(),
    // quartier_id est la colonne DB; `quartier` est un alias pour le form.
    // Le schéma Zod (BienRow) expose quartier_id.
    quartier: (bien as { quartier_id?: string | null }).quartier_id ?? "",
    etage: bien.etage,
    etagesTotal: bien.etagesTotal,
    typeEnvironnement: bien.typeEnvironnement ?? "",
    // meuble + companyContract : tri-state (null = pas saisi, true/false = explicite)
    // pour permettre la validation orange "champ mailing manquant" tant que le
    // user n'a pas tranché. Cf. lib/bienPrepValidation.ts. Avant : coerce en
    // false → impossible de distinguer "Non" explicite de "pas encore touché".
    meuble: bien.meuble ?? null,
    surfaceHabitable: bien.surfaceHabitable,
    surfaceBrute: bien.surfaceBrute,
    surfaceSalon: bien.surfaceSalon,
    // ?? "" : idem — import Immoweb écrit NULL en l'absence de cette ligne.
    surfaceCuisine: bien.surfaceCuisine ?? "",
    nbChambres: bien.nbChambres,
    nbSdb: bien.nbSdb,
    // ?? "" : imports Immoweb écrivent NULL quand "Salles de douche" est
    // absent de l'annonce — jamais null, FormField crashe sur null.length.
    nbDouches: bien.nbDouches ?? "",
    nbToilettes: bien.nbToilettes,
    typeSol: bien.typeSol ?? "",
    // `surfacesChambres` est le MIROIR lu par le mapper Immoweb. Depuis le
    // passage au répéteur, `chambresDetail` en est la source de vérité : dès
    // qu'il est rempli, le miroir en est dérivé. Sans cette priorité, la logique
    // historique ci-dessous (pad/coupe à `nbChambres`) reprenait la main à
    // l'hydratation et renvoyait des surfaces VIDES à Immoweb alors que le
    // répéteur affichait les bonnes valeurs.
    surfacesChambres: (() => {
      const detail = bien.chambresDetail;
      if (Array.isArray(detail) && detail.length > 0) {
        return detail.map((r) => r?.surface ?? "");
      }
      // Rows historiques (aucun répéteur saisi) : tableau persistant
      // `surfacesChambres` (cf. migration 035), fallback `surfaceChambre1` pour
      // les rows pré-migration, pad/coupe à `nbChambres`.
      const stored = (bien.surfacesChambres ?? null) as string[] | null;
      const target = Number(bien.nbChambres) || 0;
      const base = stored && stored.length > 0
        ? stored
        : Array.from({ length: target }, (_, i) => i === 0 ? (bien.surfaceChambre1 || "") : "");
      if (base.length === target) return base;
      if (base.length > target) return base.slice(0, target);
      return [...base, ...Array.from({ length: target - base.length }, () => "")];
    })(),
    // Répéteurs chambres / terrasses. À DÉCLARER ICI IMPÉRATIVEMENT : l'état du
    // formulaire est une liste BLANCHE explicite, et `useSessionState` repart de
    // cet objet à chaque changement de version (= à chaque save, puisque
    // `computeBienVersion` suit `updated_at`). Un champ absent d'ici est donc
    // vidé du form après le premier enregistrement — les surfaces reviendraient
    // via le miroir `surfacesChambres`, mais les ORIENTATIONS seraient perdues
    // silencieusement, puis réécrites vides au save suivant.
    chambresDetail: [...(bien.chambresDetail ?? [])],
    terrasses: [...(bien.terrasses ?? [])],
    equipements: [...(bien.equipements ?? [])],
    surfaceTerrasse: bien.surfaceTerrasse,
    surfaceJardin: bien.surfaceJardin,
    caveDisponible: bien.caveDisponible ?? "",
    caveNumero: bien.caveNumero ?? "",
    parkingDisponible: bien.parkingDisponible ?? "",
    parkingNumero: bien.parkingNumero ?? "",
    parkingFerme: bien.parkingFerme ?? false,
    parkingInterieur: bien.parkingInterieur ?? false,
    garageDisponible: bien.garageDisponible ?? "",
    garageNumero: bien.garageNumero ?? "",
    loyerMensuel: bien.loyerMensuel,
    chargesMensuelles: bien.chargesMensuelles,
    parkingLoyerMensuel: bien.parkingLoyerMensuel,
    caveLoyerMensuel: bien.caveLoyerMensuel,
    garageLoyerMensuel: bien.garageLoyerMensuel ?? "",
    chargesIncluses: [...(bien.chargesIncluses || [])],
    classePeb: bien.classePeb,
    // ?? "" : imports Immoweb écrivent NULL quand le "numéro" PEB n'est que la
    // lettre de classe répétée (pas un vrai numéro) — FormField crashe sur null.length.
    certificatPeb: bien.certificatPeb ?? "",
    pebFinLe: bien.pebFinLe,
    consoKwh: bien.consoKwh,
    emissionCo2: bien.emissionCo2 ?? "",
    consoTheorique: bien.consoTheorique ?? "",
    chauffageEnergie: bien.chauffageEnergie ?? "",
    inspectionElecValide: bien.inspectionElecValide ?? "",
    expertPeb: bien.expertPeb,
    pebDatePrevue: bien.pebDatePrevue ?? "",
    pebHeure: bien.pebHeure ?? "",
    pebCommentaire: bien.pebCommentaire ?? "",
    statutPeb: bien.statutPeb ?? "",
    nouvClassePeb: bien.nouvClassePeb ?? "",
    nouvCertificatPeb: bien.nouvCertificatPeb ?? "",
    nouvPebFinLe: bien.nouvPebFinLe ?? "",
    nouvConsoKwh: bien.nouvConsoKwh ?? "",
    nouvEmissionCo2: bien.nouvEmissionCo2 ?? "",
    nouvConsoTheorique: bien.nouvConsoTheorique ?? "",
    nouvChauffageEnergie: bien.nouvChauffageEnergie ?? "",
    nouvPebCertificatPdf: bien.nouvPebCertificatPdf ?? "",
    anneeConstruction: bien.anneeConstruction,
    anneeRenovation: bien.anneeRenovation,
    nbFacades: bien.nbFacades,
    largeurFacade: bien.largeurFacade ?? "",
    etatBatiment: bien.etatBatiment,
    // ?? au lieu de || : un bien avec statutPublication="" en DB conserve "",
    // seul un nouveau bien (undefined) reçoit "Brouillon" par défaut.
    statutPublication: bien.statutPublication ?? "Brouillon",
    photosOk: bien.photosOk || "",
    videoOk: bien.videoOk || "",
    textesOk: bien.textesOk,
    titreFr: bien.titreFr,
    descriptionFr: bien.descriptionFr,
    titreNl: bien.titreNl,
    descriptionNl: bien.descriptionNl,
    titreEn: bien.titreEn ?? "",
    descriptionEn: bien.descriptionEn ?? "",
    pointsForts: bien.pointsForts ?? "",
    videoYoutubeUrl: bien.videoYoutubeUrl ?? "",
    alerteTexte: bien.alerteTexte ?? "",
    photos: bien.photos ?? [],
    // Locataires
    ancienLocataireNom: "",
    ancienLocataireEmail: "",
    ancienLocataireTel: "",
    ancienLocataireDateDebut: "",
    ancienLocataireDateFin: "",
    ancienLocataireLoyerInitial: "",
    ancienLocataireLoyerActuel: "",
    ancienLocataireChargesInitiales: "",
    ancienLocataireChargesActuelles: "",
    ancienLocataireIndexBaseYear: "",
    nouveauLocataireNom: "",
    nouveauLocataireEmail: "",
    nouveauLocataireTel: "",
    nouveauLocataireDateDebut: "",
    nouveauLocataireDateFin: "",
    nouveauLocataireLoyerInitial: "",
    nouveauLocataireLoyerActuel: "",
    nouveauLocataireChargesInitiales: "",
    nouveauLocataireChargesActuelles: "",
    nouveauLocataireIndexBaseYear: "",
    // État des lieux
    // `prestataireEls` = prestataire PRÉCÉDENT (trace), `prestataireEle` =
    // prestataire ASSIGNÉ. Les colonnes portent le nom de l'autre état des
    // lieux ; leur renommage attend la table `cycles`.
    prestataireEls: bien.prestataireEls ?? "",
    prestataireEle: bien.prestataireEle,
    // Migration 055 — trace du refus prestataire pour la bannière "ELS refusé"
    prestataireEleRefusePar: bien.prestataireEleRefusePar ?? "",
    prestataireEleRefuseAt: bien.prestataireEleRefuseAt ?? null,
    // Migration 068 — trace du refus de l'expert PEB (symétrique ELS).
    expertPebRefusePar: bien.expertPebRefusePar ?? "",
    expertPebRefuseAt: bien.expertPebRefuseAt ?? null,
    edlDatePrevue: bien.edlDatePrevue,
    edlHeure: bien.edlHeure,
    statutEle: bien.statutEle,
    edlCommentaire: bien.edlCommentaire ?? "",
    eleNonFait: bien.eleNonFait,
    // Migration 048 — date dédiée saisie par le prestataire (EtatDesLieuxDetailPage)
    // OU par le CEO/admin via ce formulaire. Sans ce field ici, le CEO devait
    // ouvrir la vue prestataire pour saisir la date → friction.
    transfertElecDate: bien.transfertElecDate ?? "",
    appartPretPhotos: bien.appartPretPhotos ?? null,
    // `?? true` et non `?? null` : la colonne est NOT NULL DEFAULT true en base.
    // Le `??` ne couvre que le cas où la RPC l'a strippée pour un rôle sans
    // droit de lecture — auquel cas on retombe sur la valeur par défaut plutôt
    // que de faire croire à un « non ».
    elsRequis: bien.elsRequis ?? true,
    pebRequis: bien.pebRequis ?? true,
    // Finalisation SRS — le décompte se résume au total (décision 2026-08-10).
    // Les six montants détaillés (montantEls, montantPreavis, montantDommages,
    // arrieresLoyer, decompteCharges, autreMontant) ne sont plus dans cette
    // liste blanche : la fiche ne les rend pas, elle ne doit donc pas les
    // renvoyer en écriture. Migration 107 les supprime en base.
    indemnitePreavis: bien.indemnitePreavis ?? "",
    garantieLiberee: bien.garantieLiberee ?? "",
    totalSrs: bien.totalSrs ?? "",
    dossierCloture: bien.dossierCloture ?? false,
    dossierClos: bien.dossierClos ?? false,
    commentaireSrs: bien.commentaireSrs ?? "",
  }, bienVersion);

  // Tous les edits utilisateur passent par ce `set` (ou `toggle`). Ils flaggent
  // hasEdited=true explicitement. Les autres setData (sync locataires, transition
  // stub→real depuis React Query, etc.) ne flaggent PAS → pas d'autosave parasite.
  const [hasEdited, setHasEdited] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<"idle" | "saved" | "error">("idle");
  /** Dernier état refusé par la base — garde-fou anti-boucle de l'autosave debouncé. */
  const lastRejectedData = useRef<typeof data | null>(null);
  /**
   * Toujours le DERNIER `data`, lisible depuis un `onSuccess` async (audit
   * 2026-09-10 §5 : sauver A, taper B pendant l'envoi, le succès de A
   * remettait `hasEdited=false` sans vérifier que B n'avait pas remplacé A
   * entre-temps — B restait affiché mais jamais persisté). Voir `handleSave`.
   */
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  // Cible en attente de confirmation lors d'un changement de "Prestataire assigné".
  const [eleChangeTarget, setEleChangeTarget] = useState<string | null>(null);
  const set = (key: string) => (value: string) => {
    setData((prev) => ({ ...prev, [key]: value }) as typeof prev);
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  // Setter spécifique pour les booleans tri-état (meuble, companyContract).
  // Le SelectField nous envoie une string ("oui" / "non" / ""), on convertit en
  // boolean | null avant de stocker dans le form state. Cf. lib/bienPrepValidation.ts
  // pour la logique isFilled : null → manquant (orange), true/false → rempli.
  const setBool = (key: string) => (s: string) => {
    const next: boolean | null = s === "oui" ? true : s === "non" ? false : null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setData((prev) => ({ ...prev, [key]: next }) as any);
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  // Mapping bool|null|undefined → string pour l'affichage SelectField.
  const boolToOption = (v: unknown): string =>
    v === true ? "oui" : v === false ? "non" : "";

  // Fields visible in the "Apartments for Rent" detail view (SuiviEleDetailPage)
  // Highlighted in red when role = c_level to show which fields are exposed to that view
  const ELE_FIELDS = new Set([
    // Identification
    "referenceInterne", "projet",
    "meuble", "seraDispoPour", "finDeContrat", "renonRecuLe", "companyContract",
    // Localisation
    "ville", "adresse", "etage",
    // Intérieur
    "nbChambres", "nbSdb", "typeSol",
    // Suivi locatif
    "disponibilite",
    // Extérieur
    "caveDisponible", "caveNumero", "caveLoyerMensuel",
    "parkingDisponible", "parkingNumero", "parkingLoyerMensuel",
    "garageDisponible", "garageNumero", "garageLoyerMensuel",
    // Financier
    "chargesMensuelles", "chargesIncluses",
    // Énergie
    "consoKwh", "classePeb", "pebFinLe", "expertPeb",
  ]);
  const hl = (key: string) => role === "c_level" && ELE_FIELDS.has(key);

  const toggle = (key: string) => () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setData((prev) => ({ ...prev, [key]: !(prev as any)[key] }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  /**
   * ELS requis (migration 106). DÉCOCHER change le déroulement du cycle — plus
   * de clôture financière, plus de question photos, et le collaborateur cesse
   * d'être obligatoire — donc modale de confirmation. RECOCHER est le retour à
   * la normale : immédiat, rien à protéger.
   */
  const [elsRequisConfirmOpen, setElsRequisConfirmOpen] = useState(false);
  /** Cycle explicitement déclaré sans ELS — pilote les retraits ci-dessous. */
  // En mode droits, JAMAIS « sans ELS » : la branche clôture doit rester
  // visible pour qu'on puisse régler les droits de ses champs.
  const sansEls = !permsMode && data.elsRequis === false;
  const sansPeb = !permsMode && data.pebRequis === false;
  // Transfert élec fait par le prestataire = condition de clôture SRS
  // (signalé Thibaud 2026-08-20 : le collab pouvait clôturer le SRS depuis la
  // fiche complète alors que l'électricité n'était pas transférée). Même
  // check que `isTransfertElecFait` (CheckElsBadges, badge "Élec") :
  // `setTransfertElecDate` lève TOUJOURS `transfertElecFait` en même temps
  // que la date, donc la date seule suffit ici — `data` (form state local)
  // ne type pas `transfertElecFait`. Ignoré si le bien ne nécessite pas
  // d'ELS (`sansEls`), comme `appartPretPhotos`.
  const elecFaitPourCloture = sansEls || !!(data.transfertElecDate && data.transfertElecDate.trim());
  const setElsRequis = (v: boolean) => {
    setData((prev) => ({ ...prev, elsRequis: v }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };
  const handleElsRequisChange = (checked: boolean) => {
    if (checked) return setElsRequis(true);
    setElsRequisConfirmOpen(true);
  };
  /** Jumeau PEB de `setElsRequis` — pas de modale de confirmation ici :
   *  décocher ne fait que masquer la carte "Renouvellement PEB", le
   *  certificat en vigueur reste de toute façon éditable directement (rien
   *  à protéger, contrairement à `elsRequis` qui retire une clôture
   *  financière entière). */
  const setPebRequis = (v: boolean) => {
    setData((prev) => ({ ...prev, pebRequis: v }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  // Sous option (overlay pause de vitrine, cf. lib/sousOption.ts). Cocher est une
  // action de prévention (dépublication du bien) → modale de confirmation.
  // Décocher lève la pause immédiatement (réversible sans paperasse).
  const [sousOptionConfirmOpen, setSousOptionConfirmOpen] = useState(false);
  const [sousOptionUncheckOpen, setSousOptionUncheckOpen] = useState(false);

  const onToggleSousOption = (checked: boolean) => {
    // Les deux sens passent par une modale de confirmation : cocher dépublie,
    // décocher remet en ligne (action visible côté public dans les deux cas).
    if (checked) {
      setSousOptionConfirmOpen(true);
    } else {
      setSousOptionUncheckOpen(true);
    }
  };

  // Confirmation du décochage : on lève la pause. On ne remet en ligne QUE si
  // c'est la mise sous option qui avait dépublié (sousOptionStatutAvant mémorisé)
  // — cf. sousOptionExitPatch (blindage du bien retiré manuellement).
  const confirmUnsetSousOption = () => {
    setData((prev) => ({
      ...prev,
      ...sousOptionExitPatch((prev as { sousOptionStatutAvant?: string | null }).sousOptionStatutAvant),
    }) as typeof prev);
    setHasEdited(true);
    setSaveFeedback("idle");
    setSousOptionUncheckOpen(false);
  };

  // Confirmation de la mise sous option : horodate l'entrée (base du rappel
  // C-Level) et DÉPUBLIE (miroir du comportement LOUÉ/VENDU : si le bien était
  // Publié → Retiré, ce qui le sort du site vendor et d'Immoweb ; demo mode
  // Immoweb inchangé). On mémorise le statut d'avant (sousOptionEnterPatch). Le
  // suivi ELS/locataire n'est PAS touché.
  const confirmSousOption = () => {
    setData((prev) => ({
      ...prev,
      ...sousOptionEnterPatch(
        (prev as { statutPublication?: string | null }).statutPublication,
        todayIso(),
        (prev as { sousOptionDepuis?: string | null }).sousOptionDepuis,
      ),
    }) as typeof prev);
    setHasEdited(true);
    setSaveFeedback("idle");
    setSousOptionConfirmOpen(false);
  };

  // Si le bien quitte l'éligibilité (statut ≠ disponible ou transaction ≠
  // location), on retire automatiquement le « sous option » — cohérent avec le
  // verrouillage de la case dans l'UI.
  useEffect(() => {
    if (
      (data as { sousOption?: boolean | null }).sousOption &&
      !canToggleSousOption({ transaction: data.transaction, disponibilite: data.disponibilite })
    ) {
      // Sortie d'éligibilité (loué/vendu, changement de transaction) : on lève le
      // flag SANS republier — le statut publication est piloté par ailleurs
      // (loué/vendu → Retiré via confirmLoueVendu / trigger 065). On vide la
      // mémoire pour ne pas republier plus tard par erreur.
      setData((prev) => ({ ...prev, sousOption: false, sousOptionDepuis: "", sousOptionStatutAvant: "" }) as typeof prev);
      setHasEdited(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.transaction, data.disponibilite]);

  // Quartiers depuis la vraie DB (anciennement MOCK_QUARTIERS)
  const { data: quartiersData = [] } = useQuartiers();

  // Suggestions du champ "Projet" : valeurs distinctes déjà saisies sur d'autres
  // biens. Permet d'éviter de recréer un projet par typo (Casse, espaces).
  const { data: allBiens = [] } = useBiens();
  const projetSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const b of allBiens) {
      const v = (b.projet ?? "").trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out.sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [allBiens]);

  const handleCodePostalChange = (value: string) => {
    setData((prev) => {
      const updated = { ...prev, codePostal: value };
      const city = getCityFromPostalCode(value);
      const region = getRegionFromPostalCode(value);
      if (city) updated.ville = city;
      if (region) updated.region = region;
      // Reset quartier if it no longer matches the new postal code
      const matchingQuartiers = quartiersData.filter((q) =>
        (q.codesPostaux ?? []).some((cp) => cp.code === value)
      );
      if (!matchingQuartiers.some((q) => q.id === prev.quartier)) {
        updated.quartier = "";
      }
      return updated;
    });
  };

  // Reverse lookup : quand l'utilisateur tape une commune, on remplit le code
  // postal et la région automatiquement. Même UX que quand il tape un CP.
  const handleVilleChange = (value: string) => {
    setData((prev) => {
      const updated = { ...prev, ville: value };
      const code = getPostalCodeFromCity(value);
      if (code) {
        updated.codePostal = code;
        const region = getRegionFromPostalCode(code);
        if (region) updated.region = region;
        // Reset quartier si le nouveau CP ne colle plus avec le quartier
        // sélectionné (même logique que handleCodePostalChange).
        const matchingQuartiers = quartiersData.filter((q) =>
          (q.codesPostaux ?? []).some((cp) => cp.code === code)
        );
        if (!matchingQuartiers.some((q) => q.id === prev.quartier)) {
          updated.quartier = "";
        }
      }
      return updated;
    });
  };

  // Quartier options filtered by current postal code
  const quartierOptions = useMemo(() => {
    const cp = data.codePostal;
    if (!cp) return [{ value: "", label: "—" }];
    const matching = quartiersData.filter((q) =>
      (q.codesPostaux ?? []).some((p) => p.code === cp)
    );
    return [
      { value: "", label: "—" },
      ...matching.map((q) => ({ value: q.id, label: q.nom ?? q.id })),
    ];
  }, [data.codePostal, quartiersData]);

  // PEB alert: show if class is empty.
  // Note : ancienne règle "PEB émis > 10 ans" supprimée — Rentio fournit
  // désormais la date de fin (pebFinLe), pas la date d'émission.
  const pebAlert = useMemo(() => {
    return !data.classePeb;
  }, [data.classePeb]);

  // Collaborateurs SRS options — tapés sur la vraie table `profiles` (anciennement MOCK_TEAM)
  const { data: profiles = [] } = useProfiles();
  const collaborateurOptions = useMemo(() => {
    return profiles
      .filter((p) => p.role === "collaborateur" && p.statut !== "inactif")
      .map((p) => ({
        value: p.id,
        label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
      }));
  }, [profiles]);

  // Les selectors ELS / ELE / Expert PEB tapent sur la table `profiles`
  // (membres de l'équipe avec role='prestataire') filtrée par spécialité,
  // pas sur la table `prestataires` (qui contient des sociétés). L'utilisateur
  // veut pouvoir assigner une PERSONNE de son équipe, pas un fournisseur.
  const buildProfileOptions = (
    metierId: string,
    specials: { amiable?: boolean; autre?: boolean } = {},
  ) => {
    const matching = profiles
      .filter((p) => p.role === "prestataire")
      .filter((p) => p.statut !== "inactif")
      .filter((p) => (p.specialites ?? []).includes(metierId))
      .map((p) => ({
        value: p.id,
        label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      ...matching,
      ...(specials.amiable ? [{ value: "amiable", label: t("biens.expertiseAmiable") }] : []),
      ...(specials.autre ? [{ value: "autre", label: t("common.other") }] : []),
    ];
  };

  // Expert PEB : experts certifiés PEB + "Autre" (pas d'amiable — un expert
  // PEB ne peut pas être "à l'amiable")
  const expertPebOptions = useMemo(
    () => buildProfileOptions("expert-peb", { autre: true }),
    [profiles, t],
  );
  // ELS / ELE : experts état des lieux + "Expertise amiable" + "Autre"
  const elsOptions = useMemo(
    () => buildProfileOptions("etat-des-lieux", { amiable: true, autre: true }),
    [profiles, t],
  );
  const eleOptions = elsOptions;

  // "Prestataire précédent" (prestataireEls) = TRACE pure (modèle 2026-06-06) :
  // ne touche QUE prestataireEls, jamais l'assigné actif. Ne déclenche rien.
  const handleElsChange = (value: string) => {
    setData((prev) => ({ ...prev, prestataireEls: value }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  // Applique le changement de "Prestataire assigné" (prestataireEle). clearRdv=true
  // → vide date/heure/statut ELS (l'ancien créneau appartenait au prestataire
  // précédent ; le nouveau replanifie). (Ré)assigner lève aussi un refus en attente.
  const applyEleChange = (value: string, clearRdv: boolean) => {
    setData((prev) => ({
      ...prev,
      prestataireEle: value,
      ...(clearRdv ? { edlDatePrevue: "", edlHeure: "", statutEle: "" } : {}),
      ...(value && (prev.prestataireEleRefusePar || prev.prestataireEleRefuseAt)
        ? { prestataireEleRefusePar: "", prestataireEleRefuseAt: null }
        : {}),
    }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  // Changement de l'assigné : si un prestataire réel était déjà assigné, on demande
  // confirmation (l'ancien sera prévenu de l'annulation s'il avait été notifié — via
  // trigger DB migration 076). Sinon (1ʳᵉ assignation), on applique directement.
  const handleEleChange = (value: string) => {
    const old = (data.prestataireEle ?? "").trim();
    const oldReal = !!old && old !== "amiable" && old !== "autre";
    if (oldReal && value !== data.prestataireEle) {
      setEleChangeTarget(value);
    } else {
      applyEleChange(value, false);
    }
  };

  // Réassignation de l'expert PEB : (ré)assigner lève un refus PEB en attente
  // (migration 068, symétrique handleEleChange) — sinon le bien resterait
  // invisible pour le nouvel expert PEB.
  const handleExpertPebChange = (value: string) => {
    setData((prev) => ({
      ...prev,
      expertPeb: value,
      ...(value && (prev.expertPebRefusePar || prev.expertPebRefuseAt)
        ? { expertPebRefusePar: "", expertPebRefuseAt: null }
        : {}),
    }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  // Safe array accessors — guards against stale sessionStorage with wrong types
  const safeChargesIncluses = (): string[] =>
    Array.isArray(data.chargesIncluses) ? data.chargesIncluses : [];
  const safeEquipements = (): string[] =>
    Array.isArray(data.equipements) ? data.equipements : [];

  /**
   * Répéteurs chambres / terrasses (spec « fiche en onglets ») — remplacent la
   * saisie à plat « Surface chambre N » et le champ unique `surfaceTerrasse`.
   * L'affichage absorbe les colonnes historiques (cf. hydrateRepeaterRows) et
   * l'écriture réaligne les miroirs lus par le mapper Immoweb.
   */
  const repeaterRows = (key: "chambresDetail" | "terrasses"): SurfaceOrientation[] =>
    hydrateRepeaterRows(key, data);

  const setRepeaterRows =
    (key: "chambresDetail" | "terrasses") => (next: SurfaceOrientation[]) => {
      setData((prev) => ({ ...prev, ...buildRepeaterPatch(key, next) }) as typeof prev);
      setHasEdited(true);
      setSaveFeedback("idle");
    };

  // setHasEdited/setSaveFeedback explicites : sans eux, cocher un équipement ne
  // déclenchait NI le save au blur NI l'autosave debouncé (les deux sont gardés
  // par `hasEdited`), donc la case retombait au rechargement. Même défaut que
  // les anciens handlers chambres, supprimés au profit du répéteur.
  const toggleEquipement = (eq: string) => {
    setData((prev) => {
      const eqs = Array.isArray(prev.equipements) ? prev.equipements : [];
      return {
        ...prev,
        equipements: eqs.includes(eq)
          ? eqs.filter((e: string) => e !== eq)
          : [...eqs, eq],
      };
    });
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  /** Coche / décoche une charge incluse. Même contrat que toggleEquipement :
   *  `setHasEdited` explicite, sinon ni le save au blur ni l'autosave ne partent. */
  const toggleChargeIncluse = (value: string) => {
    setData((prev) => {
      const cur = Array.isArray(prev.chargesIncluses) ? prev.chargesIncluses : [];
      return {
        ...prev,
        chargesIncluses: cur.includes(value)
          ? cur.filter((c: string) => c !== value)
          : [...cur, value],
      };
    });
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  const handleStatutPebChange = (value: string) => {
    if (value === "expertise_ok") {
      // Double-cast : useSessionState conserve une forme locale (Partial + surcharges)
      // qui ne correspond pas exactement au type Bien ; on passe par unknown pour
      // satisfaire les fonctions pebCycle qui attendent le type complet.
      if (!isPebReportComplete(data as unknown as Bien)) return; // gate : nouveaux champs requis
      const patch = promotePebCycle(data as unknown as Bien);
      setData((prev) => ({ ...prev, ...patch }) as typeof prev);
      setHasEdited(true); setSaveFeedback("idle");
      return;
    }
    if (value === "rapport_en_cours") { set("statutPeb")("rapport_en_cours"); return; }
    // états dérivés (à fixer / date fixée / date passée) → retour à l'automatique
    set("statutPeb")("");
  };

  const { role } = useRole();
  const { profile } = useAuth();
  const { pathname } = useLocation();

  // ───────────────────────── Multi-portails publication ────────────────────
  // Rôles autorisés à pousser des actions portails. La matrice elle-même reste
  // visible pour tout le monde (read-only) — seuls les boutons sont gated par
  // l'Edge Function côté backend (double-couche de sécurité).
  const ALLOWED_PORTAL_ROLES = ["admin", "c_level", "publication"];
  const canTriggerPortals = ALLOWED_PORTAL_ROLES.includes(role);

  // Modal de confirmation au passage Publié → Brouillon/Retiré.
  // Le bien sortirait de l'API vendor (Site Web) — assez impactant pour
  // qu'on demande une confirmation explicite et éviter les erreurs de manip.
  const [pendingDepublishStatut, setPendingDepublishStatut] = useState<string | null>(null);
  // Modal de confirmation au passage DISPONIBLE → LOUÉ/VENDU sur un bien Publié.
  // Le trigger DB (migration 065) force alors statutPublication = 'Retiré' → le
  // bien sort du site public. On avertit l'utilisateur avant.
  const [pendingLoueVendu, setPendingLoueVendu] = useState<string | null>(null);
  // Modale LOUÉ — saisie OBLIGATOIRE du locataire entrant + date du 1er jour de
  // bail (décision user 2026-07-10, relocation anticipée). Plus de choix
  // reset/conserver : le passage en LOUÉ ne touche PLUS au cycle de sortie —
  // le nettoyage se fait au prochain passage LOUÉ → DISPONIBLE.
  const [loueForm, setLoueForm] = useState({ nom: "", email: "", tel: "", dateDebut: "", collaborateurSrs: "" });
  // Modale LOUÉ → DISPONIBLE (départ du locataire) : confirme l'archivage du
  // locataire actuel + le démarrage d'un cycle de sortie neuf (reset ELS/PEB/SRS).
  const [dispoLibereOpen, setDispoLibereOpen] = useState(false);
  // Modale « vider la fiche de sortie » (bouton manuel admin/C-Level pour
  // rattraper une erreur de manip — cycle de sortie démarré à tort).
  const [viderSortieOpen, setViderSortieOpen] = useState(false);

  /**
   * Dialogue d'ouverture de cycle — le geste unique qui amorce une rotation.
   *
   * Avant, un cycle s'ouvrait par effet de bord : saisir « Renon reçu le »
   * quelque part dans la fiche suffisait. Les trois autres décisions du même
   * moment (suivi ELS ou non, quel collaborateur, quel expert PEB si le
   * certificat est périmé) se prenaient plus tard, ailleurs, ou jamais — d'où
   * des cycles ouverts sans collaborateur assigné.
   *
   * Le brouillon est local : rien n'est écrit tant que l'utilisateur n'a pas
   * validé. À la validation, un seul patch part vers `data`, donc un seul
   * autosave — pas quatre écritures successives.
   */
  const [ouvrirCycleOpen, setOuvrirCycleOpen] = useState(false);
  const [cycleDraft, setCycleDraft] = useState({
    renonRecuLe: "",
    finDeContrat: "",
    seraDispoPour: "",
    elsRequis: true,
    pebRequis: true,
    collaborateurSrs: "",
    prestataireEls: "",
    prestataireEle: "",
    expertPeb: "",
  });
  // Ref sur la card Locataires pour scroller dessus après un passage en loué.
  const locatairesSectionRef = useRef<HTMLDivElement>(null);

  const onStatutPublicationChange = (next: string) => {
    const current = data.statutPublication;
    if (current === next) return;
    // Garde-fou : on quitte Publié → on demande confirmation
    if (current === "Publié" && (next === "Brouillon" || next === "Retiré")) {
      setPendingDepublishStatut(next);
      return;
    }
    // Garde-fou publication (liste client 2026-06-11) : impossible de passer
    // en "Publié" tant que les critères ne sont pas remplis. Même prédicat
    // que le bouton Publier + le trigger DB (migration 082).
    if (next === "Publié") {
      const gate = isPublishable(data as unknown as PublishGateInput);
      if (!gate.ok) {
        toast.error(
          t("publishGate.blocked", {
            fields: gate.missing.map((k) => t(PUBLISH_GATE_LABEL_KEYS[k] ?? k)).join(", "),
          }),
        );
        return;
      }
    }
    set("statutPublication")(next);
  };

  const confirmDepublish = () => {
    if (pendingDepublishStatut) {
      set("statutPublication")(pendingDepublishStatut);
    }
    setPendingDepublishStatut(null);
  };

  const cancelDepublish = () => {
    setPendingDepublishStatut(null);
  };

  // Intercepte le changement de disponibilité : si on passe un bien Publié en
  // LOUÉ/VENDU, on prévient qu'il sera retiré du site public (cohérent avec le
  // trigger DB qui force statutPublication = 'Retiré').
  const onDisponibiliteChange = (next: string) => {
    if (data.disponibilite === next) return;
    const prev = data.disponibilite;
    // Loué : modale systématique — saisie obligatoire du locataire entrant +
    // date de bail. Annuler = rien ne bouge (c'est LE garde-fou anti-erreur,
    // pas de retour arrière prévu). Pré-rempli si un locataire actuel est déjà
    // saisi (ex. encodé pendant la négociation).
    if (next === "loue") {
      setLoueForm({
        nom: data.nouveauLocataireNom || "",
        email: data.nouveauLocataireEmail || "",
        tel: data.nouveauLocataireTel || "",
        dateDebut: data.nouveauLocataireDateDebut || "",
        // Pré-rempli avec l'assignation actuelle (jamais resetée par ailleurs,
        // cf. elsResetCycleForLoue) : le C-Level confirme ou réassigne
        // explicitement à CE moment précis, plutôt que de laisser une
        // ancienne valeur traîner silencieusement d'un cycle à l'autre
        // (demande user 2026-08-19).
        collaborateurSrs: data.collaborateurSrs || "",
      });
      setPendingLoueVendu("loue");
      return;
    }
    // Vendu : on avertit seulement si le bien est publié (retrait du site, pas de reset).
    if (next === "vendu" && data.statutPublication === "Publié") {
      setPendingLoueVendu("vendu");
      return;
    }
    // LOUÉ → DISPONIBLE (renon reçu, le bien se libère) : modale de confirmation
    // — c'est ICI que le locataire actuel est archivé et que le cycle de sortie
    // repart à neuf (décision user 2026-07-10 : plus aucun reset au passage LOUÉ).
    if (prev === "loue" && next === "disponible") {
      setDispoLibereOpen(true);
      return;
    }
    set("disponibilite")(next);
  };

  const confirmLoueVendu = () => {
    if (pendingLoueVendu) {
      const wasPublie = data.statutPublication === "Publié";
      // Passage LOUÉ : AUCUN reset du cycle de sortie (relocation anticipée —
      // l'ELS/PEB du locataire sortant continue, cf. hasExitCycleInFlight).
      // Le locataire entrant saisi dans la modale est écrit dans les champs
      // form nouveauLocataire* → le debounced save crée/actualise la row
      // locataires type='actuel'.
      const tenantPatch =
        pendingLoueVendu === "loue"
          ? {
              nouveauLocataireNom: loueForm.nom.trim(),
              nouveauLocataireEmail: loueForm.email.trim(),
              nouveauLocataireTel: loueForm.tel.trim(),
              nouveauLocataireDateDebut: loueForm.dateDebut,
              collaborateurSrs: loueForm.collaborateurSrs,
            }
          : {};
      setData((prev) => ({
        ...prev,
        ...tenantPatch,
        disponibilite: pendingLoueVendu,
        // Miroir du trigger DB (065) UNIQUEMENT si le bien était publié.
        ...(wasPublie ? { statutPublication: "Retiré" } : {}),
      }) as typeof prev);
      setHasEdited(true);
      setSaveFeedback("idle");
      // On amène la fiche complète du locataire sous les yeux (compléter
      // loyers/charges) — l'onglet Locataires n'étant monté QUE quand il est
      // actif, `locatairesSectionRef` restait `null` et le scroll ne faisait
      // rien tant qu'on n'était pas déjà sur cet onglet (signalé Thibaud
      // 2026-08-20 : "amène-moi sur la fiche entière du locataire"). Bascule
      // d'onglet d'abord, scroll ensuite une fois la section montée.
      if (pendingLoueVendu === "loue") {
        setActiveTab("locataires");
        setTimeout(() => {
          locatairesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    }
    setPendingLoueVendu(null);
  };

  const cancelLoueVendu = () => {
    setPendingLoueVendu(null);
  };

  // Confirmation LOUÉ → DISPONIBLE : archive le locataire actuel (→ ancien,
  // dateFin = finDeContrat sinon today) PUIS remet le cycle de sortie à neuf
  // pour la nouvelle sortie (resetElsCycleForLoue — historiquement appliqué au
  // passage LOUÉ, déplacé ici le 2026-07-10). Ordre important : l'archivage lit
  // les champs nouveauLocataire* AVANT le reset.
  const confirmDispoLibere = () => {
    const archived = transferLocataireToAncien({ archivedDateFin: data.finDeContrat || todayIso() });
    if (archived) toast.success(t("biens.locataireArchiveAuto"));
    const resetPatch = resetElsCycleForLoue({ prestataireEle: (data as { prestataireEle?: string }).prestataireEle ?? "" });
    setData((prev) => ({
      ...prev,
      ...resetPatch,
      disponibilite: "disponible",
    }) as typeof prev);
    setHasEdited(true);
    setSaveFeedback("idle");
    setDispoLibereOpen(false);
    // Le bien vient de se libérer : la suite logique est d'ouvrir un cycle
    // pour la prochaine location (demande Thibaud 2026-08-20 : "amène-moi sur
    // le 2e onglet avec popup ouverture de cycle"). On rejoue ici la logique
    // d'`openCycleDialog()`, PAS un appel direct : `data` dans cette closure
    // ne reflète pas encore le `setData` ci-dessus (asynchrone), et
    // `resetPatch` vide justement `prestataireEle` (→ `prestataireEls`) ET
    // les 3 dates du cycle (renonRecuLe/finDeContrat/seraDispoPour) — les
    // seeder depuis `data` telle quelle rouvrirait le dialogue avec les
    // valeurs du cycle PRÉCÉDENT encore affichées (même bug que "elles
    // sortent d'où ?", corrigé en même temps dans `resetElsCycleForLoue`).
    setActiveTab("cycle");
    setCycleDraft({
      renonRecuLe: resetPatch.renonRecuLe ?? "",
      finDeContrat: resetPatch.finDeContrat ?? "",
      seraDispoPour: resetPatch.seraDispoPour ?? "",
      elsRequis: data.elsRequis !== false,
      pebRequis: data.pebRequis !== false,
      collaborateurSrs: data.collaborateurSrs ?? "",
      prestataireEls: resetPatch.prestataireEls ?? "",
      prestataireEle: resetPatch.prestataireEle ?? "",
      expertPeb: data.expertPeb ?? "",
    });
    setOuvrirCycleOpen(true);
  };

  // Bouton manuel « vider la fiche de sortie » (erreur de manip : cycle de
  // sortie démarré à tort). Même patch que le reset de cycle, sans toucher aux
  // locataires ni à la disponibilité.
  const confirmViderSortie = () => {
    setData((prev) => ({
      ...prev,
      ...resetElsCycleForLoue({ prestataireEle: (prev as { prestataireEle?: string }).prestataireEle ?? "" }),
    }) as typeof prev);
    setHasEdited(true);
    setSaveFeedback("idle");
    setViderSortieOpen(false);
  };

  // Click sur le bouton Publier de la section Publication : bump
  // `last_published_at = now()` côté DB (RPC SECURITY DEFINER, cf. 045).
  // Ça fait passer la pastille de orange "à pousser" → vert "Publié".
  // Décision produit 2026-05-07 : action directe sans modal de
  // confirmation — pas de validation Immoweb pour MVP, on commence par
  // faire fonctionner le Site Web (qui pull la DB via API vendor RLS-filtrée).
  const toast = useToast();
  const { mutate: markAsPublished, isPending: isMarkingPublished } = useMarkBienAsPublished(bien.id);
  // Pousse aussi vers les portails externes activés (Immoweb — spotto/zimmo
  // pas encore, cf. `PORTALS.enabled`). Édge function réelle depuis le
  // 2026-09-12 (elle existait et fonctionnait déjà côté serveur — testée
  // manuellement le 2026-06-30 avec 27 succès sandbox — mais rien côté UI ne
  // l'appelait depuis la simplification du bouton "Publier" du 2026-05-07).
  // Uniquement après succès du site web : pas la peine de pousser vers
  // Immoweb un bien que `mark_bien_as_published` vient de refuser.
  const { mutate: publishPortal, isPending: isPortalPending } = usePortalPublishMutation(bien.id);
  const handlePublishClick = () => {
    if (isMarkingPublished) return;
    markAsPublished(undefined, {
      onSuccess: () => {
        toast.success(t("portals.publishSuccessShort"));
        for (const portal of externalEnabledPortals()) {
          publishPortal(
            { portal: portal.id, action: "publish" },
            {
              onError: (e) =>
                toast.error(`${portal.label} : ${e instanceof Error ? e.message : String(e)}`),
            },
          );
        }
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
    });
  };

  /**
   * Dépublication (chantier 2026-09-14, feu vert Thibaud) : jusqu'ici,
   * repasser un bien Publié en Retiré/Brouillon — via le déroulant, via LOUÉ/
   * VENDU (qui force Retiré, miroir du trigger DB 065 dans `confirmLoueVendu`
   * plus bas), ou via une mise sous option (miroir du trigger 084, cf.
   * `sousOption.ts`) — ne notifiait JAMAIS Immoweb : l'annonce restait live
   * indéfiniment côté portail. Ces trois chemins écrivent tous
   * `statutPublication` puis passent par le MÊME `handleSave`/autosave — donc
   * un seul point d'accroche (son `onSuccess`) couvre les trois, plutôt que
   * de dupliquer l'appel portail à chaque appelant.
   *
   * UNIQUEMENT le sens dépublication (`action === "deactivate"`) : le sens
   * inverse existe déjà et reste le bouton "Publier" dédié plus bas
   * (`handlePublishClick` → `markAsPublished` + boucle portails), qui pose
   * aussi `last_published_at` (lu par `getPublicationStage` pour détecter
   * "modifs non poussées") — un appel generique ici ne le fait pas. Testé en
   * direct 2026-09-14 sur AG_A2.2 : déclencher les DEUX (déroulant sur
   * "Publié" + clic sur "Publier") envoyait deux requêtes quasi simultanées
   * à Immoweb, la seconde rejetée en 409 "REQUEST_CONFLICT". Décision
   * publish/deactivate dans `portalActionForStatutChange` (testé en
   * isolation) — seule la branche deactivate est utilisée ici.
   */
  const syncPortalOnStatutChange = (
    prevStatut: string | null | undefined,
    nextStatut: string | null | undefined,
  ) => {
    if (!canTriggerPortals) return;
    const action = portalActionForStatutChange(prevStatut, nextStatut);
    if (action !== "deactivate") return;
    for (const portal of externalEnabledPortals()) {
      publishPortal(
        { portal: portal.id, action },
        { onError: (e) => toast.error(`${portal.label} : ${e instanceof Error ? e.message : String(e)}`) },
      );
    }
  };

  // Avertit lors d'une fermeture d'onglet / refresh si le bien est
  // Publie + modifs non poussees. La nav interne React Router n'est PAS
  // bloquee (cf. doc du hook — necessite migration vers createBrowserRouter).
  useUnpublishedChangesGuard({ ...data, id: bien.id } as unknown as Bien);

  // Validation EN PRÉPARATION : cf. lib/bienPrepValidation.ts. Demande CEO 2026-05-04 :
  // marquer en rouge les 4 champs critiques manquants + en orange les champs nécessaires
  // au mailing manquants, dès que le bien est mis en EN PRÉPARATION.
  // Le statut est stocké dans le form sous forme kebab ("en_preparation"), normalisé par
  // le validator. On reconvertit en majuscules à la volée pour rester compatible avec la
  // logique existante de `disponibilite` (PostgreSQL stocke en MAJUSCULES).
  const dispoForValidation = (
    { en_preparation: "EN PRÉPARATION", disponible: "DISPONIBLE", loue: "LOUÉ", vendu: "VENDU" } as Record<string, string>
  )[data.disponibilite ?? ""] ?? data.disponibilite ?? "";
  const prepValidation = useMemo(
    () => validateBienPrep({ ...data, disponibilite: dispoForValidation }),
    [data, dispoForValidation],
  );

  // Pastilles des onglets : les bannières listent jusqu'à treize champs
  // manquants sans jamais dire OÙ les corriger — « Renon reçu le » et
  // « Expert PEB » sont dans « Cycle en cours », rien ne l'indiquait. On porte
  // donc le compte sur l'onglet concerné. Masquées quand la validation ne
  // s'applique pas (bien ni EN PRÉPARATION ni DISPONIBLE) ou quand l'appelant
  // désactive les bannières (/publi-temp).
  const tabAlerts = useMemo(() => {
    if (skipRequiredValidation || !prepValidation.isInPrep) return {};
    // Rouge dès qu'un obligatoire manque dans l'onglet, orange si seuls des
    // champs mailing manquent (décision Thibaud 2026-09-15).
    const required = countMissingByTab(prepValidation.requiredMissing);
    // Champs du cycle non bloquants (prestataire ELS, expert PEB) : même
    // niveau orange que le mailing.
    const mailing = countMissingByTab([...prepValidation.mailingMissing, ...prepValidation.cycleTodoMissing]);
    const out: Partial<Record<FicheTabId, FicheTabAlert>> = {};
    const ids = new Set<FicheTabId>([...Object.keys(required), ...Object.keys(mailing)] as FicheTabId[]);
    for (const id of ids) {
      const r = required[id] ?? 0;
      const m = mailing[id] ?? 0;
      out[id] = { count: r + m, level: r > 0 ? "required" : "mailing" };
    }
    return out;
  }, [skipRequiredValidation, prepValidation]);

  /**
   * F = enveloppe de champ. Masque si le rôle ne voit pas, VERROUILLE s'il ne
   * peut pas éditer, rend normalement sinon.
   *
   * ⚠️ « Verrouille » et non « remplace ». Avant le 2026-08-09, ce helper
   * substituait un `ReadOnlyRow` — une ligne de liste pleine largeur, libellé à
   * gauche et valeur à droite. Placée dans une cellule de grille de 3 colonnes
   * ou dans une case de `FieldGroup`, elle n'avait pas la place : les libellés
   * et les valeurs se chevauchaient (« 1140MMUNE », « 3S. DE BAIN »), et la
   * hauteur des lignes changeait d'un champ à l'autre. Un bloc où certains
   * champs sont éditables et d'autres non devenait illisible — cas constaté en
   * prod sur l'onglet Locataires en C-Level, et sur toute la fiche en
   * collaborateur.
   *
   * La géométrie d'un champ ne doit pas dépendre des droits. On rend donc LE
   * MÊME composant avec `readOnly` : même boîte, même padding, même place.
   * Aucun repère visuel supplémentaire (décision Thibaud) — seul le survol
   * distingue les deux, un champ éditable prenant une bordure pointillée.
   *
   * Si le champ apparaît dans `prepValidation.byField`, on injecte
   * `missingHint` pour activer le rendu rouge/orange du composant.
   */
  const F = (key: string, el: React.ReactNode) => {
    /**
     * Mode « réglage des droits » (page /v2/admin/droits-champs).
     *
     * On sort AVANT les tests de permission : l'admin doit voir et régler tous
     * les champs, y compris ceux que le rôle en cours de réglage ne voit pas —
     * sinon un champ « caché » deviendrait impossible à rouvrir.
     *
     * Le libellé est lu sur l'élément que la fiche allait rendre
     * (`props.label`), donc jamais recopié à la main : il ne peut pas diverger
     * de celui du champ. Repli sur la clé technique pour les rares éléments
     * sans libellé.
     */
    if (permsEditor) {
      const label = isValidElement(el)
        ? ((el.props as { label?: string }).label ?? key)
        : key;
      return (
        <PermStateCell
          key={key}
          fieldKey={key}
          label={label}
          value={permsEditor.get(key)}
          onChange={(s) => permsEditor.set(key, s)}
          register={permsEditor.register}
          unregister={permsEditor.unregister}
          allowEdit={permsEditor.allowEdit ?? true}
        />
      );
    }
    if (!isFieldVisible(key, canSee, presetActive ? presetVisible : null)) return null;
    if (!isValidElement(el)) return el;
    if (!canEdit(key)) {
      return cloneElement(el, { readOnly: true } as Partial<{ readOnly: boolean }>);
    }
    // Auto-inject missingHint sur les champs concernés par la validation prep.
    const missingLevel = prepValidation.byField.get(key);
    if (missingLevel) {
      return cloneElement(el, { missingHint: missingLevel } as Partial<{ missingHint: "required" | "mailing" }>);
    }
    return el;
  };

  /**
   * `PS` = cellule de réglage pour les champs qui NE passent pas par `F()`.
   *
   * Huit cas dans cette fiche : les deux répéteurs (chambres, terrasses), les
   * deux grilles de cases (équipements, charges comprises), les photos, la
   * mise sous option, le bloc miroir du PEB et les six horodatages système.
   * Ce ne sont pas des champs simples — ils portent leur propre mise en page —
   * donc `F()` ne les enveloppe pas. Sans `PS`, ils seraient absents de l'écran
   * de réglage et leurs droits deviendraient inaccessibles.
   *
   * Rend `null` hors mode droits : les appelants gardent leur rendu normal.
   */
  const PS = (key: string, label: string) => {
    if (!permsEditor) return null;
    return (
      <PermStateCell
        key={key}
        fieldKey={key}
        label={label}
        value={permsEditor.get(key)}
        onChange={(s) => permsEditor.set(key, s)}
        register={permsEditor.register}
        unregister={permsEditor.unregister}
        allowEdit={permsEditor.allowEdit ?? true}
      />
    );
  };

  // ── Grille de 12 colonnes de l'onglet « Le bien » ──────────────────────────
  // Chaque champ déclare sa PORTÉE au lieu de subir une largeur de colonne
  // uniforme : « Code postal » attend 4 caractères, « Adresse » une quarantaine,
  // ils ne doivent pas occuper la même place (cf. Baymard, GOV.UK). 12 parce
  // que c'est divisible par 1, 2, 3, 4 et 6.
  const GRID12 = "grid grid-cols-12 gap-x-4 gap-y-5";
  // Largeurs FIXES des cases qui jouent le même rôle dans les groupes Cave,
  // Parking et Garage : les poids étant proportionnels à la portée du groupe,
  // « disponible » mesurait 138 px dans Cave et 96 px dans Parking.
  const ANNEXE_DISPO_W = 132;
  const ANNEXE_NUM_W = 72;
  // « Fermé » / « Intérieur » : largeur FIXE, pas un poids.
  //
  // En poids (1.2), ces deux cases s'étiraient pour remplir la portée du groupe.
  // Le Parking occupait donc ses 425 px en entier, pendant que Cave et Garage
  // n'utilisaient que 210 de leurs 248 px. Résultat mesuré : 54 px de blanc
  // visible entre Cave et Parking, contre 16 entre Parking et Garage — alors
  // que les trois fieldsets sont bien espacés de 16 px.
  //
  // 82 px choisit exprès de laisser ~39 px de mou au Parking, soit celui de
  // Cave et Garage : les trois écarts visibles s'égalisent. « Non ▾ » tient
  // largement (texte 26 + chevron 16 + padding 24).
  const ANNEXE_FLAG_W = 82;
  // Classes écrites en toutes lettres : Tailwind scanne le source, une classe
  // construite dynamiquement (`col-span-${n}`) ne serait jamais générée.
  const SPAN_CLASS: Record<number, string> = {
    1: "col-span-4 md:col-span-1",
    2: "col-span-6 md:col-span-2",
    3: "col-span-6 md:col-span-3",
    4: "col-span-12 md:col-span-4",
    5: "col-span-12 md:col-span-5",
    6: "col-span-12 md:col-span-6",
    7: "col-span-12 md:col-span-7",
    8: "col-span-12 md:col-span-8",
    9: "col-span-12 md:col-span-9",
    10: "col-span-12 md:col-span-10",
    11: "col-span-12 md:col-span-11",
    12: "col-span-12",
  };
  /** `F()` + portée sur la grille. Rend `null` si le rôle ne voit pas le champ,
   *  pour ne pas laisser une cellule de grille vide derrière lui. */
  const FS = (key: string, span: number, el: React.ReactNode) => {
    const node = F(key, el);
    if (!node) return null;
    return (
      <div key={key} className={SPAN_CLASS[span] ?? "col-span-12"}>
        {node}
      </div>
    );
  };

  // Onglet courant de la fiche (chantier « fiche en onglets », spec 2026-08-07).
  // Les 5 onglets métier sont TOUJOURS présents ; "permissions" n'apparaît que
  // pour l'admin et n'est pas un onglet de la fiche mais la matrice des droits.
  const [activeTab, setActiveTab] = useState<FicheTabId>(initialTab ?? "bien");
  const allIds: FicheTabId[] =
    role === "admin" && !hidePermissionsTab
      ? [...FICHE_TAB_IDS, "permissions"]
      : [...FICHE_TAB_IDS];
  const visibleTabIds = useMemo(
    () =>
      permsMode
        ? allIds // en mode réglage des droits, l'admin voit et règle tous les onglets
        : visibleFicheTabs(allIds, canSee, presetActive ? presetVisible : null, ["permissions"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allIds.join(","), permsMode, canSee, presetActive, presetVisible],
  );
  const ficheTabs = useMemo(
    () => visibleTabIds.map((id) => ({ id, ...FICHE_TAB_META[id] })),
    [visibleTabIds],
  );

  // Si l'onglet actif vient de disparaître (changement de rôle simulé, ou
  // préréglage qui s'active), retomber sur le premier onglet encore visible.
  useEffect(() => {
    const fallback = fallbackActiveTab(visibleTabIds, activeTab);
    if (fallback && fallback !== activeTab) setActiveTab(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabIds]);
  const [histoOpen, setHistoOpen] = useState(false);
  const isAdminOrCLevel = role === "admin" || role === "c_level";

  // ── Locataires (table dédiée) ──────────────────────────────────────────────
  // Les 6 champs ancien/nouveau Locataire* dans `data` sont synchronisés avec
  // la table `locataires` via 2 effets : sync DB→form (une fois au mount) et
  // debounced save form→DB (800ms).
  const { data: locataires = [], isSuccess: locatairesLoaded } = useLocataires(bien.id);
  const currentLocataire = useMemo(() => locataires.find((l) => l.type === "actuel") ?? null, [locataires]);
  const lastAncienLocataire = useMemo(() => locataires.find((l) => l.type === "ancien") ?? null, [locataires]);
  const allAnciensLocataires = useMemo(() => locataires.filter((l) => l.type === "ancien"), [locataires]);
  const { mutate: createLocataireMut } = useCreateLocataire(bien.id);
  const { mutate: updateLocataireMut } = useUpdateLocataire(bien.id);
  const { mutate: transferLocataireMut } = useTransferLocataire(bien.id);

  // Sync DB → form. On re-synchronise :
  //  - à chaque arrivée des locataires (fresh mount / refetch),
  //  - ET à chaque changement de bienVersion (useSessionState réinitialise
  //    alors `data` à initialValue avec nouveau*="" — sans cette re-sync
  //    les champs locataires restent vides et le save effect écrase la DB).
  // La garde `syncedForRef` évite les re-syncs redondants.
  const syncedForRef = useRef<string>("");
  useEffect(() => {
    if (!locatairesLoaded) return;
    const token = `${bienVersion}::${locataires.length}::${currentLocataire?.id ?? ""}`;
    if (syncedForRef.current === token) return;
    syncedForRef.current = token;
    setData((prev) => ({
      ...prev,
      nouveauLocataireNom: currentLocataire?.nom ?? "",
      nouveauLocataireEmail: currentLocataire?.email ?? "",
      nouveauLocataireTel: currentLocataire?.telephone ?? "",
      nouveauLocataireDateDebut: currentLocataire?.dateDebut ?? "",
      nouveauLocataireDateFin: currentLocataire?.dateFin ?? "",
      nouveauLocataireLoyerInitial: currentLocataire?.loyerInitial ?? "",
      nouveauLocataireLoyerActuel: currentLocataire?.loyerActuel ?? "",
      nouveauLocataireChargesInitiales: currentLocataire?.chargesInitiales ?? "",
      nouveauLocataireChargesActuelles: currentLocataire?.chargesActuelles ?? "",
      nouveauLocataireIndexBaseYear: currentLocataire?.indexBaseYear ?? "",
      ancienLocataireNom: lastAncienLocataire?.nom ?? "",
      ancienLocataireEmail: lastAncienLocataire?.email ?? "",
      ancienLocataireTel: lastAncienLocataire?.telephone ?? "",
      ancienLocataireDateDebut: lastAncienLocataire?.dateDebut ?? "",
      ancienLocataireDateFin: lastAncienLocataire?.dateFin ?? "",
      ancienLocataireLoyerInitial: lastAncienLocataire?.loyerInitial ?? "",
      ancienLocataireLoyerActuel: lastAncienLocataire?.loyerActuel ?? "",
      ancienLocataireChargesInitiales: lastAncienLocataire?.chargesInitiales ?? "",
      ancienLocataireChargesActuelles: lastAncienLocataire?.chargesActuelles ?? "",
      ancienLocataireIndexBaseYear: lastAncienLocataire?.indexBaseYear ?? "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locataires, locatairesLoaded, bienVersion]);

  // Debounced save form → DB pour le locataire actuel (section "Nouveau").
  // La section "Ancien" est read-only depuis DB (le dernier type='ancien').
  useEffect(() => {
    const hasContent = data.nouveauLocataireNom || data.nouveauLocataireEmail || data.nouveauLocataireTel;
    // Skip tant que la sync DB→form n'a pas eu lieu pour la version courante.
    // Sans cette garde, l'effet tire au mount (data=initialValue="") et écrase
    // le locataire en DB.
    if (syncedForRef.current === "") return;
    // Skip si rien à sauver ET aucun locataire courant
    if (!hasContent && !currentLocataire) return;

    const t = setTimeout(() => {
      const payload = {
        nom: data.nouveauLocataireNom || "",
        email: data.nouveauLocataireEmail || "",
        telephone: data.nouveauLocataireTel || "",
        dateDebut: data.nouveauLocataireDateDebut || "",
        dateFin: data.nouveauLocataireDateFin || "",
        loyerInitial: data.nouveauLocataireLoyerInitial || "",
        loyerActuel: data.nouveauLocataireLoyerActuel || "",
        chargesInitiales: data.nouveauLocataireChargesInitiales || "",
        chargesActuelles: data.nouveauLocataireChargesActuelles || "",
        indexBaseYear: data.nouveauLocataireIndexBaseYear || "",
      };
      if (currentLocataire) {
        // GARDE-FOU CRITIQUE : on refuse de vider un locataire existant via
        // autosave. Si la DB a un nom et que le form est vide, c'est qu'on a
        // un race condition (sync pas encore arrivée). L'utilisateur doit
        // passer par le bouton "Transférer" pour bouger un locataire en
        // "ancien" — jamais via un autosave avec nom vide.
        const dbHasTenant = !!(currentLocataire.nom || currentLocataire.email || currentLocataire.telephone);
        const formHasTenant = !!(payload.nom || payload.email || payload.telephone);
        if (dbHasTenant && !formHasTenant) return;

        // Update existant — skip si identique pour éviter bruit DB
        const sameAsDB =
          currentLocataire.nom === payload.nom &&
          currentLocataire.email === payload.email &&
          currentLocataire.telephone === payload.telephone &&
          (currentLocataire.dateDebut ?? "") === payload.dateDebut &&
          (currentLocataire.dateFin ?? "") === payload.dateFin &&
          (currentLocataire.loyerInitial ?? "") === payload.loyerInitial &&
          (currentLocataire.loyerActuel ?? "") === payload.loyerActuel &&
          (currentLocataire.chargesInitiales ?? "") === payload.chargesInitiales &&
          (currentLocataire.chargesActuelles ?? "") === payload.chargesActuelles &&
          (currentLocataire.indexBaseYear ?? "") === payload.indexBaseYear;
        if (!sameAsDB) updateLocataireMut({ id: currentLocataire.id, patch: payload });
      } else if (payload.nom || payload.email || payload.telephone) {
        // Create dès qu'au moins un champ identitaire est rempli.
        createLocataireMut({
          bien_id: bien.id,
          type: "actuel",
          ...payload,
        });
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.nouveauLocataireNom, data.nouveauLocataireEmail, data.nouveauLocataireTel,
    data.nouveauLocataireDateDebut, data.nouveauLocataireDateFin,
    data.nouveauLocataireLoyerInitial, data.nouveauLocataireLoyerActuel,
    data.nouveauLocataireChargesInitiales, data.nouveauLocataireChargesActuelles,
    data.nouveauLocataireIndexBaseYear,
    currentLocataire,
    locatairesLoaded,
  ]);

  // Bascule le locataire actuel ("nouveau" côté form) vers "ancien". Utilisé par
  // le bouton manuel (Transférer) ET par l'archivage automatique au passage
  // LOUÉ → DISPONIBLE. `archivedDateFin` grave la date de sortie sur le locataire
  // archivé (= finDeContrat pour l'auto, sinon la dateFin saisie / today).
  // Renvoie true si une bascule a eu lieu (pour savoir s'il faut prévenir l'user).
  const transferLocataireToAncien = (opts?: { archivedDateFin?: string }): boolean => {
    const nom = data.nouveauLocataireNom || "";
    const email = data.nouveauLocataireEmail || "";
    const telephone = data.nouveauLocataireTel || "";
    const dateDebut = data.nouveauLocataireDateDebut || "";
    const dateFin = data.nouveauLocataireDateFin || "";
    const loyerInitial = data.nouveauLocataireLoyerInitial || "";
    const loyerActuel = data.nouveauLocataireLoyerActuel || "";
    const chargesInitiales = data.nouveauLocataireChargesInitiales || "";
    const chargesActuelles = data.nouveauLocataireChargesActuelles || "";
    const indexBaseYear = data.nouveauLocataireIndexBaseYear || "";
    if (!nom && !email && !telephone && !currentLocataire) return false;

    // Date de sortie effective gravée sur l'archivé : override explicite
    // (finDeContrat) sinon la dateFin saisie, sinon today (géré côté repo).
    const archivedDateFin = opts?.archivedDateFin || dateFin || "";

    // 1. DB : actuel → ancien (si existant) + création d'un "actuel" vide,
    //    dans UNE transaction (RPC transfer_locataire, migration 127).
    //    Les 7 champs contrat du locataire actuel restent sur la row DB
    //    (ils voyagent avec lui quand il bascule en type='ancien').
    transferLocataireMut({
      currentId: currentLocataire?.id ?? null,
      archivedDateFin: archivedDateFin || undefined,
    });

    // 2. Form : copie nouveau→ancien et vide nouveau (l'UI reflète l'état
    //    attendu instantanément, sans attendre le refetch React Query).
    setData((prev) => ({
      ...prev,
      ancienLocataireNom: nom,
      ancienLocataireEmail: email,
      ancienLocataireTel: telephone,
      ancienLocataireDateDebut: dateDebut,
      ancienLocataireDateFin: archivedDateFin || dateFin,
      ancienLocataireLoyerInitial: loyerInitial,
      ancienLocataireLoyerActuel: loyerActuel,
      ancienLocataireChargesInitiales: chargesInitiales,
      ancienLocataireChargesActuelles: chargesActuelles,
      ancienLocataireIndexBaseYear: indexBaseYear,
      nouveauLocataireNom: "",
      nouveauLocataireEmail: "",
      nouveauLocataireTel: "",
      nouveauLocataireDateDebut: "",
      nouveauLocataireDateFin: "",
      nouveauLocataireLoyerInitial: "",
      nouveauLocataireLoyerActuel: "",
      nouveauLocataireChargesInitiales: "",
      nouveauLocataireChargesActuelles: "",
      nouveauLocataireIndexBaseYear: "",
    }));
    return true;
  };

  const handleTransferLocataire = () => {
    transferLocataireToAncien();
  };

  // hasEdited et saveFeedback sont définis plus haut (avec `set`/`toggle`).
  // L'ancien useEffect([data]) qui settait hasEdited automatiquement a été
  // supprimé : il détectait aussi les transitions programmatiques (stub →
  // real depuis React Query, sync locataires) et déclenchait un autosave
  // qui écrasait la DB avec des valeurs vides. Maintenant seuls les
  // `set`/`toggle`/handleTransferLocataire flaggent hasEdited=true.

  // Mutations Supabase — UPDATE si le bien existe déjà, INSERT si nouveau.
  // foundBien est undefined tant que React Query n'a pas eu de réponse.
  // Une fois la réponse reçue : null si inexistant, objet sinon.
  // Passer permRole pour que le RPC update_entity_with_perms utilise le
  // même périmètre de can_edit que useFieldPermissions affiche dans l'UI.
  // Sinon : édition autorisée côté front mais rejet côté back ("Aucun champ
  // autorisé à la modification pour le rôle X") + UI qui revert.
  const { mutate: saveBienMutate, isPending: isSavingUpdate } = useUpdateBien(bien.id, permRole);
  /**
   * ⚠️ GARDE-FOU DU MODE DROITS — ne pas contourner.
   *
   * Cette fiche porte un autosave débouncé à 1,5 s, plus une poignée
   * d'écritures directes (clôture, statuts ELS, sous option…). Montée en mode
   * « réglage des droits » sur un bien réel, elle pourrait donc MODIFIER CE
   * BIEN pendant qu'un admin règle des permissions — silencieusement.
   *
   * On neutralise donc à la SOURCE plutôt qu'en essayant de désamorcer chaque
   * appelant : même si un chemin d'écriture m'échappe aujourd'hui, ou si
   * quelqu'un en ajoute un demain, il ne partira pas.
   */
  const saveBien: typeof saveBienMutate = (...args) => {
    if (permsMode) return;
    saveBienMutate(...args);
  };
  // Lancement manuel des missions (miroir du popover Pipeline ELS dans la fiche).
  const launchMut = useLaunchMission();
  /**
   * État des boutons "Lancer la mission" ELS/PEB, calculé UNE fois pour les
   * deux — chacun est ensuite rendu dans son propre cadre (ELS dans « État des
   * lieux de sortie », PEB dans « PEB — Renouvellement », demande Thibaud
   * 2026-08-19 : le bouton PEB était égaré dans le cadre ELS, sans lien visuel
   * avec le formulaire qu'il concerne). Basé sur `bien` (état SAUVÉ — l'edge
   * function lit la DB), pas sur `data`.
   */
  // Pas de useMemo : `bien` retombe sur un littéral neuf à chaque render
  // quand `foundBien` est absent (cf. ligne 256), ce qui invaliderait le
  // memo à chaque fois de toute façon — calcul recalculé à chaque render,
  // comme l'ancienne IIFE qu'il remplace.
  const missionLaunch = (() => {
    // En mode droits, on ne rend PAS ces boutons : ce ne sont pas des champs
    // (rien à régler dessus), et un clic partirait vers l'edge function
    // `launch-mission` — donc un vrai e-mail à un vrai prestataire, depuis un
    // écran d'administration.
    if (permsMode) return null;
    const canLaunch = role === "admin" || role === "c_level" || (role ?? "").startsWith("collaborateur");
    if (!canLaunch) return null;
    const b = bien as unknown as BienRow;
    const s = checkElsStates(b);
    const isRealPresta = (id?: string | null) => !!id && id !== "amiable" && id !== "autre";
    const elsLaunched = !!b.elsMissionLanceeAt;
    const pebLaunched = !!b.pebMissionLanceeAt;
    // Un process n'est « à faire » que s'il est demandé dans ce cycle et pas
    // encore terminé. Côté PEB, c'est `statutPeb` qui dit « fait » — plus la
    // validité du certificat en vigueur (`checkElsStates().peb`), qui cachait
    // le bouton « Lancer la mission PEB » d'un PEB demandé explicitement sur
    // un certificat encore valide (HK_H4.4, 2026-09-15).
    const bd = b as unknown as Record<string, unknown>;
    const elsTodo = isElsLance(bd) && s.els !== "success";
    const pebTodo = isPebLance(bd) && !isPebDone(bd);
    return {
      showLaunchEls: elsTodo && !b.dossierClos && !b.prestataireEleRefuseAt && isRealPresta(b.prestataireEle) && !elsLaunched,
      showLaunchPeb: pebTodo && !b.dossierClos && !b.expertPebRefuseAt && isRealPresta(b.expertPeb) && !pebLaunched,
      showElsTag: elsLaunched && elsTodo && !b.prestataireEleRefuseAt,
      showPebTag: pebLaunched && pebTodo && !b.expertPebRefuseAt,
      elsLaunchedAt: b.elsMissionLanceeAt,
      pebLaunchedAt: b.pebMissionLanceeAt,
    };
  })();
  const doLaunchMission = (mission: "els" | "peb") => {
    const b = bien as unknown as BienRow;
    launchMut.mutate(
      { bienId: b.id, mission, role: role ?? "collaborateur" },
      {
        onSuccess: (r) => toast.success(
          r.emailSent === false && !r.alreadyLaunched
            ? t("v2.pipeline.actions.launchedNoEmail")
            : t("v2.pipeline.actions.launchedOk"),
        ),
        onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
      },
    );
  };
  const fmtMissionAt = (at?: string | null) => {
    const d = at ? new Date(at) : null;
    return d && !isNaN(d.getTime())
      ? ` — ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
      : "";
  };
  const missionLaunchBtn = (mission: "els" | "peb", title: string) => (
    <button
      type="button"
      onClick={() => doLaunchMission(mission)}
      disabled={launchMut.isPending}
      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-accent text-white hover:bg-accent-hover cursor-pointer disabled:opacity-50 disabled:cursor-wait outline-none shadow-none transition-colors"
    >
      <Rocket size={14} /> {title}
    </button>
  );
  // create_bien et delete_bien sont admin-only cote RPC. Si l utilisateur reel
  // est admin mais simule un autre role (CLEVEL, etc.), on force "admin" pour
  // que la creation/suppression passe — sinon "Erreur d enregistrement" silent.
  const createRole = profile?.role === "admin" ? "admin" : undefined;
  const { mutate: createBien, isPending: isSavingCreate } = useCreateBien(createRole);
  const { mutate: deleteBien, isPending: isDeleting } = useDeleteBien(createRole);
  const isSaving = isSavingUpdate || isSavingCreate;
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = () => {
    // Troisième verrou : la suppression est déjà retirée de l'en-tête en mode
    // droits, mais on la bloque aussi ici — même principe qu'ailleurs, on
    // neutralise l'action, pas seulement son bouton.
    if (permsMode) return;
    setDeleteError(null);
    deleteBien(bien.id, {
      onSuccess: () => {
        setDeleteOpen(false);
        navigate("/v2/biens");
      },
      onError: (err) => {
        console.error("[deleteBien]", err);
        setDeleteError(t("biens.deleteError"));
      },
    });
  };

  const DISPO_FORM_TO_DB: Record<string, string> = {
    en_preparation: "EN PRÉPARATION",
    disponible: "DISPONIBLE",
    loue: "LOUÉ",
    vendu: "VENDU",
  };

  const handleSave = () => {
    // Deuxième verrou du mode droits, en plus de celui posé sur `saveBien` :
    // on n'entre même pas dans la construction du patch. Ceinture et bretelles
    // assumées — une écriture parasite ici modifierait un bien réel pendant
    // qu'un admin règle des permissions.
    if (permsMode) return;
    // Zod strip les clés inconnues (surfacesChambres, quartier en string, etc.)
    // et normalise disponibilite (form → DB UPPERCASE).
    // `quartier` (form alias) → `quartier_id` (colonne DB) : sans ce mapping
    // explicite, Zod strippe la valeur et la sélection n'arrive jamais en DB.
    const patchInput: Record<string, unknown> = {
      ...data,
      disponibilite: DISPO_FORM_TO_DB[data.disponibilite] ?? data.disponibilite,
      quartier_id: data.quartier ?? null,
    };
    // transaction est un enum désormais nullable (migration 013) : si
    // l'utilisateur n'a pas choisi, on envoie NULL pour que la colonne reste
    // vide en DB (avant, le DEFAULT 'location' forçait une valeur que
    // l'utilisateur n'avait pas demandée).
    if (!patchInput.transaction) patchInput.transaction = null;
    const patch = BienUpdateSchema.parse(patchInput);
    // Capturé avant l'envoi : compare à `dataRef.current` (toujours à jour)
    // au moment du succès, pour ne pas blanchir `hasEdited` si une édition
    // plus récente est arrivée pendant que cette sauvegarde était en vol.
    const attempted = data;

    const onSuccess = () => {
      if (attempted === dataRef.current) setHasEdited(false);
      setSaveFeedback("saved");
      setTimeout(() => setSaveFeedback("idle"), 2500);
      syncPortalOnStatutChange(bien.statutPublication, attempted.statutPublication as string | undefined);
    };
    const onError = () => {
      setSaveFeedback("error");
      // Bloque la relance automatique du debounce sur un patch déjà refusé.
      lastRejectedData.current = data;
    };

    if (foundBien) {
      // Bien existant → UPDATE
      saveBien(patch, { onSuccess, onError });
    } else {
      // Nouveau bien → INSERT (admin direct, RLS autorise)
      createBien({ id: bien.id, ...patch }, { onSuccess, onError });
    }
  };

  // Auto-save sur perte de focus : React onBlur bubble depuis les inputs
  // descendants. À chaque fois qu'un champ perd le focus (Tab, clic ailleurs,
  // changement de section), on déclenche un save si hasEdited=true et qu'une
  // mutation n'est pas déjà en cours. Évite les saves partiels pendant la
  // frappe d'un même champ (pas de debounce, juste sur blur).
  const handleFormBlur = () => {
    if (hasEdited && !isSaving) handleSave();
  };

  // Auto-save debouncé sur changement de data — couvre les dropdowns et autres
  // widgets custom qui ne bubblent pas un blur propre vers le wrapper (le
  // SelectField gère son focus en interne). 800ms laisse le temps à l'utilisateur
  // de taper dans un input sans déclencher un save par frappe.
  useEffect(() => {
    if (!hasEdited || isSaving) return;
    // Anti-boucle : en cas d'échec, `hasEdited` reste vrai et le même patch
    // serait rejoué toutes les 1,5 s — donc un toast toutes les 1,5 s depuis
    // que useUpdateBien porte meta.toastOnError. On mémorise l'état refusé :
    // tant que l'utilisateur ne modifie rien, on ne réessaie pas.
    if (lastRejectedData.current === data) return;
    const t = setTimeout(() => handleSave(), 1500);
    return () => clearTimeout(t);
    // handleSave capture `data` via closure — l'effet rerunne quand data
    // change (hasEdited rebascule à true), donc chaque timeout récupère la
    // version la plus fraiche de data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hasEdited, isSaving]);

  // Auto-transition statutEle "" → "date_fixee" → "date_passee" selon la date.
  // Décision user 2026-05-20 :
  // - Si une date est saisie ET statutEle vide → passe à "date_fixee"
  // - Si date < today ET statutEle="date_fixee" → passe à "date_passee"
  // - Ne touche pas "expertise_ok" (état final, prestataire a confirmé)
  // L'effet s'auto-stoppe car bien.statutEle change après la mutation.
  useEffect(() => {
    if (!bien.id) return;
    if (bien.statutEle === "expertise_ok") return; // état final, on laisse
    if (!bien.edlDatePrevue) return;
    const elsDate = new Date(bien.edlDatePrevue);
    if (isNaN(elsDate.getTime())) return;

    // Cas 1 : date saisie mais statutEle vide → date_fixee
    if (!bien.statutEle || bien.statutEle === "") {
      saveBien({ statutEle: "date_fixee" }, {
        onSuccess: () => setData((prev) => ({ ...prev, statutEle: "date_fixee" })),
      });
      return;
    }
    // Cas 2 : statutEle="date_fixee" mais date passée → date_passee
    if (bien.statutEle === "date_fixee" && elsDate < new Date()) {
      saveBien({ statutEle: "date_passee" }, {
        onSuccess: () => setData((prev) => ({ ...prev, statutEle: "date_passee" })),
      });
      return;
    }
    // saveBien est stable (mutation), pas besoin dans les deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bien.id, bien.statutEle, bien.edlDatePrevue]);

  // Navigation prev/next dans la liste d'origine. La clé dépend du chemin :
  // /biens/X, /apparts-for-rent/X, /planning-photos/X, /suivi-publication/X
  // tapent tous dans BienDetailPage mais ont chacun leur propre liste persistée.
  const navListKey =
    pathname.startsWith("/apparts-for-rent") ? "apparts-for-rent" :
    pathname.startsWith("/planning-photos") ? "planning-photos" :
    pathname.startsWith("/suivi-publication") ? "suivi-publication" :
    "biens";
  const navBase =
    navListKey === "apparts-for-rent" ? "/apparts-for-rent" :
    navListKey === "planning-photos" ? "/planning-photos" :
    navListKey === "suivi-publication" ? "/suivi-publication" :
    "/biens";
  const { prevId, nextId, position, total } = useListNavigation(navListKey, id);

  useBreadcrumbDetail({
    label: data.referenceInterne || t("biens.newBien"),
    badge: data.transaction ? <Badge variant={data.transaction as "location" | "vente"}>{t(`biens.transactionType.${data.transaction}`)}</Badge> : undefined,
    navigation: total > 0 ? {
      prevPath: prevId ? `${navBase}/${prevId}` : null,
      nextPath: nextId ? `${navBase}/${nextId}` : null,
      position,
      total,
    } : undefined,
    actions: (
      <div className="flex items-center gap-2">
        {isSaving && (
          <div className="flex items-center gap-1.5 text-xs text-text-muted">
            <span>{t("common.saving")}</span>
          </div>
        )}
        {!isSaving && saveFeedback === "saved" && (
          <div className="flex items-center gap-1.5 text-xs text-success">
            <CheckCircle size={14} />
            <span>{t("common.saved")}</span>
          </div>
        )}
        {!isSaving && saveFeedback === "error" && (
          <div className="flex items-center gap-1.5 text-xs text-danger">
            <AlertTriangle size={14} />
            <span>{t("common.saveError")}</span>
          </div>
        )}
        {!isSaving && saveFeedback === "idle" && hasEdited && (
          <div className="flex items-center gap-1.5 text-xs text-text-muted">
            <span>{t("common.unsavedChanges")}</span>
          </div>
        )}
        {/* Pas de suppression depuis l'écran de réglage des droits : le bien
            monté n'y est qu'un support d'affichage. */}
        {role === "admin" && foundBien && !permsMode && (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-danger hover:bg-danger/10 rounded-md cursor-pointer outline-none shadow-none transition-colors"
            title={t("biens.deleteTitle")}
          >
            <Trash2 size={14} />
            <span>{t("common.delete")}</span>
          </button>
        )}
      </div>
    ),
  }, [hasEdited, isSaving, saveFeedback, prevId, nextId, position, total, role, foundBien]);

  // ── État du PEB ────────────────────────────────────────────────────────────
  // Hissé hors du JSX depuis le chantier « fiche en onglets » : le certificat en
  // vigueur vit dans l'onglet « Le bien » (il survit au départ du locataire)
  // tandis que la branche de RENOUVELLEMENT vit dans « Cycle en cours ». Les deux
  // onglets lisent donc les mêmes dérivés.
  const pebCloture = data.statutPeb === "expertise_ok";
  // Forcée ouverte en mode droits (cf. `permsEditor`) : sans ça, les onze
  // champs de la branche PEB ne seraient réglables que sur un bien dont le
  // certificat est justement périmé.
  const renewalNeeded = permsMode || (isPebExpiringSoon(data.pebFinLe, data.classePeb) && !pebCloture);
  const pebStatut = getPebStatut(data as unknown as Bien);
  const fmtPebDate = (s?: string | null) => {
    if (!s) return "";
    const [y, m, d] = s.split("-");
    return d && m && y ? `${d}/${m}/${y}` : s;
  };

  /** Horodatage système → JJ/MM/AAAA. Chaîne vide si absent ou illisible. */
  const fmtStamp = (at?: string | null) => {
    if (!at) return "";
    const d = new Date(at);
    if (isNaN(d.getTime())) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };
  /** Identifiant de profil → nom lisible ; l'id brut si le profil a disparu. */
  const resolveProfileName = (id?: string | null) => {
    if (!id) return "";
    const p = profiles.find((x) => x.id === id);
    return p ? (`${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email) : id;
  };

  /**
   * Un cycle est OUVERT quand le geste « Ouvrir un cycle » l'a ouvert :
   * `cycleOuvertLe` posé (migration 135, Phase 2). Ni le renon ni un marqueur
   * isolé (expert désigné…) ne l'ouvrent plus à eux seuls — cas LS_3, 2026-09-15.
   */
  // Idem : en mode droits le cycle est réputé ouvert, sinon l'onglet se
  // réduirait à son état vide et les quatre blocs du cycle seraient hors
  // d'atteinte. Même prédicat que le pipeline (`isCycleOuvert`, plan « deux
  // cycles » 2026-09-15) : un seul fait, indépendant de l'origine du bien —
  // `entreeGestion` n'est plus lu nulle part, le bouton « Ouvrir un cycle »
  // est disponible sur tout bien.
  const cycleOuvert = permsMode || isCycleOuvert(data as unknown as Record<string, unknown>);
  /**
   * Carte "Renouvellement PEB" (expert, RDV, nouveau certificat) : visible
   * seulement si un cycle est ouvert ET que le PEB a été explicitement
   * demandé à son ouverture (`pebRequis`) — plus automatiquement dès que le
   * certificat semble expiré. Décision Thibaud 2026-09-14 : "on ne lance un
   * PEB que quand on lance un cycle ELS", pour ne pas forcer la désignation
   * d'un expert sur un bien fraîchement importé dont la date PEB n'a
   * simplement jamais été bien captée (le certificat en vigueur reste de
   * toute façon éditable directement, cf. plus haut). `permsMode` : jamais
   * masquée pour l'admin qui règle les droits des champs qu'elle contient.
   */
  const pebCycleVisible = permsMode || (cycleOuvert && data.pebRequis !== false);

  /**
   * Le geste est accessible sur un bien DISPONIBLE ou LOUÉ (décision Q4, plan
   * « deux cycles » 2026-09-15) : depuis LOUÉ, valider le dialogue enchaîne la
   * transition LOUÉ → DISPONIBLE existante (archivage du locataire, snapshot
   * SRS côté trigger 085, reset de la fiche de sortie) PUIS pose le cycle —
   * un seul geste au lieu de deux, c'est le flux en deux temps qui faisait
   * naître les faux renons.
   */
  const cycleFromLoue = data.disponibilite === "loue";
  const canOpenCycleHere = data.disponibilite === "disponible" || cycleFromLoue;

  /** Ouvre le dialogue en repartant de ce que la fiche porte déjà. */
  const openCycleDialog = () => {
    // Depuis LOUÉ, la fiche de sortie sera remise à neuf à la validation : on
    // seed le brouillon avec les valeurs POST-reset (dates vides, assigné
    // reporté en « précédent »), pas avec celles du cycle précédent — même
    // logique que `confirmDispoLibere`, même bug évité ("elles sortent d'où ?").
    const resetPatch = cycleFromLoue
      ? resetElsCycleForLoue({ prestataireEle: (data as { prestataireEle?: string }).prestataireEle ?? "" })
      : null;
    setCycleDraft({
      renonRecuLe: resetPatch ? "" : (data.renonRecuLe ?? ""),
      finDeContrat: resetPatch ? "" : (data.finDeContrat ?? ""),
      seraDispoPour: resetPatch ? "" : (data.seraDispoPour ?? ""),
      // Le défaut est « avec suivi ELS » : c'est le comportement historique,
      // et la colonne est NOT NULL DEFAULT true (migration 106).
      elsRequis: data.elsRequis !== false,
      // PEB : défaut calculé sur la validité RÉELLE du certificat en cours
      // (signalé Thibaud 2026-09-15), pas un simple "oui" historique comme
      // `elsRequis` — même seuil que la pastille catalogue (6 mois,
      // `isPebExpiringSoon`). Encore bon pour 6 mois → décoché, rien à
      // refaire ; sinon coché. Reste un pré-remplissage : la case est
      // modifiable avant validation, cf. `pebRequisHint`.
      pebRequis: isPebExpiringSoon(data.pebFinLe, data.classePeb),
      collaborateurSrs: data.collaborateurSrs ?? "",
      // "Prestataire précédent" (prestataireEls) = trace pure, pour info
      // seulement (demande Thibaud 2026-08-19) : le popup laisse le c_level la
      // consulter/corriger, mais n'assigne plus rien à sa place. L'assignation
      // réelle (prestataireEle) reste celle déjà en place (cycle rouvert via
      // le crayon) et se fait plus tard par le collaborateur, via Pipeline ELS
      // — plus de pré-remplissage automatique qui se faisait passer pour une
      // décision déjà prise.
      prestataireEls: resetPatch ? (resetPatch.prestataireEls ?? "") : (data.prestataireEls ?? ""),
      prestataireEle: resetPatch ? "" : (data.prestataireEle ?? ""),
      expertPeb: data.expertPeb ?? "",
    });
    setOuvrirCycleOpen(true);
  };

  /**
   * Un seul patch, donc un seul autosave. `expertPeb` n'est écrit que si la
   * branche PEB est ouverte — sinon on écraserait un expert déjà désigné avec
   * la chaîne vide d'un champ que le dialogue n'affichait même pas.
   */
  const confirmOuvrirCycle = () => {
    // Depuis LOUÉ : la transition LOUÉ → DISPONIBLE d'abord (mêmes effets que
    // `confirmDispoLibere` : archivage du locataire actuel, reset de la fiche
    // de sortie — le trigger 085 fait le snapshot SRS côté serveur), le cycle
    // ensuite, dans le MÊME patch → un seul autosave, pas d'état intermédiaire
    // « disponible sans cycle » si l'utilisateur ferme entre les deux.
    let transitionPatch: Record<string, unknown> = {};
    if (cycleFromLoue) {
      const archived = transferLocataireToAncien({ archivedDateFin: data.finDeContrat || todayIso() });
      if (archived) toast.success(t("biens.locataireArchiveAuto"));
      transitionPatch = {
        ...resetElsCycleForLoue({ prestataireEle: (data as { prestataireEle?: string }).prestataireEle ?? "" }),
        disponibilite: "disponible",
      };
    }
    setData((prev) => ({
      ...prev,
      ...transitionPatch,
      // LE fait qui ouvre le cycle (migration 135) : ni le renon ni un
      // marqueur isolé ne le font plus à eux seuls.
      cycleOuvertLe: new Date().toISOString(),
      renonRecuLe: cycleDraft.renonRecuLe,
      finDeContrat: cycleDraft.finDeContrat,
      seraDispoPour: cycleDraft.seraDispoPour,
      elsRequis: cycleDraft.elsRequis,
      // Un responsable interne dès qu'un process (ELS ou PEB) est demandé
      // (décision 2026-09-15) ; sans aucun process, rien à désigner.
      collaborateurSrs: cycleDraft.elsRequis || cycleDraft.pebRequis ? cycleDraft.collaborateurSrs : "",
      // Trace indépendante de elsRequis — reste une info utile même sans suivi ELS.
      prestataireEls: cycleDraft.prestataireEls,
      prestataireEle: cycleDraft.elsRequis ? cycleDraft.prestataireEle : "",
      pebRequis: cycleDraft.pebRequis,
      // Expert demandé seulement si la branche PEB a été explicitement
      // cochée dans le dialogue (pas juste affichée en motif).
      ...(cycleDraft.pebRequis ? { expertPeb: cycleDraft.expertPeb } : {}),
    }) as typeof prev);
    setHasEdited(true);
    setSaveFeedback("idle");
    setOuvrirCycleOpen(false);
  };

  /**
   * « Transfert électricité » — la date et le drapeau doivent bouger ensemble.
   *
   * Mesuré le 2026-08-10 : 9 biens portent `transfertElecDate`, mais seuls 2
   * ont `transfertElecFait` à true. Or l'alerte « transfert en retard »
   * (`useElsAlertCounts`, `tasks.ts`) ne regarde QUE le drapeau : saisir la
   * date sur la fiche ne l'éteignait pas. Les sept autres biens restaient
   * signalés en retard alors que le transfert était renseigné.
   */
  const setTransfertElecDate = (value: string) => {
    setData((prev) => ({ ...prev, transfertElecDate: value, transfertElecFait: !!value }));
    setHasEdited(true);
    setSaveFeedback("idle");
  };

  return (
    <div onBlur={handleFormBlur}>
      {/* ══ ALERTES — AU-DESSUS DE LA BARRE D'ONGLETS ══
          Demande Thibaud (2026-08-08) : les bannières de complétude et le
          refus prestataire concernent la fiche ENTIÈRE, pas l'onglet courant.
          Les enterrer sous les onglets les rendait invisibles dès qu'on
          quittait « Le bien ». Masquées sur « Droits des champs », qui n'est
          pas la fiche.

          Masquées AUSSI en mode réglage des droits : elles parlent de la
          complétude du bien qui sert de support d'affichage, ce dont l'admin
          n'a que faire quand il règle des permissions.

          Masquées AUSSI quand ficheTabs est vide (rôle réel + préréglage
          masquent tous les onglets, cf. garde plus bas) : dans ce cas précis
          on ne rend RIEN de plus que le message "aucun onglet accessible",
          ces bannières parlent d'un contenu (champs, refus prestataire) que
          ce viewer n'a par ailleurs le droit de voir sur aucun onglet. */}
      {ficheTabs.length > 0 && activeTab !== "permissions" && !permsMode && (
      <div className="flex flex-col gap-4 mb-6">
        {/* Note 2026-05-07 : ancienne légende "champs en rouge = nécessaires pour le mailing"
            retirée. Les 2 bannières ci-dessous expliquent maintenant les couleurs
            (rouge = obligatoire, orange = mailing) de manière contextuelle.
            La clé i18n biens.champsMailingHint reste dans les locales pour ne pas
            casser un éventuel autre consumer. */}

        {/* Bannières validation EN PRÉPARATION : cf. lib/bienPrepValidation.ts.
            Demande CEO 2026-05-04 : forcer la complétude dès qu'un bien entre dans la
            cohorte "en préparation" (renon ou création directe), pour ne pas casser
            la chaîne aval (Suivi ELS, Planning Photo, Mailing). */}
        {!skipRequiredValidation && prepValidation.isInPrep && prepValidation.requiredMissing.length > 0 && (
          <div className="rounded-md bg-danger/10 border border-danger/40 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-danger">
                  {t("biens.prepValidation.requiredTitle", { count: prepValidation.requiredMissing.length })}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {t("biens.prepValidation.requiredHint")}
                </p>
                <ul className="text-xs text-text-primary mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {prepValidation.requiredMissing.map((k) => (
                    <li key={k} className="before:content-['•'] before:mr-1 before:text-danger">
                      {t(`biens.prepValidation.fields.${k}`, { defaultValue: k })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {!skipRequiredValidation && prepValidation.isInPrep && prepValidation.mailingMissing.length > 0 && (
          <div className="rounded-md bg-warning/10 border border-warning/40 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-warning">
                  {t("biens.prepValidation.mailingTitle", { count: prepValidation.mailingMissing.length })}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {t("biens.prepValidation.mailingHint")}
                </p>
                <ul className="text-xs text-text-primary mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  {prepValidation.mailingMissing.map((k) => (
                    <li key={k} className="before:content-['•'] before:mr-1 before:text-warning">
                      {t(`biens.prepValidation.fields.${k}`, { defaultValue: k })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        {/* Bannière refus prestataire ELS (migration 055) — visible CEO/admin/collab ELS uniquement */}
        {(role === "c_level" || role === "admin" || role === "collaborateur" || role === "collaborateur_els")
          && bien.prestataireEleRefusePar
          && bien.prestataireEleRefuseAt && (() => {
          const refuser = profiles.find((p) => p.id === bien.prestataireEleRefusePar);
          const refuserName = refuser
            ? `${refuser.first_name ?? ""} ${refuser.last_name ?? ""}`.trim() || refuser.email
            : bien.prestataireEleRefusePar;
          const d = new Date(bien.prestataireEleRefuseAt);
          const refuseDate = isNaN(d.getTime())
            ? bien.prestataireEleRefuseAt
            : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          return (
            <div className="flex items-center gap-2 bg-danger/10 border border-danger/30 rounded-md px-3 py-2 text-sm text-danger">
              <AlertTriangle size={16} className="flex-shrink-0" />
              <span>{t("etatDesLieux.refusBanner", { date: refuseDate, name: refuserName })}</span>
            </div>
          );
        })()}
      </div>
      )}

      {permsMode && (
        <div className="flex flex-wrap gap-2 mb-3">
          {FICHE_TAB_IDS.map((id) => (
            <PermVisibilityToggle
              key={id}
              label={t(FICHE_TAB_META[id].labelKey)}
              itemKey={tabStructuralKey(id)}
              visible={permsEditor!.get(tabStructuralKey(id)) !== "hidden"}
              onChange={(v) => permsEditor!.set(tabStructuralKey(id), v ? "view" : "hidden")}
            />
          ))}
        </div>
      )}

      {/* Onglets de la fiche — structure CONSTANTE : les 5 onglets métier sont
          toujours rendus, seul « Droits des champs » (admin) est conditionnel.
          Sauf filtrage par rôle réel / préréglage popup (chantier visibilité
          contextuelle 2026-08) : ficheTabs peut alors être un sous-ensemble,
          voire vide (cf. garde ci-dessous). */}
      {ficheTabs.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-surface px-4 py-8 text-center text-sm text-text-muted">
          {t("droits.aucunOngletAccessible")}
        </div>
      ) : (
        <FicheTabBar tabs={ficheTabs} active={activeTab} onChange={setActiveTab} alerts={tabAlerts} />
      )}

      {/* Matrice des droits — hors fiche, admin uniquement */}
      {activeTab === "permissions" && role === "admin" && (
        <BienFieldPermissions />
      )}

      {/* Fiche */}
      {ficheTabs.length > 0 && activeTab !== "permissions" && (
      <div className="flex flex-col gap-6">
        {/* ══ EN-TÊTE DE FICHE — HORS ONGLETS ══
            Statut du bien et badge d'étape : deux informations lues en
            permanence, sur une seule ligne. Ni l'une ni l'autre n'est une
            caractéristique du bâti, elles ne relèvent donc d'aucun des cinq
            onglets (spec 2026-08-07).
            `transaction` a été retirée (demande Thibaud 2026-08-08) : cette
            fiche EST la fiche location, et le fil d'Ariane porte déjà le badge. */}
        <FicheHeader
          bien={bien as unknown as BienRow}
          disponibiliteField={F("disponibilite", <SelectField label={t("biens.availability")} value={data.disponibilite} onChange={onDisponibiliteChange} options={DISPONIBILITE_OPTIONS} highlight={hl("disponibilite")} inlineLabel boldLabel bgClassName="bg-white" className="w-[320px]" />)}
        />

        {/* ═══════════════ ONGLET 1 — LE BIEN ═══════════════
            Règle : ce qui SURVIT au départ du locataire. 52 entrées.

            MISE EN PAGE (chantier 2026-08-08) — trois décisions liées :
            1. Les blocs sont EMPILÉS EN PLEINE LARGEUR, jamais appariés deux
               par deux. C'est l'appariement qui produisait 626 px de blanc :
               une grille aligne ses cellules sur la plus haute, donc Identité
               (5 champs) s'étirait à la hauteur de Localisation (9 champs).
            2. Grille de 12 colonnes, chaque champ déclarant sa PORTÉE, au lieu
               de colonnes de largeur fixe. « Code postal » n'occupe plus la
               même place que « Adresse » (cf. Baymard : la largeur d'un champ
               annonce la longueur attendue).
            3. Les champs qui répondent à UNE MÊME question passent sous un
               libellé unique (FieldGroup) — motif fieldset/legend de GOV.UK.
               Cela SUPPRIME les libellés redondants au lieu de les abréger. */}
        {activeTab === "bien" && (
        <>
          {/* ── Identité ── */}
          <SectionCard title={t("biens.sections.identite")} icon={Building2} columns={1} collapsible density="compact">
            <div className={GRID12}>
              {/* Ligne 1 : ce qui identifie le bien chez NOUS (3 + 5 + 4 = 12).
                  Ligne 2 : les références EXTERNES, Rentio et Immoweb. */}
              {FS("referenceInterne", 3, <FormField label={t("biens.internalRef")} value={data.referenceInterne} onChange={set("referenceInterne")} highlight={hl("referenceInterne")} />)}
              {FS("projet", 5, <FreeTextAutocompleteField label={t("biens.projet")} value={data.projet} onChange={set("projet")} suggestions={projetSuggestions} highlight={hl("projet")} />)}
              {FS("typeBien", 4, <SelectField label={t("biens.type")} value={data.typeBien} onChange={set("typeBien")} options={TYPES_BIEN} highlight={hl("typeBien")} />)}
              {FS("referenceExterne", 3, <FormField label={t("biens.externalRef")} value={data.referenceExterne} onChange={set("referenceExterne")} highlight={hl("referenceExterne")} />)}
              {FS("codeImmoweb", 4, <FormField label={t("biens.codeImmoweb")} value={data.codeImmoweb} onChange={set("codeImmoweb")} highlight={hl("codeImmoweb")} />)}
            </div>
          </SectionCard>

          {/* ── Localisation ── */}
          <SectionCard title={t("biens.sections.localisation")} icon={MapPin} columns={1} collapsible density="compact">
            <div className={GRID12}>
              <div className="col-span-12 md:col-span-10">
                <FieldGroup
                  legend={t("biens.groups.adresseLegend")}
                  units={[
                    { key: "adresse", weight: 6, node: F("adresse", <FormField label={t("biens.groups.adresseRue")} value={data.adresse} onChange={set("adresse")} highlight={hl("adresse")} labelBelow />) },
                    { key: "propertyBox", weight: 1.2, node: F("propertyBox", <FormField label={t("biens.groups.adresseBoite")} value={data.propertyBox} onChange={set("propertyBox")} highlight={hl("propertyBox")} labelBelow align="center" />) },
                    { key: "codePostal", weight: 1.4, node: F("codePostal", <FormField label={t("biens.groups.adresseCp")} value={data.codePostal} onChange={handleCodePostalChange} highlight={hl("codePostal")} labelBelow align="center" />) },
                    { key: "ville", weight: 3.4, node: F("ville", <FormField label={t("biens.groups.adresseCommune")} value={data.ville} onChange={handleVilleChange} highlight={hl("ville")} labelBelow align="center" />) },
                  ]}
                />
              </div>
              {FS("region", 2, <SelectField label={t("common.region")} value={data.region} onChange={set("region")} options={[{ value: "bruxelles", label: t("biens.regions.bruxelles") }, { value: "flandre", label: t("biens.regions.flandre") }, { value: "wallonie", label: t("biens.regions.wallonie") }]} highlight={hl("region")} />)}
              {FS("quartier_id", 4, <SelectField label={t("biens.quartier")} value={data.quartier} onChange={set("quartier")} options={quartierOptions} />)}
              {FS("typeEnvironnement", 4, <SelectField label={t("biens.environmentType")} value={data.typeEnvironnement} onChange={set("typeEnvironnement")} options={TYPES_ENVIRONNEMENT} />)}
              <div className="col-span-12 md:col-span-4">
                <FieldGroup
                  legend={t("biens.groups.etageLegend")}
                  units={[
                    { key: "etage", node: F("etage", <FormField label={t("biens.groups.etageNiveau")} value={data.etage} onChange={set("etage")} highlight={hl("etage")} labelBelow align="center" />) },
                    { key: "etagesTotal", before: t("biens.groups.etageSur"), node: F("etagesTotal", <FormField label={t("biens.groups.etageTotal")} value={data.etagesTotal} onChange={set("etagesTotal")} highlight={hl("etagesTotal")} labelBelow align="center" />) },
                  ]}
                />
              </div>
            </div>
          </SectionCard>

          {/* ── Intérieur ── */}
          <SectionCard title={t("biens.sections.interieur")} icon={Home} columns={1} collapsible density="compact">
            <div className={GRID12}>
              <div className="col-span-12 md:col-span-4">
                <FieldGroup
                  legend={t("biens.groups.piecesLegend")}
                  units={[
                    { key: "nbChambres", node: F("nbChambres", <FormField label={t("biens.groups.piecesChambres")} value={data.nbChambres} onChange={set("nbChambres")} highlight={hl("nbChambres")} align="center" labelBelow />) },
                    { key: "nbSdb", node: F("nbSdb", <FormField label={t("biens.groups.piecesSdb")} value={data.nbSdb} onChange={set("nbSdb")} highlight={hl("nbSdb")} align="center" labelBelow />) },
                    { key: "nbDouches", node: F("nbDouches", <FormField label={t("biens.groups.piecesDouches")} value={data.nbDouches} onChange={set("nbDouches")} align="center" labelBelow />) },
                    { key: "nbToilettes", node: F("nbToilettes", <FormField label={t("biens.groups.piecesWc")} value={data.nbToilettes} onChange={set("nbToilettes")} align="center" labelBelow />) },
                  ]}
                />
              </div>
              <div className="col-span-12 md:col-span-5">
                <FieldGroup
                  legend={t("biens.groups.surfacesLegend")}
                  units={[
                    { key: "surfaceHabitable", node: F("surfaceHabitable", <FormField label={t("biens.groups.surfacesHabitable")} value={data.surfaceHabitable} onChange={set("surfaceHabitable")} align="center" labelBelow />) },
                    { key: "surfaceBrute", node: F("surfaceBrute", <FormField label={t("biens.groups.surfacesBrute")} value={data.surfaceBrute} onChange={set("surfaceBrute")} align="center" labelBelow />) },
                    { key: "surfaceSalon", node: F("surfaceSalon", <FormField label={t("biens.groups.surfacesSalon")} value={data.surfaceSalon} onChange={set("surfaceSalon")} align="center" labelBelow />) },
                    { key: "surfaceCuisine", node: F("surfaceCuisine", <FormField label={t("biens.groups.surfacesCuisine")} value={data.surfaceCuisine} onChange={set("surfaceCuisine")} align="center" labelBelow />) },
                  ]}
                />
              </div>
              {FS("typeSol", 3, <SelectField label={t("biens.floorType")} value={data.typeSol} onChange={set("typeSol")} options={TYPES_SOL} highlight={hl("typeSol")} />)}
              {/* Répéteur chambres — alimente `chambresDetail` ET le miroir
                  `surfacesChambres` lu par le mapper Immoweb. Laissé en pleine
                  largeur : c'est un des deux seuls éléments à hauteur variable
                  de l'onglet, l'enfermer dans une colonne recréerait du vide. */}
              {/* Répéteur : hors entonnoir `F()`, donc réglé via `PS`. */}
              {permsMode && (
                <div className="col-span-12 md:col-span-4">{PS("chambresDetail", t("biens.repeater.chambresTitle"))}</div>
              )}
              {!permsMode && isFieldVisible("chambresDetail", canSee, presetActive ? presetVisible : null) && (
                <div className="col-span-12 border-t border-border pt-4 mt-1">
                  <p className="text-[10.5px] font-semibold tracking-widest uppercase text-text-secondary mb-3">{t("biens.repeater.chambresTitle")}</p>
                  {/* Lignes PILOTÉES par `nbChambres` : pas de bouton d'ajout,
                      le nombre de chambres est déjà saisi juste au-dessus. */}
                  <SurfaceOrientationRepeater
                    kind="chambre"
                    rows={repeaterRows("chambresDetail")}
                    onChange={setRepeaterRows("chambresDetail")}
                    editable={canEdit("chambresDetail")}
                    count={Number(data.nbChambres) || 0}
                  />
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── Extérieur et annexes ── */}
          <SectionCard title={t("biens.sections.exterieurAnnexes")} icon={Trees} columns={1} collapsible density="compact">
            <div className={GRID12}>
              <div className="col-span-12 md:col-span-3">
                <FieldGroup
                  legend={t("biens.groups.caveLegend")}
                  units={[
                    { key: "caveDisponible", basis: ANNEXE_DISPO_W, node: F("caveDisponible", <SelectField label={t("biens.groups.annexeDisponible")} value={data.caveDisponible} onChange={set("caveDisponible")} options={CAVE_OPTIONS} highlight={hl("caveDisponible")} labelBelow />) },
                    { key: "caveNumero", basis: ANNEXE_NUM_W, node: F("caveNumero", <FormField label={t("biens.groups.annexeNumero")} value={data.caveNumero} onChange={set("caveNumero")} highlight={hl("caveNumero")} labelBelow />) },
                  ]}
                />
              </div>
              <div className="col-span-12 md:col-span-5">
                <FieldGroup
                  legend={t("biens.groups.parkingLegend")}
                  units={[
                    { key: "parkingDisponible", basis: ANNEXE_DISPO_W, node: F("parkingDisponible", <SelectField label={t("biens.groups.annexeDisponible")} value={data.parkingDisponible} onChange={set("parkingDisponible")} options={PARKING_OPTIONS} highlight={hl("parkingDisponible")} labelBelow />) },
                    { key: "parkingNumero", basis: ANNEXE_NUM_W, node: F("parkingNumero", <FormField label={t("biens.groups.annexeNumero")} value={data.parkingNumero} onChange={set("parkingNumero")} highlight={hl("parkingNumero")} labelBelow />) },
                    { key: "parkingFerme", basis: ANNEXE_FLAG_W, node: F("parkingFerme", <SelectField label={t("biens.groups.annexeFerme")} value={boolToOption(data.parkingFerme)} onChange={setBool("parkingFerme")} options={OUI_NON_OPTIONS} labelBelow />) },
                    { key: "parkingInterieur", basis: ANNEXE_FLAG_W, node: F("parkingInterieur", <SelectField label={t("biens.groups.annexeInterieur")} value={boolToOption(data.parkingInterieur)} onChange={setBool("parkingInterieur")} options={OUI_NON_OPTIONS} labelBelow />) },
                  ]}
                />
              </div>
              <div className="col-span-12 md:col-span-3">
                <FieldGroup
                  legend={t("biens.groups.garageLegend")}
                  units={[
                    { key: "garageDisponible", basis: ANNEXE_DISPO_W, node: F("garageDisponible", <SelectField label={t("biens.groups.annexeDisponible")} value={data.garageDisponible} onChange={set("garageDisponible")} options={GARAGE_OPTIONS} highlight={hl("garageDisponible")} labelBelow />) },
                    { key: "garageNumero", basis: ANNEXE_NUM_W, node: F("garageNumero", <FormField label={t("biens.groups.annexeNumero")} value={data.garageNumero} onChange={set("garageNumero")} highlight={hl("garageNumero")} labelBelow />) },
                  ]}
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <FieldGroup
                  legend={t("biens.groups.jardinCourt")}
                  units={[
                    { key: "surfaceJardin", node: F("surfaceJardin", <FormField label={t("biens.repeater.unitSurface")} value={data.surfaceJardin} onChange={set("surfaceJardin")} align="center" labelBelow />) },
                  ]}
                />
              </div>
              {permsMode && (
                <div className="col-span-12 md:col-span-4">{PS("terrasses", t("biens.repeater.terrassesTitle"))}</div>
              )}
              {!permsMode && isFieldVisible("terrasses", canSee, presetActive ? presetVisible : null) && (
                <div className="col-span-12 border-t border-border pt-4 mt-1">
                  <p className="text-[10.5px] font-semibold tracking-widest uppercase text-text-secondary mb-3">{t("biens.repeater.terrassesTitle")}</p>
                  <SurfaceOrientationRepeater
                    kind="terrasse"
                    rows={repeaterRows("terrasses")}
                    onChange={setRepeaterRows("terrasses")}
                    editable={canEdit("terrasses")}
                  />
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── Équipements ── */}
          <SectionCard title={t("biens.sections.equipements")} icon={Settings} columns={1} collapsible density="compact">
            {/* Grille de douze cases, une seule colonne DB : le droit se règle
                sur `equipements` en bloc, pas case par case. */}
            {permsMode && <div className="max-w-xs">{PS("equipements", t("biens.sections.equipements"))}</div>}
            <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3", permsMode && "hidden")}>
              {/* Liste UNIQUE partagée avec la fiche vente (décision Thibaud
                  2026-08-09) : les deux écrivent la même colonne `equipements`,
                  deux listes différentes rendaient certains tags invisibles
                  d'un côté. Les libellés passent enfin par i18n — ils étaient
                  écrits en français en dur. `tag` reste la valeur stockée. */}
              {isFieldVisible("equipements", canSee, presetActive ? presetVisible : null) && EQUIPEMENT_TAGS.map(({ tag, labelKey }) => (
                <div key={tag} className={!canEdit("equipements") ? "pointer-events-none select-none" : undefined}>
                  <Checkbox label={t(labelKey)} checked={safeEquipements().includes(tag)} onChange={() => toggleEquipement(tag)} />
                </div>
              ))}
            </div>
          </SectionCard>

          {/* ── Bâti ── */}
          <SectionCard title={t("biens.sections.bati")} icon={Building2} columns={1} collapsible density="compact">
            <div className={GRID12}>
              {FS("anneeConstruction", 2, <SelectField label={t("biens.anneeConstruction")} value={data.anneeConstruction} onChange={set("anneeConstruction")} options={ANNEES_BATI} align="center" />)}
              {FS("anneeRenovation", 2, <SelectField label={t("biens.anneeRenovation")} value={data.anneeRenovation} onChange={set("anneeRenovation")} options={ANNEES_BATI} align="center" />)}
              {FS("etatBatiment", 3, <SelectField label={t("biens.buildingCondition")} value={data.etatBatiment} onChange={set("etatBatiment")} options={ETATS_BATIMENT} />)}
              {FS("nbFacades", 2, <FormField label={t("biens.nbFacades")} value={data.nbFacades} onChange={set("nbFacades")} align="center" />)}
              {FS("largeurFacade", 2, <FormField label={t("biens.largeurFacade")} value={data.largeurFacade} onChange={set("largeurFacade")} align="center" />)}
            </div>
          </SectionCard>

          {/* ── Énergie — CERTIFICAT EN VIGUEUR uniquement. La branche de
                 renouvellement (expert, RDV, nouveau certificat) appartient au
                 cycle : elle vit dans l'onglet « Cycle en cours ». ── */}
          <SectionCard
            title={t("biens.sections.energieCertificat")}
            icon={Zap}
            columns={1}
            actions={renewalNeeded ? <AlertTriangle size={16} className="text-warning" /> : pebAlert ? <AlertTriangle size={16} className="text-danger" /> : undefined}
            collapsible
            density="compact"
          >
            <div className={GRID12}>
              {/* Décision Thibaud 2026-09-14 : le certificat en vigueur reste
                  TOUJOURS directement éditable, comme l'énergie/l'électricité
                  juste en dessous — même quand `renewalNeeded` est vrai. Un
                  bien fraîchement importé (Immoweb/WHISE) sans date PEB
                  fiable n'a pas besoin d'un "cycle PEB" avec expert pour
                  qu'on tape la vraie donnée qu'on a par ailleurs ; verrouiller
                  ce champ forçait à tort le passage par la carte
                  "Renouvellement PEB" (expert, RDV) pour une simple
                  correction de données. Cette carte reste affichée en dessous
                  comme démarche séparée et volontaire (commander une vraie
                  expertise), elle ne conditionne plus l'édition directe. */}
              {FS("classePeb", 2, <SelectField label={t("biens.pebClass")} value={data.classePeb} onChange={set("classePeb")} options={CLASSES_PEB} highlight={hl("classePeb")} align="center" />)}
              {FS("certificatPeb", 7, <FormField label={t("biens.pebCertificate")} value={data.certificatPeb} onChange={set("certificatPeb")} highlight={hl("certificatPeb")} />)}
              {FS("pebFinLe", 3, <DateField label={t("biens.pebExpiryDate")} value={data.pebFinLe} onChange={set("pebFinLe")} highlight={hl("pebFinLe")} />)}
              {FS("consoKwh", 3, <FormField label={t("biens.consumption")} value={data.consoKwh} onChange={set("consoKwh")} highlight={hl("consoKwh")} align="center" />)}
              {FS("emissionCo2", 3, <FormField label={t("biens.co2Emission")} value={data.emissionCo2} onChange={set("emissionCo2")} highlight={hl("emissionCo2")} align="center" />)}
              {FS("consoTheorique", 3, <FormField label={t("vente.fields.consoTheorique")} value={data.consoTheorique} onChange={set("consoTheorique")} highlight={hl("consoTheorique")} align="center" />)}
              {FS("chauffageEnergie", 4, <SelectField label={t("vente.fields.chauffageEnergie")} value={data.chauffageEnergie} onChange={set("chauffageEnergie")} options={CHAUFFAGE_ENERGIE} highlight={hl("chauffageEnergie")} />)}
              {FS("inspectionElecValide", 4, <SelectField label={t("vente.fields.inspectionElecValide")} value={data.inspectionElecValide} onChange={set("inspectionElecValide")} options={OUI_NON_OPTIONS} highlight={hl("inspectionElecValide")} />)}
              <div className="col-span-12 flex items-center justify-center gap-2 pt-1">
                {/* Signalé Thibaud 2026-09-14 : ce liseré restait vert
                    "valide jusqu'au [date passée]" même pour un certificat
                    déjà expiré — devenu visible dès que les champs ci-dessus
                    ont cessé d'être verrouillés en lecture seule dans ce cas.
                    Recalculé ici sur les données brutes (pas `renewalNeeded`,
                    qui inclut `permsMode`) pour rester exact. */}
                {pebCloture || !isPebExpiringSoon(data.pebFinLe, data.classePeb) ? (
                  <>
                    <CheckCircle size={16} className="text-success" />
                    <p className="text-sm text-text-secondary">
                      {pebCloture ? t("biens.pebExpertiseTerminee") : t("biens.pebValideJusqu", { date: fmtPebDate(data.pebFinLe) })}
                    </p>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={16} className="text-danger" />
                    <p className="text-sm text-danger">{t("biens.ouvrirCycle.pebMotif")}</p>
                  </>
                )}
                {bien.pebCertificatPdf && isFieldVisible("certificatPeb", canSee, presetActive ? presetVisible : null) && (
                  <a
                    href={bien.pebCertificatPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                  >
                    <FileText size={13} /> {t("auditPeb.openPdf")}
                  </a>
                )}
              </div>
            </div>
          </SectionCard>

          {/* ── Financier — valeurs de RÉFÉRENCE du bien (loyer demandé, ce qu'on
                 affiche). Le loyer CONTRACTUEL, ce qu'un locataire paie
                 réellement, vit dans l'onglet Locataires / table `locataires`. ── */}
          <SectionCard title={t("biens.sections.financierReference")} icon={DollarSign} columns={1} collapsible density="compact">
            <div className={GRID12}>
              <div className="col-span-12 md:col-span-3">
                <FieldGroup
                  legend={t("biens.groups.loyerLegend")}
                  units={[
                    {
                      // Repère visuel (placeholder, pas une valeur) : ce que payait
                      // le dernier locataire, pour que le loyer demandé se fixe en
                      // connaissance de cause. Jamais pré-rempli — confirmation
                      // manuelle obligatoire (demande Thibaud). Le locataire ACTUEL
                      // n'a de loyer que si le bien est encore LOUÉ (renon reçu,
                      // pas encore archivé) ; une fois le bien repassé DISPONIBLE
                      // (nettoyage LOUÉ→DISPONIBLE), c'est l'ANCIEN qui porte la
                      // dernière valeur connue — vérifié en base sur les TEST-L09
                      // (Gilles Poux, 628.59€/100€, archivé en "ancien").
                      key: "loyerMensuel",
                      node: F("loyerMensuel", <FormField label={t("biens.groups.loyerLoyer")} value={data.loyerMensuel} onChange={set("loyerMensuel")} placeholder={data.nouveauLocataireLoyerActuel || data.ancienLocataireLoyerActuel || "..."} labelBelow align="center" />),
                    },
                    {
                      key: "chargesMensuelles",
                      node: F("chargesMensuelles", <FormField label={t("biens.groups.loyerCharges")} value={data.chargesMensuelles} onChange={set("chargesMensuelles")} highlight={hl("chargesMensuelles")} placeholder={data.nouveauLocataireChargesActuelles || data.ancienLocataireChargesActuelles || "..."} labelBelow align="center" />),
                    },
                  ]}
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <FieldGroup
                  legend={t("biens.groups.annexesLegend")}
                  units={[
                    { key: "caveLoyerMensuel", node: F("caveLoyerMensuel", <FormField label={t("biens.groups.annexesCave")} value={data.caveLoyerMensuel} onChange={set("caveLoyerMensuel")} highlight={hl("caveLoyerMensuel")} labelBelow align="center" />) },
                    { key: "parkingLoyerMensuel", node: F("parkingLoyerMensuel", <FormField label={t("biens.groups.annexesParking")} value={data.parkingLoyerMensuel} onChange={set("parkingLoyerMensuel")} highlight={hl("parkingLoyerMensuel")} labelBelow align="center" />) },
                    { key: "garageLoyerMensuel", node: F("garageLoyerMensuel", <FormField label={t("biens.groups.annexesGarage")} value={data.garageLoyerMensuel} onChange={set("garageLoyerMensuel")} highlight={hl("garageLoyerMensuel")} labelBelow align="center" />) },
                  ]}
                />
              </div>
              {FS("meuble", 2, <SelectField label={t("biens.meuble")} value={boolToOption(data.meuble)} onChange={setBool("meuble")} options={OUI_NON_OPTIONS} highlight={hl("meuble")} align="center" />)}
              {/* `companyContract` : condition commerciale, il se lit avec le
                  loyer et le meublé (demande Thibaud 2026-08-08). */}
              {FS("companyContract", 3, <SelectField label={t("biens.companyContract")} value={boolToOption(data.companyContract)} onChange={setBool("companyContract")} options={OUI_NON_OPTIONS} highlight={hl("companyContract")} align="center" />)}
              {/* Charges incluses : cases à cocher plutôt qu'un déroulant
                  multi-sélection. Huit choix courts, tous lisibles d'un coup —
                  le déroulant obligeait à l'ouvrir pour savoir ce qui était
                  coché. Même traitement que les équipements. */}
              {permsMode && (
                <div className="col-span-12 md:col-span-4">{PS("chargesIncluses", t("biens.chargesIncluded"))}</div>
              )}
              {!permsMode && isFieldVisible("chargesIncluses", canSee, presetActive ? presetVisible : null) && (
                <div className="col-span-12 border-t border-border pt-4 mt-1">
                  {/* Champ mailing (orange) rendu hors de `F()` (grille de cases) :
                      on reproduit à la main le signal de manque de FormField —
                      libellé et cadre orange tant que rien n'est coché
                      (signalé Thibaud 2026-09-15 : « on voit qu'on doit
                      remplir le plancher mais pas les charges »). */}
                  <p className={cn("text-[10.5px] font-semibold tracking-widest uppercase mb-3", prepValidation.byField.get("chargesIncluses") === "mailing" ? "text-warning" : "text-text-secondary")}>{t("biens.chargesIncluded")}</p>
                  <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3", !canEdit("chargesIncluses") && "pointer-events-none select-none", prepValidation.byField.get("chargesIncluses") === "mailing" && "rounded-md border border-solid border-warning px-3 py-2")}>
                    {CHARGES_INCLUSES_OPTIONS.map((opt) => (
                      <Checkbox
                        key={opt.value}
                        label={opt.label}
                        checked={safeChargesIncluses().includes(opt.value)}
                        onChange={() => toggleChargeIncluse(opt.value)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        </>
        )}

        {/* ═══════════════ ONGLET 3 — LOCATAIRES ═══════════════
            Loyers CONTRACTUELS. Ces champs lisent et écrivent la table
            `locataires`, pas des colonnes de `biens`. Reste à faire dans une
            passe dédiée : les 6 colonnes d'annexes (cave/parking/garage ×
            initial/actuel) + les 120 lignes field_permissions associées. */}
        {activeTab === "locataires" && (
          showSection("locataires") ? (
            <CardGrid>
              <CardGridItem width="full">
            <div ref={locatairesSectionRef}>
            <SectionCard
              title={t("biens.sections.locataires")}
              icon={Users}
              columns={1}
              collapsible
              actions={isAdminOrCLevel ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    title={t("biens.transfererLocataire")}
                    className="p-1.5 rounded hover:bg-surface-2 cursor-pointer text-text-muted hover:text-accent transition-colors outline-none shadow-none"
                    onClick={handleTransferLocataire}
                  >
                    <ArrowLeftRight size={15} />
                  </button>
                  <button
                    type="button"
                    title={t("biens.historiqueLocataires")}
                    className="p-1.5 rounded hover:bg-surface-2 cursor-pointer text-text-muted hover:text-accent transition-colors outline-none shadow-none"
                    onClick={() => setHistoOpen(true)}
                  >
                    <History size={15} />
                  </button>
                </div>
              ) : undefined}
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="text-xs font-medium tracking-wider uppercase text-text-muted">
                    {t("biens.ancienLocataire")}
                  </div>
                  {F("ancienLocataireNom", <FormField label={t("common.name")} value={data.ancienLocataireNom} onChange={set("ancienLocataireNom")} />)}
                  {F("ancienLocataireEmail", <FormField label={t("common.email")} value={data.ancienLocataireEmail} onChange={set("ancienLocataireEmail")} />)}
                  {F("ancienLocataireTel", <FormField label={t("common.phone")} value={data.ancienLocataireTel} onChange={set("ancienLocataireTel")} />)}
                  {F("ancienLocataireDateDebut", <DateField label={t("locataires.dateDebut")} value={data.ancienLocataireDateDebut} onChange={set("ancienLocataireDateDebut")} />)}
                  {F("ancienLocataireDateFin", <DateField label={t("locataires.dateFin")} value={data.ancienLocataireDateFin} onChange={set("ancienLocataireDateFin")} />)}
                  {F("ancienLocataireLoyerInitial", <FormField label={t("locataires.loyerInitial")} value={data.ancienLocataireLoyerInitial} onChange={set("ancienLocataireLoyerInitial")} />)}
                  {F("ancienLocataireLoyerActuel", <FormField label={t("locataires.loyerActuel")} value={data.ancienLocataireLoyerActuel} onChange={set("ancienLocataireLoyerActuel")} />)}
                  {F("ancienLocataireChargesInitiales", <FormField label={t("locataires.chargesInitiales")} value={data.ancienLocataireChargesInitiales} onChange={set("ancienLocataireChargesInitiales")} />)}
                  {F("ancienLocataireChargesActuelles", <FormField label={t("locataires.chargesActuelles")} value={data.ancienLocataireChargesActuelles} onChange={set("ancienLocataireChargesActuelles")} />)}
                  {F("ancienLocataireIndexBaseYear", <FormField label={t("locataires.indexBaseYear")} value={data.ancienLocataireIndexBaseYear} onChange={set("ancienLocataireIndexBaseYear")} />)}
                </div>
                <div className="space-y-4">
                  <div className="text-xs font-medium tracking-wider uppercase text-text-muted">
                    {t("biens.nouveauLocataire")}
                  </div>
                  {F("nouveauLocataireNom", <FormField label={t("common.name")} value={data.nouveauLocataireNom} onChange={set("nouveauLocataireNom")} />)}
                  {F("nouveauLocataireEmail", <FormField label={t("common.email")} value={data.nouveauLocataireEmail} onChange={set("nouveauLocataireEmail")} />)}
                  {F("nouveauLocataireTel", <FormField label={t("common.phone")} value={data.nouveauLocataireTel} onChange={set("nouveauLocataireTel")} />)}
                  {F("nouveauLocataireDateDebut", <DateField label={t("locataires.dateDebut")} value={data.nouveauLocataireDateDebut} onChange={set("nouveauLocataireDateDebut")} />)}
                  {F("nouveauLocataireDateFin", <DateField label={t("locataires.dateFin")} value={data.nouveauLocataireDateFin} onChange={set("nouveauLocataireDateFin")} />)}
                  {/* Placeholder gris = "ce qui est demandé sur Le bien"
                      (loyerMensuel/chargesMensuelles), même patron que
                      l'indication inverse sur l'onglet Le bien (placeholder
                      depuis le locataire) — demande Thibaud 2026-08-20. Un
                      placeholder n'écrit rien : le collab tape la vraie
                      valeur au bail signé, qui peut différer. */}
                  {F("nouveauLocataireLoyerInitial", <FormField label={t("locataires.loyerInitial")} value={data.nouveauLocataireLoyerInitial} onChange={set("nouveauLocataireLoyerInitial")} placeholder={data.loyerMensuel || "..."} />)}
                  {F("nouveauLocataireLoyerActuel", <FormField label={t("locataires.loyerActuel")} value={data.nouveauLocataireLoyerActuel} onChange={set("nouveauLocataireLoyerActuel")} />)}
                  {F("nouveauLocataireChargesInitiales", <FormField label={t("locataires.chargesInitiales")} value={data.nouveauLocataireChargesInitiales} onChange={set("nouveauLocataireChargesInitiales")} placeholder={data.chargesMensuelles || "..."} />)}
                  {F("nouveauLocataireChargesActuelles", <FormField label={t("locataires.chargesActuelles")} value={data.nouveauLocataireChargesActuelles} onChange={set("nouveauLocataireChargesActuelles")} />)}
                  {F("nouveauLocataireIndexBaseYear", <FormField label={t("locataires.indexBaseYear")} value={data.nouveauLocataireIndexBaseYear} onChange={set("nouveauLocataireIndexBaseYear")} />)}
                </div>
              </div>
            </SectionCard>
            </div>

            {/* Modal historique locataires — liste des type='ancien' triés par dateFin desc */}
            <Modal isOpen={histoOpen} onClose={() => setHistoOpen(false)} title={t("biens.historiqueLocataires")}>
              {allAnciensLocataires.length === 0 ? (
                <p className="text-sm text-text-muted">{t("biens.aucunHistorique")}</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {allAnciensLocataires.map((loc) => (
                    <div key={loc.id} className="border-b border-border pb-3 last:border-0 last:pb-0">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-text-primary">{loc.nom || "—"}</span>
                        {(loc.dateDebut || loc.dateFin) && (
                          <span className="text-xs text-text-muted">
                            {(loc.dateDebut || "—")} → {(loc.dateFin || "—")}
                          </span>
                        )}
                      </div>
                      {(loc.email || loc.telephone) && (
                        <div className="mt-1 text-xs text-text-secondary space-y-0.5">
                          {loc.email && <div>{loc.email}</div>}
                          {loc.telephone && <div>{loc.telephone}</div>}
                        </div>
                      )}
                      {(loc.loyerInitial || loc.loyerActuel || loc.chargesInitiales || loc.chargesActuelles || loc.indexBaseYear) && (
                        <div className="mt-2 flex flex-col gap-0.5 text-xs text-text-secondary">
                          {loc.loyerInitial && <div><span className="text-text-muted">{t("locataires.loyerInitial")}:</span> {loc.loyerInitial}</div>}
                          {loc.loyerActuel && <div><span className="text-text-muted">{t("locataires.loyerActuel")}:</span> {loc.loyerActuel}</div>}
                          {loc.chargesInitiales && <div><span className="text-text-muted">{t("locataires.chargesInitiales")}:</span> {loc.chargesInitiales}</div>}
                          {loc.chargesActuelles && <div><span className="text-text-muted">{t("locataires.chargesActuelles")}:</span> {loc.chargesActuelles}</div>}
                          {loc.indexBaseYear && <div><span className="text-text-muted">{t("locataires.indexBaseYear")}:</span> {loc.indexBaseYear}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Modal>
              </CardGridItem>
            </CardGrid>
          ) : (
            <SectionCard title={t("biens.sections.locataires")} icon={Users} columns={1}>
              <p className="text-sm text-text-muted">{t("biens.ficheTabs.sectionMasquee")}</p>
            </SectionCard>
          )
        )}


        {/* ═══════════════ ONGLET 2 — CYCLE EN COURS ═══════════════
            Règle : ce qui est REMIS À ZÉRO à chaque rotation. L'onglet reste
            présent même sans cycle ouvert (structure constante) — il affiche
            alors son état vide, réduit au geste qui ouvre un cycle.

            Mise en page alignée sur « Le bien » et sur la fiche vente : blocs
            `SectionCard density="compact"` EMPILÉS pleine largeur (jamais
            appariés — c'est ce qui creusait des colonnes de vide), grille de 12
            colonnes à portées variables, `FieldGroup` pour les champs qui
            répondent à une même question. */}
        {activeTab === "cycle" && (
        <>
          {/* ── Disponibilité ──
              Sortie du cycle pour TOUS les biens, y compris en rotation
              (2026-09-14, demande Thibaud) : cette date alimente directement
              la publication Immoweb (`management.isImmediatelyAvailable` /
              `availableFrom`, cf. le mapper) et doit rester réglable même
              sans renon en main — le cas de tout bien fraîchement importé,
              qui n'a par définition aucun papier de préavis chez nous. Rendu
              inconditionnel, hors de `cycleOuvert`. Chemin identique côté
              vente (`venteFormFields.ts`), qui n'a jamais eu ce verrou. */}
          <SectionCard title={t("biens.sections.disponibilite")} icon={CalendarCheck} columns={1} collapsible density="compact">
            <div className={GRID12}>
              <div className="col-span-12 md:col-span-4">
                {F("seraDispoPour", <DateField label={t("biens.groups.cycleLibreLe")} value={data.seraDispoPour} onChange={set("seraDispoPour")} highlight={hl("seraDispoPour")} />)}
              </div>
              {/* « Fin de contrat » à côté de « Libre le » (décision Thibaud
                  2026-09-15 soir) : champ mailing (orange = alerte, pas une
                  obligation), il vivait dans la carte « Ouverture du cycle »
                  — donc invisible sans cycle ouvert alors que la pastille de
                  l'onglet le comptait (bien Rentio 7709976). */}
              <div className="col-span-12 md:col-span-4">
                {F("finDeContrat", <DateField label={t("biens.groups.cycleFinContrat")} value={data.finDeContrat} onChange={set("finDeContrat")} highlight={hl("finDeContrat")} />)}
              </div>
            </div>
          </SectionCard>

          {/* ── Ouverture du cycle ──
              Sans cycle ouvert, ce bloc ne montre QUE la porte d'entrée : le
              dialogue. Avant, les quatre décisions d'ouverture étaient quatre
              champs indépendants, et saisir « Renon reçu le » ouvrait un cycle
              par effet de bord, sans collaborateur ni expert désigné.
              Rendu pour TOUT bien depuis le plan « deux cycles » (2026-09-15) :
              l'existence d'un cycle est un geste explicite du C-Level, plus une
              déduction de l'origine du bien (`entreeGestion`, abandonné). */}
          {/* Le crayon qui rouvrait le dialogue "Ouvrir un cycle" en édition
              a été retiré : les mêmes champs (dates, collaborateur, ELS
              requis) sont déjà directement éditables inline ci-dessous, donc
              redondant (signalé Thibaud 2026-08-20 : "pas besoin du crayon,
              j'ai accès aux champs directement"). `openCycleDialog` reste
              utilisé par le bouton "Ouvrir un cycle" initial (sans cycle
              ouvert), plus bas. */}
          {(
          <SectionCard
            title={t("biens.sections.ouvertureCycle")}
            icon={RefreshCw}
            columns={1}
            collapsible
            density="compact"
            actions={
              // En-tête « Cycle ouvert le … » : le fait explicite (migration
              // 135), lisible carte repliée. Sans cycle, l'état vide ci-dessous
              // dit déjà « Aucun cycle de sortie ouvert ».
              cycleOuvert && data.cycleOuvertLe ? (
                <span className="text-xs text-text-muted">
                  {t("biens.cycleOuvertLe", {
                    date: formatDate(data.cycleOuvertLe, i18n.language, { day: "2-digit", month: "2-digit", year: "numeric" }),
                  })}
                </span>
              ) : undefined
            }
          >
            {cycleOuvert ? (
              <div className={GRID12}>
                {/* « Fin de contrat » et « Libre le » vivent dans la carte
                    Disponibilité au-dessus (toujours visible) ; ici ne reste
                    que ce qui n'a de sens qu'avec un cycle : le renon. */}
                {FS("renonRecuLe", 3, <DateField label={t("biens.groups.cycleRenon")} value={data.renonRecuLe} onChange={set("renonRecuLe")} highlight={hl("renonRecuLe")} />)}
                {/* Un responsable interne dès qu'un process est lancé — ELS OU
                    PEB (décision Thibaud 2026-09-15 soir). Sans aucun process
                    (dates seules), le champ disparaît au lieu de rester vide
                    et obligatoire. */}
                {(!sansEls || !sansPeb) && FS("collaborateurSrs", 3, <SelectField label={t("biens.collaborateurSrs")} value={data.collaborateurSrs} onChange={set("collaborateurSrs")} options={collaborateurOptions} highlight={hl("collaborateurSrs")} />)}
                {canSee("elsRequis") && (
                  <div className="col-span-12 md:col-span-3 flex flex-col">
                    {/* Repère invisible de la même hauteur que le libellé du
                        SelectField voisin (text-xs + mb-1), pour que la case
                        se centre exactement sur la même bande verticale que
                        le champ « Collaborateur SRS » — un simple items-end
                        décalait la case de 9px plus bas (mesuré). */}
                    <span className="text-xs mb-1 invisible" aria-hidden="true">&nbsp;</span>
                    <div className="h-[38px] flex items-center">
                      {F("elsRequis", <Checkbox label={t("biens.elsRequis.label")} checked={data.elsRequis !== false} onChange={handleElsRequisChange} />)}
                    </div>
                  </div>
                )}
                {/* Jumeau PEB de « ELS requis » (migration 131, spec Thibaud
                    2026-09-14) : décision explicite, plus un affichage
                    automatique sur `renewalNeeded`. Pas de modale de
                    confirmation au décochage — cf. `setPebRequis`. */}
                {canSee("pebRequis") && (
                  <div className="col-span-12 md:col-span-3 flex flex-col">
                    <span className="text-xs mb-1 invisible" aria-hidden="true">&nbsp;</span>
                    <div className="h-[38px] flex items-center">
                      {F("pebRequis", <Checkbox label={t("biens.pebRequis.label")} checked={data.pebRequis !== false} onChange={setPebRequis} />)}
                    </div>
                  </div>
                )}
                {/* Motif informatif seulement — le système connaît déjà la
                    réponse (`isPebExpiringSoon`), la case ci-dessus reste la
                    seule décision à prendre. */}
                {renewalNeeded && (
                  <p className="col-span-12 flex items-center gap-1.5 text-xs font-medium text-warning">
                    <AlertTriangle size={13} className="shrink-0" />
                    {t("biens.ouvrirCycle.pebMotif")}
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border bg-surface px-4 py-8 text-center">
                <p className="text-sm font-medium text-text-secondary">{t("biens.cycleVide.title")}</p>
                <p className="text-xs text-text-muted mt-1 mb-4">{t("biens.cycleVide.hint")}</p>
                {/* DISPONIBLE ou LOUÉ (décision Q4) : depuis LOUÉ, le dialogue
                    enchaîne le passage en disponible dans le même geste. */}
                {isAdminOrCLevel && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openCycleDialog}
                    disabled={!canOpenCycleHere}
                    title={!canOpenCycleHere ? t("biens.ouvrirCycle.lockedHint") : undefined}
                  >
                    {t("biens.ouvrirCycle.cta")}
                  </Button>
                )}
                {isAdminOrCLevel && !canOpenCycleHere && (
                  <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-warning mt-2">
                    <AlertTriangle size={13} className="shrink-0" />
                    {t("biens.ouvrirCycle.lockedHint")}
                  </p>
                )}
              </div>
            )}
          </SectionCard>
          )}

          {/* ── État des lieux de sortie ──
              Bloc EMPILÉ pleine largeur : apparié à « Clôture SRS » dans une
              CardGrid, il s'étirait à la hauteur du plus grand des deux.
              `!sansEls` (signalé
              Thibaud 2026-09-15) : la carte affichait quand même prestataire/
              date/statut d'un ELS qu'on vient explicitement de dire qu'il
              n'aura pas lieu — cohérent avec `operationalElsStage`, qui
              traite déjà l'ELS comme satisfait dans ce cas. */}
          {cycleOuvert && showSection("etatDesLieux") && !sansEls && (
              <SectionCard
                title={t("biens.sections.etatDesLieuxSortie")}
                icon={ClipboardCheck}
                columns={1}
                collapsible
                density="compact"
                actions={permsMode ? (
                  <PermVisibilityToggle
                    label={t("biens.sections.etatDesLieuxSortie")}
                    itemKey={sectionStructuralKey("etatDesLieux")}
                    visible={permsEditor!.get(sectionStructuralKey("etatDesLieux")) !== "hidden"}
                    onChange={(v) => permsEditor!.set(sectionStructuralKey("etatDesLieux"), v ? "view" : "hidden")}
                  />
                ) : isAdminOrCLevel && hasExitCycleInFlight(data as unknown as BienRow) ? (
                  <button
                    type="button"
                    title={t("biens.viderSortie.button")}
                    className="p-1.5 rounded hover:bg-surface-2 cursor-pointer text-text-muted hover:text-danger transition-colors outline-none shadow-none"
                    onClick={(e) => { e.stopPropagation(); setViderSortieOpen(true); }}
                  >
                    <Trash2 size={15} />
                  </button>
                ) : undefined}
              >
              <div className={GRID12}>
                {/* Les deux prestataires répondent à la même question — QUI ? —
                    et c'est précisément leur confusion qui pose problème : les
                    colonnes portent le nom de l'autre état des lieux. Les
                    réunir sous une légende unique force la lecture par le SENS
                    (« précédent » / « assigné ») plutôt que par le nom. */}
                <div className="col-span-12 md:col-span-6">
                  <FieldGroup
                    legend={t("biens.groups.prestatairesLegend")}
                    units={[
                      // Trace pure : ne déclenche rien (modèle 2026-06-06).
                      { key: "prestataireEls", node: F("prestataireEls", <SelectField label={t("biens.prestataireEls")} value={data.prestataireEls} onChange={handleElsChange} options={elsOptions} labelBelow />) },
                      // L'assignation ACTIVE : c'est elle qui, une fois la
                      // mission lancée, envoie le mail et rend le bien visible.
                      { key: "prestataireEle", node: F("prestataireEle", <SelectField label={t("biens.prestataireEle")} value={data.prestataireEle} onChange={handleEleChange} options={eleOptions} labelBelow />) },
                    ]}
                  />
                  {/* Suggestion, PAS un pré-remplissage : le modèle 2026-06-06
                      veut un choix délibéré (assigner déclenche le mail au
                      lancement). Le C-Level clique pour reprendre le
                      prestataire précédent au lieu de le rechercher dans la
                      liste — rien n'est appliqué sans ce clic explicite
                      (demande user 2026-08-19). */}
                  {!permsMode && canEdit("prestataireEle") && !data.prestataireEle
                    && !!data.prestataireEls && data.prestataireEls !== "amiable" && data.prestataireEls !== "autre"
                    && (
                      <button
                        type="button"
                        onClick={() => handleEleChange(data.prestataireEls)}
                        className="text-[10.5px] text-accent hover:underline text-left px-3 mt-1 cursor-pointer"
                      >
                        {t("biens.prestataireEleSuggestion", {
                          name: elsOptions.find((o) => o.value === data.prestataireEls)?.label ?? data.prestataireEls,
                        })}
                      </button>
                    )}
                </div>
                <div className="col-span-12 md:col-span-3">
                  <FieldGroup
                    legend={t("biens.groups.rdvLegend")}
                    units={[
                      { key: "edlDatePrevue", weight: 1.7, node: F("edlDatePrevue", <DateField label={t("biens.datePrevue")} value={data.edlDatePrevue} onChange={set("edlDatePrevue")} labelBelow />) },
                      { key: "edlHeure", weight: 1, node: F("edlHeure", <TimeField label={t("biens.heure")} value={data.edlHeure} onChange={set("edlHeure")} labelBelow />) },
                    ]}
                  />
                </div>
                {/* `statutEle` est, malgré son nom, LE statut de l'ELS de
                    sortie : c'est lui que lit `isElsOk()`. */}
                {FS("statutEle", 3, <SelectField label={t("biens.statutEle")} value={data.statutEle} onChange={set("statutEle")} options={[
                  { value: "", label: t("biens.statutEleOptions.pasEncoreFixee") },
                  { value: "date_fixee", label: t("biens.statutEleOptions.dateFixee") },
                  { value: "date_passee", label: t("biens.statutEleOptions.datePassee") },
                  { value: "expertise_ok", label: t("biens.statutEleOptions.expertiseOk") },
                ]} />)}
                {/* Migration 103 (appliquée le 2026-08-10) : la case est enfin
                    gardée par la clé de SA colonne. Elle était enveloppée dans
                    `F("jeNaiPasFaitEle", …)`, un nom sans colonne ni lecteur —
                    le signal partait en base et personne ne le lisait. */}
                {canSee("eleNonFait") && (
                  <div className="col-span-12 md:col-span-4 flex items-end pb-2">
                    {F("eleNonFait", <Checkbox label={t("biens.eleNonFait")} checked={!!data.eleNonFait} onChange={toggle("eleNonFait")} />)}
                  </div>
                )}
                {FS("edlCommentaire", 12, <TextAreaField label={t("biens.edlCommentaire")} value={data.edlCommentaire} onChange={set("edlCommentaire")} />)}
              </div>
                {/* « Transfert électricité » et « Appart prêt photos » sont
                    remontés dans leur propre bloc « Préparation » (spec) : ce
                    ne sont pas des données de l'état des lieux, mais ce qui
                    suit une fois l'ELS faite. */}

                {/* Miroir du lancement manuel de mission (popover Pipeline ELS → fiche).
                    Mêmes conditions que PipelineActionsPopover : prestataire assigné
                    réel, non refusé, mission encore à faire, pas déjà lancée. Le
                    bouton PEB vit dans son propre cadre (« PEB — Renouvellement »),
                    pas ici — cf. `missionLaunch` plus haut. */}
                {missionLaunch && (missionLaunch.showLaunchEls || missionLaunch.showElsTag) && (
                  <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-border/50">
                    {missionLaunch.showLaunchEls && missionLaunchBtn("els", t("v2.pipeline.actions.launchEls"))}
                    {missionLaunch.showElsTag && (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-success">
                        <CheckCircle2 size={13} /> {t("v2.pipeline.actions.elsLaunched")}{fmtMissionAt(missionLaunch.elsLaunchedAt)}
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>
          )}

          {/* ── Préparation ──
              Ce qui suit l'état des lieux, avant la remise sur le marché.
              Rubrique propre dans la spec : ce ne sont pas des données de
              l'ELS mais les suites qu'elle déclenche. Le transfert élec est un
              relevé de compteur pris au départ d'un locataire sortant — donc
              seulement dans un cycle ouvert (le certificat d'inspection
              électrique, lui, reste accessible via `inspectionElecValide`
              dans l'onglet "Le bien", indépendamment du cycle). `!sansEls`
              (signalé Thibaud 2026-09-15) : cohérent avec
              `elecFaitPourCloture` plus haut, qui traite déjà le transfert
              élec comme acquis dès que `sansEls` — la carte n'avait donc
              plus rien à faire remplir de toute façon. */}
          {cycleOuvert && showSection("etatDesLieux") && !sansEls && (
            <SectionCard title={t("biens.sections.preparation")} icon={Plug} columns={1} collapsible density="compact">
              <div className={GRID12}>
                {/* Migration 048 : la date de transfert élec vit dans sa colonne
                    dédiée `transfertElecDate`. L'ancien champ
                    `ancienPrestataireEls` — mal nommé, c'était un DateField
                    « Transfert élec fait le » avant le 2026-05-20 — a disparu
                    de la fiche ET de la liste blanche du formulaire ; la
                    migration 107 recopie ses dernières valeurs puis le supprime.

                    La saisie de la date lève AUSSI le drapeau `transfertElecFait`
                    (cf. `setTransfertElecDate`) : c'est lui, et lui seul, que
                    regardent les alertes « transfert en retard ». */}
                {/* Portée 4 et non 3 : « Transfert elec fait le » se tronquait
                    à 3 dès que la zone de contenu descendait sous ~800 px. */}
                {FS("transfertElecDate", 4, <DateField label={t("suivi.elecTransferDate")} value={data.transfertElecDate as string} onChange={setTransfertElecDate} maxDate={todayIso()} />)}
                {/* Sans ELS, personne ne passe constater l'état de l'appartement :
                    la question des photos n'a pas d'objet (spec 2026-08-07). */}
                {!sansEls && FS("appartPretPhotos", 4, <SelectField label={t("biens.appartPretPhotos")} value={boolToOption(data.appartPretPhotos)} onChange={setBool("appartPretPhotos")} options={OUI_NON_OPTIONS} />)}
              </div>
            </SectionCard>
          )}

          {/* ── PEB — branche visible seulement si le cycle la demande ──
              Historique : ouverte automatiquement par `isPebExpiringSoon`
              (2026-08-10), puis conditionnée à `cycleOuvert` en plus
              (2026-08-19) — retiré le 2026-09-12 car un bien neuf sans
              rotation locataire (donc sans `renonRecuLe`) n'avait alors AUCUN
              moyen d'encoder un certificat déjà valide en réalité.

              Revirement du 2026-09-14 : le certificat en vigueur (plus haut,
              carte "Certificat en vigueur") est désormais TOUJOURS éditable
              directement, cycle ou pas — donc l'impasse du 09-12 n'existe
              plus, cette carte-ci peut redevenir une décision explicite
              plutôt qu'un affichage automatique. `pebCycleVisible` (défini
              plus haut) : visible seulement si un cycle est ouvert ET que
              `pebRequis` n'a pas été décoché — décidé au même geste que
              `elsRequis` dans le dialogue d'ouverture de cycle. ELS et PEB
              sont deux process au même niveau (2026-09-15) : un cycle peut
              être PEB seul, via cette case, sans état des lieux de sortie. */}
          {pebCycleVisible && (
            <SectionCard title={t("biens.sections.pebRenouvellement")} icon={Zap} columns={1} collapsible density="compact">
              <div className={GRID12}>
                {FS("expertPeb", 4, <SelectField label={t("biens.expertPeb")} value={data.expertPeb} onChange={handleExpertPebChange} options={expertPebOptions} highlight={hl("expertPeb")} />)}
                {FS("statutPeb", 4, <SelectField label={t("biens.statutPeb")} value={pebStatut} onChange={handleStatutPebChange} options={[
                  { value: "", label: t("biens.statutPebOptions.aFixer") },
                  { value: "date_fixee", label: t("biens.statutPebOptions.dateFixee") },
                  { value: "date_passee", label: t("biens.statutPebOptions.rdvPasse") },
                  { value: "rapport_en_cours", label: t("biens.statutPebOptions.rapportEnCours") },
                  { value: "expertise_ok", label: t("biens.statutPebOptions.expertiseTerminee") },
                ]} />)}
                <div className="col-span-12 md:col-span-3">
                  <FieldGroup
                    legend={t("biens.groups.rdvLegend")}
                    units={[
                      { key: "pebDatePrevue", weight: 1.7, node: F("pebDatePrevue", <DateField label={t("biens.datePrevue")} value={data.pebDatePrevue} onChange={set("pebDatePrevue")} labelBelow />) },
                      { key: "pebHeure", weight: 1, node: F("pebHeure", <TimeField label={t("biens.heure")} value={data.pebHeure} onChange={set("pebHeure")} labelBelow />) },
                    ]}
                  />
                </div>
                {FS("pebCommentaire", 12, <TextAreaField label={t("auditPeb.commentairePeb")} value={data.pebCommentaire} onChange={set("pebCommentaire")} />)}

                {/* Le certificat qui REMPLACERA celui de l'onglet « Le bien »
                    une fois l'expertise terminée. */}
                <p className="col-span-12 text-[10.5px] font-semibold tracking-widest uppercase text-text-secondary border-t border-border pt-4 mt-1">{t("biens.nouveauCertificatPeb")}</p>
                {FS("nouvClassePeb", 2, <SelectField label={t("biens.pebClass")} value={data.nouvClassePeb} onChange={set("nouvClassePeb")} options={CLASSES_PEB} />)}
                {FS("nouvCertificatPeb", 3, <FormField label={t("biens.pebCertificate")} value={data.nouvCertificatPeb} onChange={set("nouvCertificatPeb")} />)}
                {FS("nouvPebFinLe", 3, <DateField label={t("biens.pebExpiryDate")} value={data.nouvPebFinLe} onChange={set("nouvPebFinLe")} />)}
                {/* Consommation et CO₂ sous une légende commune : « Conso en
                    kWhep/m²/an » ne tient dans aucune cellule étroite (mesuré
                    tronqué à la portée 2). Même groupe que la fiche vente, d'où
                    les clés `vente.groups.mesure*` réutilisées telles quelles. */}
                <div className="col-span-12 md:col-span-4">
                  <FieldGroup
                    legend={t("vente.groups.mesuresLegend")}
                    units={[
                      { key: "nouvConsoKwh", node: F("nouvConsoKwh", <FormField label={t("vente.groups.mesureConso")} value={data.nouvConsoKwh} onChange={set("nouvConsoKwh")} align="center" labelBelow />) },
                      { key: "nouvEmissionCo2", node: F("nouvEmissionCo2", <FormField label={t("vente.groups.mesureCo2")} value={data.nouvEmissionCo2} onChange={set("nouvEmissionCo2")} align="center" labelBelow />) },
                    ]}
                  />
                </div>
                {/* Signalé Thibaud 2026-09-12 : ces 2 champs n'avaient aucun
                    point de saisie pendant un renouvellement en cours — ils ne
                    redeviennent éditables sous « Le bien » → Énergie qu'une
                    fois le certificat promu (`renewalNeeded` repasse à faux),
                    avec leur ancienne valeur entre-temps figée. Miroirs
                    `nouvConsoTheorique`/`nouvChauffageEnergie`, promus en même
                    temps que le reste du certificat (cf. `promotePebCycle`). */}
                {FS("nouvConsoTheorique", 3, <FormField label={t("vente.fields.consoTheorique")} value={data.nouvConsoTheorique} onChange={set("nouvConsoTheorique")} align="center" />)}
                {FS("nouvChauffageEnergie", 4, <SelectField label={t("vente.fields.chauffageEnergie")} value={data.nouvChauffageEnergie} onChange={set("nouvChauffageEnergie")} options={CHAUFFAGE_ENERGIE} />)}
                {data.nouvPebCertificatPdf && (
                  <div className="col-span-12">
                    <a href={data.nouvPebCertificatPdf} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline">{t("auditPeb.openPdf")}</a>
                  </div>
                )}
                {/* Lancement manuel de la mission PEB — dans SON cadre, pas
                    celui de l'ELS (demande Thibaud 2026-08-19). Cf. `missionLaunch`. */}
                {missionLaunch && (missionLaunch.showLaunchPeb || missionLaunch.showPebTag) && (
                  <div className="col-span-12 flex flex-col gap-2 pt-4 mt-1 border-t border-border">
                    {missionLaunch.showLaunchPeb && missionLaunchBtn("peb", t("v2.pipeline.actions.launchPeb"))}
                    {missionLaunch.showPebTag && (
                      <div className="flex items-center gap-1.5 text-xs font-medium text-success">
                        <CheckCircle2 size={13} /> {t("v2.pipeline.actions.pebLaunched")}{fmtMissionAt(missionLaunch.pebLaunchedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {/* ── Clôture SRS ──
              Sans ELS, il n'y a pas de décompte de sortie à arrêter : la
              clôture financière disparaît du cycle (spec 2026-08-07) — et
              sans cycle ouvert, a fortiori. */}
          {cycleOuvert && showSection("finalisationSrs") && !sansEls && (
              <SectionCard
                title={t("biens.sections.clotureSrs")}
                icon={Wallet}
                columns={1}
                collapsible
                density="compact"
                actions={permsMode ? (
                  <PermVisibilityToggle
                    label={t("biens.sections.clotureSrs")}
                    itemKey={sectionStructuralKey("finalisationSrs")}
                    visible={permsEditor!.get(sectionStructuralKey("finalisationSrs")) !== "hidden"}
                    onChange={(v) => permsEditor!.set(sectionStructuralKey("finalisationSrs"), v ? "view" : "hidden")}
                  />
                ) : undefined}
              >
              <div className={GRID12}>
                {/* Checklist intro */}
                <div className="col-span-12 flex flex-col gap-2">
                  <p className="text-sm text-text-secondary">{t("biens.finalisationCheckTitle")}</p>
                  <ul className="flex flex-col gap-1 pl-4">
                    {(["finalisationCheck1", "finalisationCheck2", "finalisationCheck3", "finalisationCheck4"] as const).map((key) => (
                      <li key={key} className="text-sm text-text-muted list-disc">{t(`biens.${key}`)}</li>
                    ))}
                  </ul>
                </div>

                {/* Décompte réduit au total (décision 2026-08-10) : les six
                    montants détaillés n'étaient affichés nulle part depuis la
                    suppression des pages V1 en juillet, et zéro des 1574 biens
                    en portait un. `srs_historique` archivait des chaînes vides. */}
                {/* ⚠️ Ce champ était le DERNIER champ simple de la fiche à
                    garder l'ancien motif « droits » : un wrapper
                    `pointer-events-none select-none`. Il devenait alors
                    totalement inerte — pas de curseur, pas d'ouverture — tout
                    en gardant l'apparence exacte d'un champ éditable. Aucun
                    signal, donc : « le déroulant ne marche pas » (constaté par
                    Thibaud le 2026-08-10 en rôle collaborateur, qui a
                    `can_see` mais pas `can_edit` sur cette clé).
                    Il passe par `FS()` comme tous les autres : même géométrie,
                    et `readOnly` donne l'apparence verrouillée cohérente
                    (cf. PR #61, « la géométrie d'un champ ne dépend plus des
                    droits »). Restent sur l'ancien motif les seules GRILLES de
                    cases — `equipements` et `chargesIncluses` — qui ne sont pas
                    des champs uniques. */}
                {FS("indemnitePreavis", 6, <SelectField
                  label={t("biens.indemnitePreavisQ")}
                  value={data.indemnitePreavis}
                  onChange={set("indemnitePreavis")}
                  options={[
                    { value: "", label: "—" },
                    { value: "non", label: t("biens.indemnitePreavisOptions.non") },
                    { value: "1", label: t("biens.indemnitePreavisOptions.un") },
                    { value: "2", label: t("biens.indemnitePreavisOptions.deux") },
                    { value: "3", label: t("biens.indemnitePreavisOptions.trois") },
                  ]}
                />)}

                {/* Garantie + Total — le décompte, désormais, c'est ces deux
                    chiffres et le commentaire. */}
                {FS("garantieLiberee", 3, <SelectField label={t("biens.garantieLiberee")} value={data.garantieLiberee} onChange={set("garantieLiberee")} options={[
                  { value: "", label: "—" },
                  { value: "oui", label: t("common.yes") },
                  { value: "non", label: t("common.no") },
                ]} />)}
                {FS("totalSrs", 3, <FormField label={t("biens.totalSrs")} value={data.totalSrs} onChange={set("totalSrs")} />)}
                {FS("commentaireSrs", 12, <TextAreaField label={t("biens.commentaireSrsLabel")} value={data.commentaireSrs} onChange={set("commentaireSrs")} />)}

                {/* Action buttons — 2 étapes :
                    1. Le collaborateur clôture (dossierCloture=true) quand
                       indemnité + garantie sont OK.
                    2. Le C-Level / admin valide la fermeture (dossierClos=true)
                       une fois la clôture faite.
                    Une fois l'étape franchie, le bouton devient un badge vert
                    passif "✓ Fait" non cliquable.

                    ⚠️ Chaque bouton est gardé par le droit d'écriture de LA
                    colonne qu'il écrit. Sans cette garde, un rôle sans droit
                    voyait un bouton actif dont le clic était rejeté par la RPC
                    (`update_entity_with_perms` filtre sur `field_permissions`)
                    — un échec sans cause lisible pour l'utilisateur.
                    L'étape 2 se gardait déjà, mais par un test de rôle en dur
                    (`admin || c_level`) : on lit désormais le droit réel, ce
                    qui donne exactement les mêmes rôles aujourd'hui et suivra
                    la matrice si elle change. */}
                {/* Pourquoi le bouton est verrouillé.
                    La condition n'est PAS « les deux questions sont
                    répondues » : l'indemnité accepte n'importe quelle réponse,
                    « non » compris, mais la garantie doit valoir « oui » — on
                    ne clôt pas un dossier tant que la garantie locative n'est
                    pas rendue (règle confirmée par Thibaud le 2026-08-10).
                    Cette asymétrie est invisible : un bouton grisé sans motif
                    se lit comme une panne. On affiche donc ce qui manque. */}
                {!data.dossierCloture && canEdit("dossierCloture") && (() => {
                  const manquants = [
                    !data.indemnitePreavis ? t("biens.clotureBloquee.indemnite") : null,
                    data.garantieLiberee !== "oui" ? t("biens.clotureBloquee.garantie") : null,
                    !elecFaitPourCloture ? t("biens.clotureBloquee.elec") : null,
                  ].filter(Boolean);
                  if (manquants.length === 0) return null;
                  return (
                    <p className="col-span-12 -mb-1 flex items-start gap-1.5 text-xs text-text-muted">
                      <AlertTriangle size={13} className="shrink-0 mt-0.5 text-warning" />
                      <span>{manquants.join(" · ")}</span>
                    </p>
                  );
                })()}

                <div className="col-span-12 flex items-center gap-3 pt-2">
                  {data.dossierCloture ? (
                    <Button variant="secondary" disabled>
                      ✓ {t("biens.dossierClotureFait")}
                    </Button>
                  ) : canEdit("dossierCloture") && (
                    <Button
                      variant="primary"
                      disabled={!data.indemnitePreavis || data.garantieLiberee !== "oui" || !elecFaitPourCloture || isSavingUpdate}
                      onClick={() => {
                        saveBien({ dossierCloture: true }, {
                          onSuccess: () => {
                            setData((prev) => ({ ...prev, dossierCloture: true }));
                            toast.success(t("biens.dossierClotureSuccess"));
                          },
                          // Pas de onError local : useUpdateBien porte
                          // meta.toastOnError, le toast est émis globalement
                          // (cf. src/lib/queryClient.ts). En doubler un ici
                          // afficherait deux toasts pour la même erreur.
                        });
                      }}
                    >
                      {t("biens.cloturerDossier")}
                    </Button>
                  )}
                  {/* Le badge « ✓ Dossier fermé » est un ÉTAT, pas une action :
                      il s'affiche pour tout le monde. Seul le bouton qui écrit
                      la colonne est gardé. Avant, la garde englobait les deux,
                      donc un collaborateur ne pouvait pas savoir que le dossier
                      était fermé. Même forme que l'étape 1 juste au-dessus. */}
                  {data.dossierClos ? (
                    <Button variant="secondary" disabled>
                      ✓ {t("biens.dossierClosFait")}
                    </Button>
                  ) : canEdit("dossierClos") && (
                      // Bouton vert (pas `variant="secondary"` du composant
                      // partagé, gris) : c'est une action ENCORE À FAIRE par
                      // le C-Level, pas un état passif — doit se distinguer
                      // du badge "✓ Fait" ci-dessus (signalé Thibaud
                      // 2026-08-20 : "c'est une action à faire... pas encore
                      // faite"). `cn()` ne fusionne pas les classes en conflit
                      // (= clsx, cf. bug Modal.tsx documenté) → bouton brut
                      // plutôt qu'un override de className sur `<Button>`.
                      <button
                        type="button"
                        disabled={!data.dossierCloture || isSavingUpdate}
                        onClick={() => {
                          saveBien({ dossierClos: true }, {
                            onSuccess: () => {
                              setData((prev) => ({ ...prev, dossierClos: true }));
                              toast.success(t("biens.dossierClosSuccess"));
                            },
                            // Toast d'erreur émis globalement (meta.toastOnError).
                          });
                        }}
                        className={cn(
                          "inline-flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium rounded-md outline-none shadow-none transition-colors",
                          // Vert plein UNIQUEMENT quand cliquable — le fondu à
                          // 50% d'opacité (disabled:opacity-50, patron partagé
                          // par <Button>) rendait un vert délavé illisible tant
                          // que l'étape 1 n'était pas faite (signalé Thibaud
                          // 2026-08-20 : "le vert n'est pas bon"). Gris explicite
                          // à la place, même patron que le badge "✓ Fait".
                          !data.dossierCloture || isSavingUpdate
                            ? "bg-gray-200 text-text-muted cursor-not-allowed"
                            : "bg-success text-white hover:bg-success/90 cursor-pointer",
                        )}
                      >
                        {t("biens.dossierCloture")}
                      </button>
                    )}
                </div>
              </div>
              </SectionCard>
          )}

          {/* ── Suivi système — affiché, jamais saisi ──
              Les six horodatages que le cycle pose tout seul : lancement des
              missions et refus des prestataires. Ils existaient en base et
              pilotaient déjà des bannières, mais n'étaient visibles nulle part
              sur la fiche — un C-Level ne pouvait pas répondre à « quand la
              mission est-elle partie, et qui a refusé ? » sans ouvrir la DB.
              Lecture SEULE : ces colonnes sont écrites par l'edge function et
              par l'espace prestataire, jamais à la main. */}
          {cycleOuvert && (() => {
            const b = bien as unknown as BienRow;
            const rows: { key: string; label: string; value: string }[] = [
              { key: "elsMissionLanceeAt", label: t("biens.suiviSysteme.elsLanceeLe"), value: fmtStamp(b.elsMissionLanceeAt) },
              { key: "pebMissionLanceeAt", label: t("biens.suiviSysteme.pebLanceeLe"), value: fmtStamp(b.pebMissionLanceeAt) },
              { key: "prestataireEleRefuseAt", label: t("biens.suiviSysteme.refusElsDate"), value: fmtStamp(b.prestataireEleRefuseAt) },
              { key: "prestataireEleRefusePar", label: t("biens.suiviSysteme.refusElsPar"), value: resolveProfileName(b.prestataireEleRefusePar) },
              { key: "expertPebRefuseAt", label: t("biens.suiviSysteme.refusPebDate"), value: fmtStamp(b.expertPebRefuseAt) },
              { key: "expertPebRefusePar", label: t("biens.suiviSysteme.refusPebPar"), value: resolveProfileName(b.expertPebRefusePar) },
            ];
            // En mode droits : bloc TOUJOURS rendu, en cellules de réglage.
            // Ces six colonnes ne sont jamais saisies, mais leur VISIBILITÉ se
            // décide — un collaborateur doit-il voir qui a refusé la mission ?
            // Sans ça, ces droits ne seraient réglables depuis aucun écran.
            if (permsMode) {
              return (
                <SectionCard title={t("biens.sections.suiviSysteme")} icon={Activity} columns={1} collapsible density="compact">
                  <div className={GRID12}>
                    {rows.map((r) => (
                      <div key={r.key} className="col-span-12 md:col-span-4">{PS(r.key, r.label)}</div>
                    ))}
                  </div>
                </SectionCard>
              );
            }
            const visibles = rows.filter((r) => isFieldVisible(r.key, canSee, presetActive ? presetVisible : null));
            // Aucun horodatage posé ET rien de visible → le bloc ne s'affiche
            // pas : un cycle qui vient de s'ouvrir n'a rien à raconter.
            if (visibles.length === 0 || visibles.every((r) => !r.value)) return null;
            return (
              <SectionCard title={t("biens.sections.suiviSysteme")} icon={Activity} columns={1} collapsible density="compact">
                <div className={GRID12}>
                  {visibles.map((r) => (
                    <div key={r.key} className="col-span-6 md:col-span-2">
                      <div className="text-[10.5px] font-semibold tracking-widest uppercase text-text-muted mb-1">{r.label}</div>
                      <div className="text-sm text-text-primary">{r.value || "—"}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            );
          })()}
        </>
        )}

        {/* ═══════════════ ONGLET 4 — COMMERCIALISATION ═══════════════
            Rythme propre, indépendant du cycle ELS (axe 2 de bienLifecycle).
            Vitrine + acte de publier : les valeurs financières de référence sont
            remontées dans « Le bien ». */}
        {activeTab === "commercialisation" && (
        <>
        <CardGrid>
          <CardGridItem>
            <SectionCard title={t("biens.sections.pointsForts")} icon={Sparkles} columns={1} collapsible>
              {F("pointsForts", <TextAreaField label={t("biens.pointsFortsLabel")} value={data.pointsForts} onChange={set("pointsForts")} />)}
            </SectionCard>
          </CardGridItem>
          <CardGridItem>
            <SectionCard title={t("common.publication")} icon={Globe} collapsible>
              {F("statutPublication", <SelectField label={t("biens.statutPublication")} value={data.statutPublication} onChange={onStatutPublicationChange} options={[{ value: "Brouillon", label: t("biens.statutPublicationOptions.brouillon") }, { value: "Publié", label: t("biens.statutPublicationOptions.publie") }, { value: "Retiré", label: t("biens.statutPublicationOptions.retire") }]} />)}
              {/* Bouton Publier dans la colonne droite, meme rangee que le
                  dropdown Statut publication (user 2026-05-27). Bouton seul,
                  align-end pour suivre la hauteur du dropdown. */}
              {canTriggerPortals && (
                <PublicationPublishButton
                  bien={{
                    ...data,
                    id: bien.id,
                    updated_at: bien.updated_at,
                    last_published_at: bien.last_published_at,
                    content_updated_at: bien.content_updated_at,
                  } as unknown as Bien}
                  onPublishClick={handlePublishClick}
                  isPending={isMarkingPublished}
                />
              )}
              {/* Statut Immoweb réel (portal_publications) — demande Thibaud
                  2026-09-16 : on ne voyait pas un REJECTED sans aller en base. */}
              {canTriggerPortals && (
                <div className="col-span-2">
                  <ImmowebStatusLine
                    bienId={bien.id}
                    codeImmoweb={bien.codeImmoweb}
                    statutPublication={data.statutPublication}
                    deactivating={isPortalPending}
                    onDeactivate={() =>
                      publishPortal(
                        { portal: "immoweb", action: "deactivate" },
                        { onError: (e) => toast.error(`Immoweb : ${e instanceof Error ? e.message : String(e)}`) },
                      )
                    }
                  />
                </div>
              )}
              {/* Banniere "Modifs non poussees" : col-span-2 sous la rangee
                  dropdown/bouton. Rend null si pas stale (zero footprint). */}
              {canTriggerPortals && (
                <div className="col-span-2">
                  <PublicationStaleBanner
                    bien={{
                      ...data,
                      id: bien.id,
                      updated_at: bien.updated_at,
                      last_published_at: bien.last_published_at,
                      content_updated_at: bien.content_updated_at,
                    } as unknown as Bien}
                  />
                </div>
              )}
              {F("photosOk", <SelectField label={t("biens.photosOk")} value={data.photosOk} onChange={set("photosOk")} options={[{ value: "", label: "—" }, { value: "pas_de_photo", label: t("biens.photosOptions.pas_de_photo") }, { value: "suffisant", label: t("biens.photosOptions.suffisant") }, { value: "bien", label: t("biens.photosOptions.bien") }, { value: "tres_bien", label: t("biens.photosOptions.tres_bien") }]} />)}
              {F("videoOk", <SelectField label={t("biens.videoOk")} value={data.videoOk} onChange={set("videoOk")} options={[{ value: "", label: "—" }, { value: "pas_de_video", label: t("biens.videoOptions.pas_de_video") }, { value: "suffisant", label: t("biens.videoOptions.suffisant") }, { value: "bien", label: t("biens.videoOptions.bien") }, { value: "tres_bien", label: t("biens.videoOptions.tres_bien") }]} />)}
              {F("textesOk", <Checkbox label={t("biens.textesOk")} checked={!!data.textesOk} onChange={toggle("textesOk")} />)}
              {F("alerteTexte", <DateField label={t("biens.alerteTexte")} value={data.alerteTexte} onChange={set("alerteTexte")} />)}
              {/* « Sous option » — overlay de mise en ligne (le bien est retiré du
                  marché sans quitter son cycle), d'où sa place ici et non dans
                  l'en-tête à côté de la disponibilité (rubrique « Mise en ligne »
                  de la spec). Ne s'applique qu'à un bien en location DISPONIBLE. */}
              {/* `sousOption` a sa propre mise en page (case + aide + date
                  « depuis »), donc hors entonnoir `F()`. En mode droits, on la
                  rend en cellule ET sans la condition `transaction`, sinon le
                  bien support déciderait de ce qui est réglable. */}
              {permsMode && <div className="col-span-2 max-w-xs">{PS("sousOption", t("biens.sousOption.label"))}</div>}
              {!permsMode && isFieldVisible("sousOption", canSee, presetActive ? presetVisible : null) && data.transaction === "location" && (() => {
                const enabled = canEdit("sousOption") && data.disponibilite === "disponible";
                const active = !!(data as { sousOption?: boolean | null }).sousOption;
                const depuis = (data as { sousOptionDepuis?: string | null }).sousOptionDepuis || "";
                const fmtDate = (iso: string) => {
                  const [y, m, d] = iso.split("-");
                  return d && m && y ? `${d}/${m}/${y}` : iso;
                };
                return (
                  <div
                    className={cn(
                      "col-span-2 [&_span]:text-xs [&_span]:leading-tight",
                      !enabled && "opacity-50 pointer-events-none",
                    )}
                    title={!enabled ? t("biens.sousOption.lockedHint") : undefined}
                  >
                    <Checkbox
                      checked={active}
                      onChange={(c) => { if (enabled) onToggleSousOption(c); }}
                      label={t("biens.sousOption.label")}
                    />
                    <p className="text-xs text-text-secondary mt-1 pl-6 leading-tight">{t("biens.sousOption.help")}</p>
                    {active && (
                      <div className="mt-2 pl-6">
                        <div className="text-xs font-medium tracking-wider uppercase text-text-muted mb-1">{t("biens.sousOption.since")}</div>
                        <div className="text-sm text-text-primary">{depuis ? fmtDate(depuis) : "—"}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </SectionCard>
          </CardGridItem>
        </CardGrid>

        {/* DESCRIPTIONS FR + NL + EN — rotation par langue UI */}
        <DescriptionCards
          bienId={bien.id}
          uiLang={i18n.language}
          data={data}
          set={set}
          F={F}
          canEdit={canEdit}
          t={t}
        />

        {/* MÉDIAS */}
        <SectionCard title={t("biens.sections.medias")} icon={Image} columns={1} collapsible>
          <div className="flex flex-col gap-4">
            {F("videoYoutubeUrl", <FormField label={t("biens.videoYoutubeUrl")} value={data.videoYoutubeUrl} onChange={set("videoYoutubeUrl")} placeholder="https://youtube.com/watch?v=..." />)}
            {/* La galerie téléverse dans le bucket : hors de question de la
                rendre en mode droits, un glisser-déposer malheureux écrirait
                sur le bien support. */}
            {permsMode && <div className="max-w-xs">{PS("photos", t("biens.sections.medias"))}</div>}
            {!permsMode && isFieldVisible("photos", canSee, presetActive ? presetVisible : null) && (
              <PhotoGallery
                bienId={bien.id}
                value={data.photos as import("@/types/bien").BienPhoto[]}
                onChange={(next) => {
                  setData((prev) => ({ ...prev, photos: next }));
                  setHasEdited(true);
                  setSaveFeedback("idle");
                }}
                readOnly={!canEdit("photos")}
              />
            )}
          </div>
        </SectionCard>
        </>
        )}

        {/* ═══════════════ ONGLET 5 — HISTORIQUE ═══════════════
            Aucun champ éditable : lecture seule sur les cycles clos
            (`srs_historique`). Branchement dans la passe dédiée. */}
        {activeTab === "historique" && (
          <SectionCard title={t("biens.ficheTabs.historique")} icon={History} columns={1}>
            <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-center">
              <History size={22} className="text-text-muted" />
              <p className="text-sm font-medium text-text-secondary">{t("biens.historiqueCycles.title")}</p>
              <p className="text-xs text-text-muted max-w-md">{t("biens.historiqueCycles.hint")}</p>
            </div>
          </SectionCard>
        )}

      </div>
      )}



      {/* Confirmation Publié → Brouillon/Retiré (le bien sort de l'API vendor) */}
      <Modal
        isOpen={pendingDepublishStatut !== null}
        onClose={cancelDepublish}
        title={t("portals.depublishConfirmTitle")}
      >
        <p className="text-sm text-text-primary mb-5">
          {pendingDepublishStatut === "Brouillon"
            ? t("portals.depublishConfirmBodyBrouillon")
            : t("portals.depublishConfirmBodyRetire")}
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={cancelDepublish}>
            {t("common.cancel")}
          </Button>
          <button
            type="button"
            onClick={confirmDepublish}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-warning hover:bg-warning/90 rounded-md cursor-pointer outline-none shadow-none transition-colors"
          >
            <span>{t("portals.depublishConfirmAction")}</span>
          </button>
        </div>
      </Modal>

      {/* Confirmation « cycle sans état des lieux de sortie ».
          Décocher n'est pas une saisie comme une autre : ça retire la clôture
          financière et la question photos du cycle, et ça sort le collaborateur
          des champs obligatoires. On laisse donc une porte de sortie. */}
      <Modal
        isOpen={elsRequisConfirmOpen}
        onClose={() => setElsRequisConfirmOpen(false)}
        title={t("biens.elsRequis.confirmTitle")}
      >
        <p className="text-sm text-text-primary mb-3">{t("biens.elsRequis.confirmBody")}</p>
        <p className="text-sm text-warning mb-4 flex items-start gap-1.5">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <span>{t("biens.elsRequis.confirmWarning")}</span>
        </p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setElsRequisConfirmOpen(false)}>
            {t("common.cancel")}
          </Button>
          <button
            type="button"
            onClick={() => {
              setElsRequis(false);
              setElsRequisConfirmOpen(false);
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-warning hover:bg-warning/90 rounded-md cursor-pointer outline-none shadow-none transition-colors"
          >
            <span>{t("biens.elsRequis.confirmAction")}</span>
          </button>
        </div>
      </Modal>

      {/* ── Dialogue d'ouverture de cycle ──
          Le geste unique : les trois dates, la décision « suivi ELS », le
          collaborateur, et l'expert PEB quand le certificat est à refaire.
          Tout est local jusqu'à la validation, d'où un seul autosave.

          Valider pose `cycleOuvertLe` (migration 135) : c'est ce fait, et lui
          seul, qui définit un cycle ouvert. La date de renon n'est obligatoire
          que si un ELS est demandé — un cycle PEB seul (bien vide, sans
          locataire sortant) n'en a pas (Phase 2, 2026-09-15). Depuis un bien
          LOUÉ, valider enchaîne aussi le passage en disponible (décision Q4). */}
      <Modal
        isOpen={ouvrirCycleOpen}
        onClose={() => setOuvrirCycleOpen(false)}
        title={t("biens.ouvrirCycle.title")}
        className="max-w-2xl"
      >
        <p className="text-sm text-text-secondary mb-4">{t("biens.ouvrirCycle.intro")}</p>
        {/* Depuis LOUÉ : on dit ce que le geste va faire en plus (archivage du
            locataire, fiche de sortie à neuf) — et on reprend le garde-fou SRS
            de la modale « Passer en disponible » si un dossier finalisé n'a
            pas encore été validé par le C-Level. */}
        {cycleFromLoue && (
          <div className="mb-4 rounded-md border border-warning/40 bg-warning/10 p-3 flex flex-col gap-2">
            <p className="text-sm text-text-primary">{t("biens.ouvrirCycle.fromLoueNotice")}</p>
            {data.dossierCloture && !data.dossierClos && (
              <p className="text-sm text-text-primary">{t("biens.dispoLibere.srsWarning")}</p>
            )}
          </div>
        )}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-3">
            <DateField
              label={t("biens.groups.cycleRenon")}
              value={cycleDraft.renonRecuLe}
              onChange={(v) => setCycleDraft((d) => ({ ...d, renonRecuLe: v }))}
            />
            <DateField
              label={t("biens.groups.cycleFinContrat")}
              value={cycleDraft.finDeContrat}
              onChange={(v) => setCycleDraft((d) => ({ ...d, finDeContrat: v }))}
            />
            <DateField
              label={t("biens.groups.cycleLibreLe")}
              value={cycleDraft.seraDispoPour}
              onChange={(v) => setCycleDraft((d) => ({ ...d, seraDispoPour: v }))}
            />
          </div>
          <p className="text-xs text-text-muted -mt-2 leading-tight">{t("biens.ouvrirCycle.renonHint")}</p>

          <div>
            <Checkbox
              label={t("biens.elsRequis.label")}
              checked={cycleDraft.elsRequis}
              onChange={(c) => setCycleDraft((d) => ({ ...d, elsRequis: c }))}
            />
            <p className="text-xs text-text-muted mt-1 pl-6 leading-tight">{t("biens.ouvrirCycle.elsRequisHint")}</p>
          </div>

          {/* Trace informative (qui a fait l'état des lieux d'entrée du
              locataire actuel) — modifiable ici pour la corriger/compléter,
              mais n'assigne rien : l'assignation réelle (Prestataire assigné)
              se fait plus tard par le collaborateur, via Pipeline ELS (demande
              Thibaud 2026-08-19). Le collaborateur, lui, est plus bas : il
              concerne l'ELS ET le PEB. */}
          {cycleDraft.elsRequis && (
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                label={t("biens.prestataireEls")}
                value={cycleDraft.prestataireEls}
                onChange={(v) => setCycleDraft((d) => ({ ...d, prestataireEls: v }))}
                options={elsOptions}
              />
            </div>
          )}
          {cycleDraft.elsRequis && (
            <p className="text-xs text-text-muted -mt-2 leading-tight">{t("biens.ouvrirCycle.prestataireElsHint")}</p>
          )}

          {/* Jumeau PEB de "ELS requis" (migration 131, spec Thibaud
              2026-09-14) : décision explicite prise au même geste, plus un
              déclenchement automatique sur `renewalNeeded`. Le motif reste
              affiché comme indice quand le certificat semble à refaire, mais
              ne conditionne plus rien à lui seul. */}
          <div>
            <Checkbox
              label={t("biens.pebRequis.label")}
              checked={cycleDraft.pebRequis}
              onChange={(c) => setCycleDraft((d) => ({ ...d, pebRequis: c }))}
            />
            <p className="text-xs text-text-muted mt-1 pl-6 leading-tight">{t("biens.ouvrirCycle.pebRequisHint")}</p>
            {/* Justifie le défaut de la case juste au-dessus (pré-rempli sur
                `isPebExpiringSoon`, cf. `openCycleDialog`) : sans cette date,
                un décoché par défaut peut passer pour un oubli plutôt qu'un
                calcul (signalé Thibaud 2026-09-15). */}
            {data.pebFinLe && (
              <p className="text-xs text-text-muted mt-1 pl-6 leading-tight">
                {t("biens.ouvrirCycle.pebValidUntil", {
                  date: formatDate(data.pebFinLe, i18n.language, { day: "2-digit", month: "2-digit", year: "numeric" }),
                })}
              </p>
            )}
            {renewalNeeded && (
              <p className="text-xs text-warning mt-1 pl-6 flex items-start gap-1.5">
                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                <span>{t("biens.ouvrirCycle.pebMotif")}</span>
              </p>
            )}
          </div>
          {cycleDraft.pebRequis && (
            <SelectField
              label={t("biens.expertPeb")}
              value={cycleDraft.expertPeb}
              onChange={(v) => setCycleDraft((d) => ({ ...d, expertPeb: v }))}
              options={expertPebOptions}
            />
          )}

          {/* Responsable du dossier en interne — dès qu'un process est demandé,
              ELS ou PEB (décision Thibaud 2026-09-15 soir). Pas bloquant à la
              validation : sans lui, le cycle reste « à compléter » (stage
              draft_cycle) et n'entre pas dans le Pipeline. */}
          {(cycleDraft.elsRequis || cycleDraft.pebRequis) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <SelectField
                  label={t("biens.collaborateurSrs")}
                  value={cycleDraft.collaborateurSrs}
                  onChange={(v) => setCycleDraft((d) => ({ ...d, collaborateurSrs: v }))}
                  options={collaborateurOptions}
                />
                <p className="text-xs text-text-muted mt-1 leading-tight">{t("biens.ouvrirCycle.collaborateurHint")}</p>
              </div>
            </div>
          )}

          {/* Désigner ne lance rien : le mail et la visibilité prestataire
              restent derrière le bouton « Lancer la mission » (chantier du
              11 juillet). On le dit ici pour que l'absence d'effet ne passe
              pas pour un bug. */}
          <p className="text-xs text-text-muted leading-tight">{t("biens.ouvrirCycle.lancementHint")}</p>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button variant="secondary" size="sm" onClick={() => setOuvrirCycleOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={cycleDraft.elsRequis && !cycleDraft.renonRecuLe}
            onClick={confirmOuvrirCycle}
          >
            {t(cycleFromLoue ? "biens.ouvrirCycle.confirmFromLoue" : "biens.ouvrirCycle.confirm")}
          </Button>
        </div>
      </Modal>

      {/* Confirmation mise sous option (dépublication réversible) */}
      <Modal
        isOpen={sousOptionConfirmOpen}
        onClose={() => setSousOptionConfirmOpen(false)}
        title={t("biens.sousOption.confirmTitle")}
      >
        <p className="text-sm text-text-primary mb-3">{t("biens.sousOption.confirmBody")}</p>
        {data.statutPublication === "Publié" && (
          <p className="text-sm text-warning mb-4 flex items-start gap-1.5">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <span>{t("biens.sousOption.confirmDepublishWarning")}</span>
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setSousOptionConfirmOpen(false)}>
            {t("common.cancel")}
          </Button>
          <button
            type="button"
            onClick={confirmSousOption}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-warning hover:bg-warning/90 rounded-md cursor-pointer outline-none shadow-none transition-colors"
          >
            <span>{t("biens.sousOption.confirmAction")}</span>
          </button>
        </div>
      </Modal>

      {/* Confirmation levée de la mise sous option (remise en ligne) */}
      <Modal
        isOpen={sousOptionUncheckOpen}
        onClose={() => setSousOptionUncheckOpen(false)}
        title={t("biens.sousOption.uncheckTitle")}
      >
        <p className="text-sm text-text-primary mb-3">{t("biens.sousOption.uncheckBody")}</p>
        {!!(data as { sousOptionStatutAvant?: string | null }).sousOptionStatutAvant && (
          <p className="text-sm text-accent mb-4 flex items-start gap-1.5">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <span>{t("biens.sousOption.uncheckRepublishWarning")}</span>
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setSousOptionUncheckOpen(false)}>
            {t("common.cancel")}
          </Button>
          <button
            type="button"
            onClick={confirmUnsetSousOption}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-accent-foreground bg-accent hover:bg-accent-hover rounded-md cursor-pointer outline-none shadow-none transition-colors"
          >
            <span>{t("biens.sousOption.uncheckAction")}</span>
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={pendingLoueVendu !== null}
        onClose={cancelLoueVendu}
        title={
          pendingLoueVendu === "loue"
            ? t("biens.passageLoue.title")
            : t("portals.loueVenduDepublishTitle")
        }
      >
        {pendingLoueVendu === "loue" ? (() => {
          const canConfirmLoue = loueForm.nom.trim().length > 0 && loueForm.dateDebut.length > 0;
          return (
            <>
              <p className="text-sm text-text-primary mb-3">{t("biens.passageLoue.body")}</p>
              {data.statutPublication === "Publié" && (
                <p className="text-sm text-warning mb-3 flex items-start gap-1.5">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                  <span>{t("portals.loueDepublishWarning")}</span>
                </p>
              )}
              {hasExitCycleInFlight(data as unknown as BienRow) && (
                <p className="text-sm text-accent mb-4 flex items-start gap-1.5">
                  <ClipboardCheck size={15} className="shrink-0 mt-0.5" />
                  <span>{t("biens.passageLoue.sortieEnCoursNote")}</span>
                </p>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 mb-4">
                <FormField label={t("common.name")} value={loueForm.nom} onChange={(v) => setLoueForm((f) => ({ ...f, nom: v }))} />
                <DateField label={t("biens.passageLoue.dateDebutBail")} value={loueForm.dateDebut} onChange={(v) => setLoueForm((f) => ({ ...f, dateDebut: v }))} />
                <FormField label={t("common.email")} value={loueForm.email} onChange={(v) => setLoueForm((f) => ({ ...f, email: v }))} />
                <FormField label={t("common.phone")} value={loueForm.tel} onChange={(v) => setLoueForm((f) => ({ ...f, tel: v }))} />
              </div>
              {/* Assignation explicite à CE moment précis : collaborateurSrs
                  n'est sinon jamais remis à zéro (elsResetCycleForLoue ne le
                  touche pas) et traînerait silencieusement d'un locataire au
                  suivant sans confirmation (demande user 2026-08-19). */}
              <div className="mb-2">
                <SelectField
                  label={t("biens.passageLoue.collaborateurResponsable")}
                  value={loueForm.collaborateurSrs}
                  onChange={(v) => setLoueForm((f) => ({ ...f, collaborateurSrs: v }))}
                  options={collaborateurOptions}
                />
              </div>
              <p className="text-xs text-text-muted mb-5 px-3">{t("biens.passageLoue.requiredHint")}</p>
              <div className="flex items-center justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={cancelLoueVendu}>
                  {t("common.cancel")}
                </Button>
                <button
                  type="button"
                  onClick={confirmLoueVendu}
                  disabled={!canConfirmLoue}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-md outline-none shadow-none transition-colors",
                    canConfirmLoue
                      ? "bg-warning hover:bg-warning/90 cursor-pointer"
                      : "bg-warning/40 cursor-not-allowed",
                  )}
                >
                  <span>{t("biens.passageLoue.action")}</span>
                </button>
              </div>
            </>
          );
        })() : (
          <>
            <p className="text-sm text-text-primary mb-5 whitespace-pre-line">
              {t("portals.loueVenduDepublishBody")}
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={cancelLoueVendu}>
                {t("common.cancel")}
              </Button>
              <button
                type="button"
                onClick={confirmLoueVendu}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-warning hover:bg-warning/90 rounded-md cursor-pointer outline-none shadow-none transition-colors"
              >
                <span>{t("portals.loueVenduDepublishAction")}</span>
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Confirmation LOUÉ → DISPONIBLE : archivage du locataire + cycle de
          sortie remis à neuf (le nettoyage vit ICI depuis 2026-07-10, plus au
          passage en loué). */}
      <Modal
        isOpen={dispoLibereOpen}
        onClose={() => setDispoLibereOpen(false)}
        title={t("biens.dispoLibere.title")}
      >
        <p className="text-sm text-text-primary mb-5 whitespace-pre-line">{t("biens.dispoLibere.body")}</p>
        {/* Garde-fou clôture SRS : le collab a finalisé (dossierCloture) mais le
            C-Level n'a pas validé (dossierClos). Libérer maintenant archiverait
            le dossier en non clôturé (reset_exit_cycle_on_libere / snapshot 085)
            → perte de la validation. On avertit + on propose de clôturer d'abord. */}
        {data.dossierCloture && !data.dossierClos && (
          <div className="mb-5 rounded-md border border-warning/40 bg-warning/10 p-3">
            <p className="text-sm text-text-primary">{t("biens.dispoLibere.srsWarning")}</p>
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDispoLibereOpen(false)}>
            {t("common.cancel")}
          </Button>
          {data.dossierCloture && !data.dossierClos && (role === "admin" || role === "c_level") && (
            <Button
              variant="primary"
              size="sm"
              disabled={isSavingUpdate}
              onClick={() => {
                saveBien({ dossierClos: true }, {
                  onSuccess: () => {
                    setData((prev) => ({ ...prev, dossierClos: true }));
                    toast.success(t("biens.dossierClosSuccess"));
                    setDispoLibereOpen(false);
                  },
                  // Toast d'erreur émis globalement (meta.toastOnError).
                });
              }}
            >
              {t("biens.dispoLibere.validateFirst")}
            </Button>
          )}
          <button
            type="button"
            onClick={confirmDispoLibere}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-warning hover:bg-warning/90 rounded-md cursor-pointer outline-none shadow-none transition-colors"
          >
            <span>{t(data.dossierCloture && !data.dossierClos ? "biens.dispoLibere.freeAnyway" : "biens.dispoLibere.action")}</span>
          </button>
        </div>
      </Modal>

      {/* Confirmation « vider la fiche de sortie » (rattrapage d'erreur de manip) */}
      <Modal
        isOpen={viderSortieOpen}
        onClose={() => setViderSortieOpen(false)}
        title={t("biens.viderSortie.title")}
      >
        <p className="text-sm text-text-primary mb-5">{t("biens.viderSortie.body")}</p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setViderSortieOpen(false)}>
            {t("common.cancel")}
          </Button>
          <button
            type="button"
            onClick={confirmViderSortie}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-danger hover:bg-danger/90 rounded-md cursor-pointer outline-none shadow-none transition-colors"
          >
            <span>{t("biens.viderSortie.action")}</span>
          </button>
        </div>
      </Modal>

      <Modal isOpen={deleteOpen} onClose={() => !isDeleting && setDeleteOpen(false)} title={t("biens.deleteTitle")}>
        <p className="text-sm text-text-primary mb-5">{t("biens.deleteConfirm")}</p>
        {deleteError && (
          <p className="text-xs text-danger mb-4">{deleteError}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
            {t("common.cancel")}
          </Button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-danger hover:bg-danger/90 rounded-md cursor-pointer outline-none shadow-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            <span>{t("common.delete")}</span>
          </button>
        </div>
      </Modal>

      {/* Confirmation au changement de "Prestataire assigné" (annulation de l'ancien). */}
      {eleChangeTarget !== null && (() => {
        const oldName = eleOptions.find((o) => o.value === data.prestataireEle)?.label || data.prestataireEle;
        const hasDate = !!data.edlDatePrevue;
        const datePassed = hasDate && String(data.edlDatePrevue) < todayIso();
        const done = data.statutEle === "expertise_ok";
        const stateKey = done ? "done" : !hasDate ? "noDate" : datePassed ? "passed" : "future";
        return (
          <Modal isOpen onClose={() => setEleChangeTarget(null)} title={t("biens.changeEle.title")}>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-text-primary">
                {t(`biens.changeEle.${stateKey}`, { name: oldName, date: data.edlDatePrevue, time: data.edlHeure || "" })}
              </p>
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="secondary" size="sm" onClick={() => setEleChangeTarget(null)}>
                  {t("common.cancel")}
                </Button>
                <Button variant="primary" size="sm" onClick={() => { applyEleChange(eleChangeTarget, true); setEleChangeTarget(null); }}>
                  {t("common.confirm")}
                </Button>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// ─── DescriptionCards ──────────────────────────────────────────────────────
//
// Trois cards (FR/NL/EN) avec rotation par langue UI. La langue de
// l'interface est primaire (bouton "Générer" = appel Anthropic depuis les
// données structurées du bien). Les 2 autres ont un bouton "Traduire" qui
// part de la langue primaire vers la langue cible.
//
// État local (pas du form auto-save) :
//   - aiBusy : la card en cours d'appel (null = aucune)
//   - aiError : message d'erreur global, reset à chaque tentative

type DescLang = "fr" | "nl" | "en";
const DESC_LANGS: DescLang[] = ["fr", "nl", "en"];

function fieldKeys(lang: DescLang): { titre: "titreFr" | "titreNl" | "titreEn"; description: "descriptionFr" | "descriptionNl" | "descriptionEn" } {
  if (lang === "fr") return { titre: "titreFr", description: "descriptionFr" };
  if (lang === "nl") return { titre: "titreNl", description: "descriptionNl" };
  return { titre: "titreEn", description: "descriptionEn" };
}

function detailsLabelKey(lang: DescLang): string {
  return lang === "fr" ? "biens.detailsFr" : lang === "nl" ? "biens.detailsNl" : "biens.detailsEn";
}

function pickPrimaryLang(uiLang: string): DescLang {
  const code = (uiLang || "").toLowerCase().slice(0, 2);
  if (code === "nl") return "nl";
  if (code === "en") return "en";
  return "fr";
}

interface DescriptionCardsProps {
  bienId: string;
  uiLang: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  set: (key: string) => (value: string) => void;
  F: (key: string, el: React.ReactNode) => React.ReactNode;
  canEdit: (key: string) => boolean;
  t: (key: string) => string;
}

function DescriptionCards({ bienId, uiLang, data, set, F, canEdit, t }: DescriptionCardsProps) {
  const primaryLang = pickPrimaryLang(uiLang);
  const orderedLangs: DescLang[] = [primaryLang, ...DESC_LANGS.filter((l) => l !== primaryLang)];

  const [aiBusy, setAiBusy] = useState<DescLang | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const primaryKeys = fieldKeys(primaryLang);
  const primaryHasContent = Boolean((data[primaryKeys.titre] || "").trim() || (data[primaryKeys.description] || "").trim());

  const runGenerate = async (lang: DescLang) => {
    const k = fieldKeys(lang);
    const hasContent = Boolean((data[k.titre] || "").trim() || (data[k.description] || "").trim());
    if (hasContent && !window.confirm(t("biens.aiGenerateConfirm"))) return;
    setAiBusy(lang);
    setAiError(null);
    try {
      const res = await generateBienDescription({ bienId, lang: lang as AiLang });
      set(k.titre)(res.titre);
      set(k.description)(res.description);
    } catch (err) {
      console.error("[generateBienDescription]", err);
      setAiError(t("biens.aiError"));
    } finally {
      setAiBusy(null);
    }
  };

  const runTranslate = async (target: DescLang) => {
    const k = fieldKeys(target);
    const sourceKeys = fieldKeys(primaryLang);
    const sourceTitre = (data[sourceKeys.titre] || "").trim();
    const sourceDescription = (data[sourceKeys.description] || "").trim();
    if (!sourceTitre && !sourceDescription) return; // bouton désactivé normalement
    const hasContent = Boolean((data[k.titre] || "").trim() || (data[k.description] || "").trim());
    if (hasContent && !window.confirm(t("biens.aiTranslateConfirm"))) return;
    setAiBusy(target);
    setAiError(null);
    try {
      const res = await translateBienDescription({
        lang: target as AiLang,
        sourceLang: primaryLang as AiLang,
        sourceTitre,
        sourceDescription,
      });
      set(k.titre)(res.titre);
      set(k.description)(res.description);
    } catch (err) {
      console.error("[translateBienDescription]", err);
      setAiError(t("biens.aiError"));
    } finally {
      setAiBusy(null);
    }
  };

  return (
    // 3 colonnes (FR / NL / EN) au lieu du CardGrid 2-col par defaut. La
    // 3e card descendait toute seule en bas a gauche, layout asymetrique.
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {orderedLangs.map((lang) => {
        const k = fieldKeys(lang);
        const isPrimary = lang === primaryLang;
        const editable = canEdit(k.titre) && canEdit(k.description);
        const busy = aiBusy === lang;
        const translateDisabled = !primaryHasContent || aiBusy !== null;
        return (
          <div key={lang} className="bg-surface border border-border rounded-lg shadow-sm p-6 h-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-text-secondary">
                {t(detailsLabelKey(lang))}
              </h3>
              {editable && (
                isPrimary ? (
                  <button
                    type="button"
                    className="text-xs text-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={aiBusy !== null}
                    onClick={() => runGenerate(lang)}
                  >
                    {busy ? t("biens.aiGenerating") : t("common.generate")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={translateDisabled}
                    onClick={() => runTranslate(lang)}
                  >
                    {busy ? t("biens.aiTranslating") : t("common.translate")}
                  </button>
                )
              )}
            </div>
            <div className="border-b border-border mb-5" />
            <div className="flex flex-col gap-6">
              {F(k.titre, <FormField label={t("common.titleField")} value={data[k.titre] ?? ""} onChange={set(k.titre)} maxLength={IMMOWEB_TEXT_LIMITS.headline} />)}
              {F(k.description, <TextAreaField label={t("common.description")} value={data[k.description] ?? ""} onChange={set(k.description)} maxLength={IMMOWEB_TEXT_LIMITS.description} />)}
            </div>
            {aiError && isPrimary && aiBusy === null && (
              <p className="text-xs text-red-600 mt-3">{aiError}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
